/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Table from "react-bootstrap/Table";
import { Row, Col } from "react-bootstrap";
import TextField from "@mui/material/TextField";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Form from "react-bootstrap/Form";
import { Button as RBButton } from "react-bootstrap";
import Link from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew"; // add this at the top
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import SyncIcon from "@mui/icons-material/Sync";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import PrintIcon from "@mui/icons-material/Print";
import EventIcon from "@mui/icons-material/Event";
import CommentIcon from "@mui/icons-material/Comment";
import SendIcon from "@mui/icons-material/Send";
import CommentSection from "components/CommentSection/CommentSection";
import QRCode from "qrcode";
import HomeIcon from "@mui/icons-material/Home";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

// Authentication context
import { useAuth } from "context/AuthContext";

// @mui material components
import { useMaterialUIController, setMiniSidenav } from "context";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DialogContentText from "@mui/material/DialogContentText";
import Chip from "@mui/material/Chip";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Avatar,
  Box,
  Typography,
  Badge,
  Stack,
  Grow,
  Select,
  Collapse,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import RefreshIcon from "@mui/icons-material/Refresh";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Logo from "components/Logo";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { API_ENDPOINTS, API_BASE_URL } from "config/api";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import NotificationsIcon from "@mui/icons-material/Notifications";
import Notifications from "components/Notifications/index";
import SummaryWidget from "./SummaryWidget";

// Environment variables - defined at module level for access across all components
const HOSTAWAY_BASE_URL = process.env.REACT_APP_HOSTAWAY_BASE_URL || "https://api.hostaway.com/v1";
const rawHostawayToken = process.env.REACT_APP_HOSTAWAY_API_TOKEN || process.env.HOSTAWAY_AUTH_TOKEN || "";
const HOSTAWAY_TOKEN = rawHostawayToken.startsWith("Bearer ") ? rawHostawayToken.substring(7).trim() : rawHostawayToken.trim();

const GENDER_OPTIONS = ["Male", "Female"];
const PROVINCE_OPTIONS = [
  "Azad Kashmir",
  "Balochistan",
  "F.A.T.A",
  "Gilgit Baltistan",
  "Islamabad",
  "Khyber Pakhtunkhwa",
  "Name Unknown",
  "Other",
  "Punjab",
  "Sindh"
];

const formatPakistanDateTime = (raw, includeSeconds = false) => {
  if (!raw) return "-";
  const str = String(raw).trim();
  if (!str || str.toLowerCase() === "n/a" || str === "-") return "-";
  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  const opts = {
    timeZone: "Asia/Karachi",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  if (includeSeconds) {
    opts.second = "2-digit";
  }
  return d.toLocaleString("en-US", opts);
};

const getOriginalChargeId = (record, allPayments = []) => {
  if (!record) return "-";
  const fields = record.fields || record.raw || record || {};
  const recId = String(fields.id || fields.ID || fields.Id || record.id || "").trim();
  const rawAmt = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ""));
  const typeStr = String(fields.Type || fields.type || "").toLowerCase().trim();
  const statusStr = String(fields.Status || fields.status || "").toLowerCase().trim();
  const descStr = String(fields.Description || fields.description || fields.Title || fields.title || "").toLowerCase();

  const isRefund =
    typeStr === "refund" ||
    statusStr === "refunded" ||
    descStr.includes("refund") ||
    (!isNaN(rawAmt) && rawAmt < 0);

  // If it's a positive charge, its own ID is the original charge ID
  if (!isRefund && recId && recId !== "-" && recId !== "0") {
    return recId;
  }

  // 1. Direct refundedChargeId / originalChargeId
  const directRefundId = String(
    fields.refundInfo?.refundedChargeId ||
    fields.raw?.refundInfo?.refundedChargeId ||
    record.refundInfo?.refundedChargeId ||
    record.raw?.refundInfo?.refundedChargeId ||
    fields.originalChargeId ||
    fields.original_charge_id ||
    record.originalChargeId ||
    record.original_charge_id ||
    fields["Refunded Charge ID"] ||
    fields["RefundedChargeId"] ||
    fields.refundedChargeId ||
    record.refundedChargeId ||
    ""
  ).trim();

  if (directRefundId && directRefundId !== "-" && directRefundId !== "0" && directRefundId !== recId) {
    return directRefundId;
  }

  // 2. Check if this refund is listed in refundIds of any positive charge in allPayments
  const matchingParentCharge = (allPayments || []).find((p) => {
    const pf = p.fields || p.raw || p || {};
    const rIds = pf.refundIds || p.refundIds || (p.raw && p.raw.refundIds) || [];
    const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
    return pAmt > 0 && Array.isArray(rIds) && (rIds.map(String).includes(recId) || (recId && rIds.includes(Number(recId))));
  });
  if (matchingParentCharge) {
    const mpf = matchingParentCharge.fields || matchingParentCharge.raw || matchingParentCharge || {};
    const parentId = String(mpf.id || mpf.ID || mpf.Id || matchingParentCharge.id || "").trim();
    if (parentId) return parentId;
  }

  // 3. Find matching positive charges in allPayments
  const positiveCharges = (allPayments || []).filter((p) => {
    const pf = p.fields || p.raw || p || {};
    const pAmt = Number(String(pf.Amount || pf.amount || "0").replace(/[^0-9.-]/g, ""));
    const pType = String(pf.Type || pf.type || "").toLowerCase().trim();
    const pStatus = String(pf.Status || pf.status || "").toLowerCase().trim();
    const pDesc = String(pf.Description || pf.description || pf.Title || pf.title || "").toLowerCase();
    const isPRefund = pType === "refund" || pStatus === "refunded" || pDesc.includes("refund") || (!isNaN(pAmt) && pAmt < 0);
    return !isPRefund && pAmt > 0;
  });

  if (positiveCharges.length === 1) {
    const pf = positiveCharges[0].fields || positiveCharges[0].raw || positiveCharges[0] || {};
    const pId = String(pf.id || pf.ID || pf.Id || positiveCharges[0].id || "").trim();
    if (pId) return pId;
  } else if (positiveCharges.length > 1) {
    const recTitle = String(fields.Title || fields.title || fields.name || "").trim().toLowerCase();
    const sameTitleCharges = positiveCharges.filter((p) => {
      const pf = p.fields || p.raw || p || {};
      const pTitle = String(pf.Title || pf.title || pf.name || "").trim().toLowerCase();
      return pTitle && recTitle && pTitle === recTitle;
    });

    const candidateList = sameTitleCharges.length > 0 ? sameTitleCharges : positiveCharges;

    const refDate = new Date(
      fields["Charge Date and Time"] ||
      fields["Charge Date"] ||
      fields.chargeDate ||
      fields.date ||
      fields.dateTime ||
      fields.created_at ||
      record.dateTime ||
      record.date_time ||
      record.created_at ||
      0
    ).getTime();
    let bestCharge = candidateList[0];
    let minDiff = Infinity;

    for (const pc of candidateList) {
      const pf = pc.fields || pc.raw || pc || {};
      const cDate = new Date(
        pf["Charge Date and Time"] ||
        pf["Charge Date"] ||
        pf.chargeDate ||
        pf.date ||
        pf.dateTime ||
        pf.created_at ||
        pc.dateTime ||
        pc.date_time ||
        pc.created_at ||
        0
      ).getTime();
      if (!isNaN(refDate) && !isNaN(cDate) && refDate >= cDate) {
        const diff = refDate - cDate;
        if (diff < minDiff) {
          minDiff = diff;
          bestCharge = pc;
        }
      }
    }

    const pf = bestCharge.fields || bestCharge.raw || bestCharge || {};
    const pId = String(pf.id || pf.ID || pf.Id || bestCharge.id || "").trim();
    if (pId) return pId;
  }

  return recId || "-";
};

const stacks = [
  "Early Checked In",
  "Checked In",
  "Staying Guest",
  "Upcoming Checkout",
  "Early Checked Out",
  "Checked Out",
  "Same Day Checked Out",
  "Upcoming Stay",
  "No Show",
];

const stackStyles = {
  "Upcoming Stay": { backgroundColor: "#fafafa" },
  "Checked In": { backgroundColor: "#fafafa" },
  "Early Checked In": { backgroundColor: "#fafafa" },
  "Staying Guest": { backgroundColor: "#fafafa" },
  "Upcoming Checkout": { backgroundColor: "#fafafa" },
  "Early Checked Out": { backgroundColor: "#fafafa" },
  "Checked Out": { backgroundColor: "#fafafa" },
  "Same Day Checked Out": { backgroundColor: "#fafafa" },
  "No Show": { backgroundColor: "#fafafa" },
};

// Clear console on component load
//console.clear();

function ReservationCard({
  guest,
  setSnackbar,
  stack,
  isViewOnly,
  isCustom,
  hasPermission,
  openCommentReservationId,
  setOpenCommentReservationId,
  openPreviewReservationId, // ✅ New prop
  setOpenPreviewReservationId, // ✅ New prop
  allGuests,
  hotelEyeEntryGlobal,
  onUpdateHotelEyeEntry,
  onRefresh,
  sseTrigger,
}) {
  const [open, setOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [reservationDetails, setReservationDetails] = useState({});
  const [error, setError] = useState(null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showPrintCheckIn, setShowPrintCheckIn] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [canPrintCheckIn, setCanPrintCheckIn] = useState(() => {
    if (guest?.printedCheckIn) return false;
    return guest?.["Can Print Check In"] || false;
  });
  const [canPrintCheckOut, setCanPrintCheckOut] = useState(() => {
    if (guest?.printedCheckOut) return false;
    return guest?.["Can Print Check Out"] || false;
  });
  const [isSyncingCard, setIsSyncingCard] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth(); // ✅ get logged-in user
  const [bookingDate, setBookingDate] = useState(null);
  const [commentCount, setCommentCount] = useState(0);
  const [latestComment, setLatestComment] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [showCheckInOptions, setShowCheckInOptions] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null); // Store QR code
  const [qrExpiration, setQrExpiration] = useState(null); // Store QR expiration
  const [hasSmartLock, setHasSmartLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [checkInMissingFields, setCheckInMissingFields] = useState([]);
  const [isFetchingCheckInDetails, setIsFetchingCheckInDetails] = useState(false);
  const [hotelEyeInputData, setHotelEyeInputData] = useState({});
  const [isSavingHotelEyeData, setIsSavingHotelEyeData] = useState(false);
  const checkInReservationDataRef = useRef(null);
  const [actionType, setActionType] = useState(""); // "Check-In" or "Check-Out"
  const [reprintMenuAnchor, setReprintMenuAnchor] = useState(null);
  const [hotelEyeEntry, setHotelEyeEntry] = useState(hotelEyeEntryGlobal?.status || "pending");
  const [hotelEyeEntryUser, setHotelEyeEntryUser] = useState(hotelEyeEntryGlobal?.user || "");
  const [hotelEyeEntryTime, setHotelEyeEntryTime] = useState(hotelEyeEntryGlobal?.time || "");
  const [isSendingHotelEye, setIsSendingHotelEye] = useState(false);
  const [isCreatingOpusTicket, setIsCreatingOpusTicket] = useState(false);

  // Stacks where "To do" cleaning status should display as "Clean"
  const ACTIVE_GUEST_STACKS = ["Upcoming Stay", "Checked In", "Staying Guest", "Upcoming Checkout"];

  const [cleaningStatus, setCleaningStatus] = useState(
    ACTIVE_GUEST_STACKS.includes(stack) ? "Clean" : "To do"
  );

  const fetchCleaningStatus = async () => {
    try {
      const aptName = guest.listingName;
      if (!aptName) return;

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const resId = guest.reservationId || "";
      const targetName = resId ? `${aptName} (${resId})` : aptName;

      const res = await fetch(`/api/cleaning/get-record/${encodeURIComponent(targetName)}`, {
        headers: headers
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          const fields = json.data;
          let col = 'To do';
          if (fields['Clean'] === 'Clean') col = 'Clean';
          else if (fields['Hold'] === 'Hold') col = 'Hold';
          else if (fields['In Progress'] === 'In progress') col = 'In progress';
          else if (fields['TO DO'] === 'To do') col = 'To do';

          // ✅ For active-guest stacks, "To do" should display as "Clean"
          if (col === 'To do' && ACTIVE_GUEST_STACKS.includes(stack)) {
            col = 'Clean';
          }

          setCleaningStatus(col);
        } else {
          // No record found — apply same override for active-guest stacks
          setCleaningStatus(ACTIVE_GUEST_STACKS.includes(stack) ? "Clean" : "To do");
        }
      }
    } catch (err) {
      console.error("❌ Failed to fetch cleaning status:", err);
    }
  };

  useEffect(() => {
    fetchCleaningStatus();
  }, [guest?.listingName, stack]);

  useEffect(() => {
    if (hotelEyeEntryGlobal) {
      setHotelEyeEntry(hotelEyeEntryGlobal.status || "pending");
      setHotelEyeEntryUser(hotelEyeEntryGlobal.user || "");
      setHotelEyeEntryTime(hotelEyeEntryGlobal.time || "");
    }
  }, [hotelEyeEntryGlobal]);

  // ✅ Auto-open preview if reservation ID matches deep link
  useEffect(() => {
    if (openPreviewReservationId && (openPreviewReservationId === guest.reservationId || openPreviewReservationId === Number(guest.reservationId))) {
      setOpen(true);
    }
  }, [openPreviewReservationId, guest.reservationId]);

  const handleReprintMenuOpen = (event) => {
    setReprintMenuAnchor(event.currentTarget);
  };

  const handleReprintMenuClose = () => {
    setReprintMenuAnchor(null);
  };

  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [fdoRefundIdMap, setFdoRefundIdMap] = useState({}); // internalRefundId → providerRefundId



  const fetchPaymentDetails = async () => {
    setLoadingPayment(true);
    setPaymentDetails([]);
    setFdoRefundIdMap({});
    try {
      // Fetch Teable records and Hostaway finance-fields (with enriched refundIds) in parallel
      const [response, finResp] = await Promise.allSettled([
        fetch(API_ENDPOINTS.PAYMENT_DETAILS(guest.reservationId)),
        fetch(`/api/charges/finance-fields/${guest.reservationId}`),
      ]);

      let records = [];
      if (response.status === 'fulfilled' && response.value.ok) {
        const data = await response.value.json();
        records = data.data || [];
      }

      let hostPayments = [];
      if (finResp.status === 'fulfilled' && finResp.value.ok) {
        const finData = await finResp.value.json();
        if (finData?.success) {
          hostPayments = Array.isArray(finData.payments) ? finData.payments : [];
          if (finData.refundIdMap && typeof finData.refundIdMap === 'object') {
            setFdoRefundIdMap(finData.refundIdMap);
          }
        }
      }

      // Merge: Hostaway rows first (have paymentProviderRefundId set), then Teable rows
      const seen = new Set();
      const merged = [];
      for (const item of [...hostPayments, ...records]) {
        if (!item) continue;
        const f = item.fields || item || {};
        const rawId = f.id || f.ID || f.Id || f.chargeId || item.id;
        const key = rawId ? `id_${String(rawId).trim()}` : `fb_${f.Amount || f.amount}_${f.Title || f.title}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(item);
        }
      }

      const paidRecords = merged.filter(record => {
        const f = record.fields || record || {};
        let statusVal = f.Status || f.status || "";
        if (typeof statusVal === 'object' && statusVal !== null) {
          statusVal = statusVal.name || statusVal.value || String(statusVal);
        }
        const status = String(statusVal).toLowerCase().trim();
        const amt = Number(String(f.Amount || f.amount || '0').replace(/[^0-9.-]/g, ''));
        return status === "paid" || status === "successded" || status === "succeeded" || status === "refunded" || amt < 0;
      });

      const sortedPaidRecords = paidRecords.sort((a, b) => {
        const fa = a.fields || a || {};
        const fb = b.fields || b || {};
        const dateA = new Date(fa["Charge Date"] || fa.chargeDate || fa.date || fa.dateTime || fa.date_time || fa.created_at || fa.createdAt || 0).getTime();
        const dateB = new Date(fb["Charge Date"] || fb.chargeDate || fb.date || fb.dateTime || fb.date_time || fb.created_at || fb.createdAt || 0).getTime();
        if (!isNaN(dateA) && !isNaN(dateB) && dateA !== dateB) {
          return dateA - dateB;
        }
        const idA = Number(fa.id || fa.ID || a.id || 0);
        const idB = Number(fb.id || fb.ID || b.id || 0);
        return idA - idB;
      });
      setPaymentDetails(sortedPaidRecords);
    } catch (error) {
      console.error(error);
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: "Failed to load payment details",
          severity: "error",
        });
      }
    } finally {
      setLoadingPayment(false);
    }
  };

  // Derived state from props
  const showComments = openCommentReservationId === guest.reservationId;

  // Check if there's any other reservation for the same room (double reservation)
  const hasDoubleReservation = allGuests?.some(
    (otherGuest) =>
      otherGuest.reservationId !== guest.reservationId &&
      otherGuest.listingName === guest.listingName
  );

  // Check if another guest has ACTUALLY checked in and not checked out (currently occupying)
  const isRoomOccupiedWithCheckedInGuest = allGuests?.some(
    (otherGuest) =>
      otherGuest.reservationId !== guest.reservationId &&
      otherGuest.listingName === guest.listingName &&
      otherGuest.actualCheckin &&
      otherGuest.actualCheckin !== "N/A" &&
      (!otherGuest.actualCheckout || otherGuest.actualCheckout === "N/A")
  );

  const HOSTAWAY_BASE_URL = process.env.REACT_APP_HOSTAWAY_BASE_URL || "https://api.hostaway.com/v1";
  const HOSTAWAY_API = process.env.REACT_APP_HOSTAWAY_API_URL || `${HOSTAWAY_BASE_URL}/reservations`;
  const HOSTAWAY_TOKEN = process.env.REACT_APP_HOSTAWAY_API_TOKEN;

  // Migrated to local database Action Logs
  const TEABLE_API_URL = "/api/fdo/logs";
  const TEABLE_API_TOKEN = "";

  const TEABLE_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;
  const TABLE_URL = process.env.REACT_APP_TEABLE_TABLE_URL_COMMENTS || "https://teable.namuve.com/api/table/tblqGKHWkCHhPq71rCo/record";

  const NOTIFICATION_API = process.env.REACT_APP_TEABLE_TABLE_URL_NOTIFICATIONS || "https://teable.namuve.com/api/table/tblP2sVyihyhLjXYtRI/record";

  const API_ENDPOINT = process.env.REACT_APP_TEABLE_TABLE_URL_FDO_MAIN || "https://teable.namuve.com/api/table/tbliOdo8ldmMO8rrYyN/record";
  const API_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;

  // Debug: Check if environment variables are loaded
  if (!HOSTAWAY_TOKEN) {
    console.error("❌ HOSTAWAY_TOKEN is undefined - check .env file");
  }
  if (!TEABLE_TOKEN) {
    console.error("❌ TEABLE_TOKEN is undefined - check .env file");
  }

  const searchUrl = `${API_ENDPOINT}?search=${guest.reservationId}&search=Reservation+ID&search=true`;

  const handleSendHotelEyeData = async () => {
    setIsSendingHotelEye(true);
    try {
      // Fetch reservation data from Hostaway to get all custom fields
      const getResUrl = `${HOSTAWAY_API}/${guest.reservationId}`;
      const res = await fetch(getResUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
          "Cache-Control": "no-cache",
        },
      });

      if (!res.ok) throw new Error("Failed to fetch latest reservation data");
      const resData = await res.json();

      const getCustomFieldValue = (name) => {
        return resData.result?.customFieldValues?.find(field => field.customField?.name === name)?.value || "";
      };

      const triggerCheckinData = {
        "Reservation ID": guest.reservationId,
        "CNIC#": getCustomFieldValue("CNIC#") || getCustomFieldValue("ID card Number/ Passport number"),
        "Name": resData.result?.guestName || guest.guestName || "",
        "Father Name": getCustomFieldValue("Father's Name") || getCustomFieldValue("Father Name"),
        "Address": getCustomFieldValue("Address"),
        "Gender": getCustomFieldValue("Gender"),
        "Cell Phone#": resData.result?.phone || guest.phone || "",
        "Province": getCustomFieldValue("Province"),
        "District": getCustomFieldValue("District") || getCustomFieldValue("City/District") || "",
        "Total# of Accompaning Guest": resData.result?.numberOfGuests || guest.numberOfGuests || "",
        "Children": resData.result?.children || guest.children || 0,
        "Purpose of Visit": getCustomFieldValue("Purpose of Travel") || getCustomFieldValue("Purpose Of Travel"),
        "Check-out Date": resData.result?.departureDate ? resData.result.departureDate.split("T")[0] : (guest.departureDate ? guest.departureDate.split("T")[0] : ""),
        "Check-out Time": resData.result?.checkOutTime || "",
        "Room#": guest.listingName || "",
        "raw_custom_fields": resData.result?.customFieldValues || []
      };

      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_HOTEL_EYE_CHECKIN || "https://n8n.namuve.com/webhook/935f4d72-c78d-4c92-b49c-4392f038feba";

      const webhookRes = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerCheckinData),
      });

      if (!webhookRes.ok) {
        const data = await webhookRes.json().catch(() => ({}));
        throw new Error(data.message || "Failed to trigger webhook");
      }

      setSnackbar({
        open: true,
        message: "Hotel Eye webhook triggered! Waiting for background script to process...",
        severity: "success",
      });

    } catch (error) {
      console.error("❌ Error sending Hotel Eye data:", error);
      setSnackbar({
        open: true,
        message: `Error sending Hotel Eye data: ${error.message}`,
        severity: "error",
      });
    } finally {
      setIsSendingHotelEye(false);
    }
  };

  const handleCreateOpusTicket = async () => {
    setIsCreatingOpusTicket(true);
    try {
      // Helper function to format arrival/departure with ISO 8601
      const formatISO = (dateStr, defaultTime) => {
        if (!dateStr) return null;
        if (dateStr.length === 10) return `${dateStr}${defaultTime}`; // YYYY-MM-DD
        try { return new Date(dateStr).toISOString(); } catch(e) { return null; }
      };

      const opusPayload = {
        apartment_id: guest.listingName,
        guest_name: guest.guestName,
        arrival: formatISO(guest.arrivalDate, 'T10:00:00Z'),
        departure: formatISO(guest.departureDate, 'T07:00:00Z'),
        occupancy: Number(guest["Number of Guests"] || guest.numberOfGuests || reservationDetails?.numberOfGuests)
      };

      const resp = await fetch('https://portal.theopus.biz/api/webhooks/external-ticket/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(opusPayload)
      });
      if (resp.ok) {
        setSnackbar({ open: true, message: "Opus Ticket created successfully!", severity: "success" });
      } else {
        throw new Error(`Server returned ${resp.status}`);
      }
    } catch (error) {
      console.error('Failed to create Opus ticket:', error);
      setSnackbar({ open: true, message: "Failed to create Opus ticket. See console for details.", severity: "error" });
    } finally {
      setIsCreatingOpusTicket(false);
    }
  };

  const handleHotelEyeChange = async (event) => {
    const newValue = event.target.value;
    const currentUser = user?.name || user?.username || user?.email?.split("@")[0] || "Unknown User";
    const currentTime = new Date().toISOString();

    setHotelEyeEntry(newValue);
    setHotelEyeEntryUser(currentUser);
    setHotelEyeEntryTime(currentTime);

    if (onUpdateHotelEyeEntry) {
      onUpdateHotelEyeEntry(guest.reservationId, { status: newValue, user: currentUser, time: currentTime });
    }

    try {
      const res = await fetch("/api/fdo/hoteleye", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservation_id: guest.reservationId,
          status: newValue,
          user: currentUser
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        console.error("Failed to update Hotel Eye Entry:", errData);
      }
    } catch (error) {
      console.error("Error updating Hotel Eye Entry:", error);
    }
  };

  const handleSendNotification = async (commentText, type = "comment") => {
    if (!guest.reservationId || !commentText?.trim()) return;

    try {
      const res = await fetch("/api/fdo/notifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: user?.name || user?.username,
          text: commentText.trim(),
          reservation_id: guest.reservationId,
          type: type,
          apt: guest.apartment || guest.listingName || "N/A",
          guest_name: guest.guestName || "Unknown",
        }),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("Notification failed:", res.status, err);
      } else {
        console.log("Notification saved:", type);
      }
    } catch (err) {
      console.error("Network error:", err);
    }
  };

  const sendToGoogleChat = async (message, type = "comment") => {
    // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
    // PASTE YOUR NEW WEBHOOK URL HERE
    const GOOGLE_CHAT_WEBHOOK = process.env.REACT_APP_GOOGLE_CHAT_WEBHOOK_COMMENTS || "https://chat.googleapis.com/v1/spaces/AAQATia2_pc/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=5-ONneVmdY5TJo8dqggt-BtC2EnTdAFZAOET3ZywDew";
    // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

    const guestName = guest.guestName || "Guest";
    const aptName = guest.apartment || guest.listingName || "Unit";

    let actionText = "commented on";
    if (type.includes("reply")) actionText = "replied to";
    if (type.includes("edit")) actionText = "edited comment on";

    const text = `👤 *${user.name
      }* ${actionText} reservation of *${guestName} (${aptName} 🏠)*\n\n${message.trim()}\n\n🔗 https://dashboard.hostaway.com/reservations/${guest.reservationId
      }`;

    // ✅ Send to Google Chat (COMMENTED OUT AS PER USER REQUEST)
    /* try {
      const res = await fetch(GOOGLE_CHAT_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("Google Chat error:", res.status, err);
      }
    } catch (err) {
      console.error("Network error:", err);
    } */

    // ✅ Send to Mattermost
    const mattermostMessage = `${user.name || "User"} commented on reservation of ${guestName} (${aptName} 🏠)

${message.trim()}

Reservation ID: [${guest.reservationId}](https://dashboard.hostaway.com/reservations/${guest.reservationId})`;

    try {
      const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";
      const mmToken = process.env.REACT_APP_MATTERMOST_TOKEN || "ymptq118c3be8jtyxpghf811xc";
      const mmChannel = process.env.REACT_APP_MATTERMOST_CHANNEL_ID || "3wgp8gakejgci86w8nowyxnhia";

      const res = await fetch(mmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${mmToken}`
        },
        body: JSON.stringify({
          channel_id: mmChannel,
          message: mattermostMessage
        }),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("❌ Mattermost error (card view):", res.status, err);
      } else {
        console.log("✅ Mattermost comment notification sent from card view");
      }
    } catch (err) {
      console.error("❌ Mattermost network error (card view):", err);
    }
  };

  const sendDoubleReservationAlert = async (actionType) => {
    const DOUBLE_RESERVATION_WEBHOOK = process.env.REACT_APP_GOOGLE_CHAT_WEBHOOK_ALERTS;
    const DOUBLE_RESERVATION_TEABLE = process.env.REACT_APP_TEABLE_TABLE_URL_ALERTS;

    const now = new Date();
    const formattedDateTime = now.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    const emoji = actionType === "Check-In" ? "📥" : "📤";
    const action = actionType === "Check-In" ? "checked in" : "checked out";

    const text = `${emoji} Double Reservation ${actionType} Alert for ${guest.guestName || "Guest"
      }!\n👤 ${guest.guestName || "Guest"} has ${action} to 🏠 ${guest.listingName || "N/A"
      } at 🕒 ${formattedDateTime} by ${user?.name || user?.username || "Unknown"
      }.\n\n🔗 https://dashboard.hostaway.com/reservations/${guest.reservationId}`;

    // Send to Google Chat (COMMENTED OUT AS PER USER REQUEST)
    /* try {
      const res = await fetch(DOUBLE_RESERVATION_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!res.ok) {
        const err = await res.text();
        console.error("Double reservation alert error:", res.status, err);
      }
    } catch (err) {
      console.error("Network error:", err);
    } */

    // Send to Teable
    try {
      const teablePayload = {
        records: [
          {
            fields: {
              Button: `Mark ${actionType}`, // Button
              "Reservation ID": Number(guest.reservationId), // Reservation ID
              User: user?.name || user?.username || "Unknown", // User
              Time: formattedDateTime, // Time
            },
          },
        ],
      };

      const teableRes = await fetch(DOUBLE_RESERVATION_TEABLE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TEABLE_TOKEN}`,
        },
        body: JSON.stringify(teablePayload),
      });

      if (!teableRes.ok) {
        const errText = await teableRes.text();
        console.error("Teable logging error:", teableRes.status, errText);
      } else {
        console.log("✅ Double reservation logged to Teable");
      }
    } catch (err) {
      console.error("Teable network error:", err);
    }
  };

  /* ------------------------------------------------- POST COMMENT ------------------------------------------------- */
  const handleSend = async () => {
    // FIXED: Only block if empty OR no reservationId
    if (!newComment.trim() || !guest.reservationId) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/fdo/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservation_id: guest.reservationId,
          comment: newComment,
          user: user?.name || user?.username || "Unknown FDO",
        }),
      });

      if (res.ok) {
        setNewComment("");
        fetchCommentCount(guest.reservationId); // ← REFRESH COMMENTS counts
        handleSendNotification(newComment, "commented");
        sendToGoogleChat(newComment, "commented");
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentCount = async (reservationId) => {
    try {
      const res = await fetch(`/api/fdo/comments?_=${Date.now()}`, { cache: "no-store" });
      const data = await res.json();

      const filtered = (data.records || []).filter(
        (r) => String(r.reservation_id) === String(reservationId)
      );

      const count = filtered.length;
      setCommentCount(count);

      if (count > 0) {
        const latest = filtered.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )[0];
        setLatestComment(latest.comment || "No text");
      } else {
        setLatestComment(null);
      }
    } catch (err) {
      console.error("Failed to fetch comment count", err);
      setCommentCount(0);
      setLatestComment(null);
    }
  };

  useEffect(() => {
    if (guest?.reservationId) {
      fetchCommentCount(guest.reservationId);
    }
  }, [guest?.reservationId, sseTrigger]);

  const handlePrintCheckIn = async () => {
    // ✅ CRITICAL: Open window IMMEDIATELY to prevent popup blocker
    // Must be done synchronously in response to user click, before any async operations
    const formWindow = window.open("", "_blank");

    if (!formWindow) {
      setSnackbar({
        open: true,
        message: "Popup blocked! Please allow popups for this site.",
        severity: "error",
      });
      return;
    }

    // Show loading message in the window
    formWindow.document.write('<html><body><h2 style="text-align:center;margin-top:50px;">Loading...</h2></body></html>');

    try {
      // Prevent view_only users and custom users without complete access from printing
      if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
        console.log("❌ User does not have permission to print check-in forms");
        formWindow.close();
        setSnackbar({
          open: true,
          message: "You do not have permission to print forms",
          severity: "error",
        });
        return;
      }

      // Fetch latest reservation from API
      const response = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${HOSTAWAY_TOKEN}` },
      });

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const data = await response.json();
      const reservation = data.result || {};
      const guestName = reservation.guestName || "N/A";

      const cnic =
        reservation.customFieldValues?.find(
          (field) => field.customField?.name === "ID card Number/ Passport number"
        )?.value || "Not provided";

      const listingMapId = guest.listingName || "N/A";
      const listingType = guest.type || "N/A";
      const contact = reservation.phone || "N/A";
      const duration = reservation.nights || "N/A";
      const totalPrice = reservation.totalPrice || "N/A";
      const currencyLabel = reservation.currency || "";
      let earlyCheckIn = "0";
      if (Array.isArray(reservation.financeField)) {
        // 🔹 Early Check-in Fee
        const earlyCheckinField = reservation.financeField.find(
          (field) => field.alias === "Early Checkin Charges per hour" && field.isDeleted === 0
        );
        if (earlyCheckinField) {
          earlyCheckIn = earlyCheckinField.total ?? earlyCheckinField.value ?? "0";
        }
      }

      let pricePerNight = "N/A";
      if (Array.isArray(reservation.customFieldValues)) {
        const priceField = reservation.customFieldValues.find(
          (field) => field.customField?.id === 63430
        );
        if (priceField) {
          pricePerNight = priceField.value || "N/A";
        }
      }
      const channelName = reservation.channelName || "N/A";

      const address =
        reservation.customFieldValues?.find((field) => field.customField?.name === "Address")
          ?.value ||
        guest.address ||
        "Not provided";

      const email = reservation.guestEmail || "Not provided";
      const adults = reservation.numberOfGuests || "N/A";
      const children = reservation.children || "0";
      const arrival = reservation.arrivalDate || "N/A";
      const checkInTime = reservation.checkInTime
        ? formatTime(reservation.checkInTime)
        : guest.checkinTime
          ? formatTime(guest.checkinTime)
          : "N/A";

      const departure = reservation.departureDate || guest.checkoutDate || "N/A";

      const checkOutTime = reservation.checkOutTime
        ? formatTime(reservation.checkOutTime)
        : guest.checkoutTime
          ? formatTime(guest.checkoutTime)
          : "N/A";

      const vehicleNumber =
        reservation.customFieldValues?.find((field) => field.customField?.name === "Vehicle Number")
          ?.value || "Not provided";

      let securityDepositFee = "N/A";
      if (Array.isArray(reservation.financeField)) {
        const securityField = reservation.financeField.find(
          (field) => field.alias === "Security Deposit" && field.isDeleted === 0
        );
        if (securityField) {
          securityDepositFee = securityField.total ?? securityField.value ?? "0";
        }
      }

      let actualCheckInTime = "N/A";

      if (Array.isArray(reservation.customFieldValues)) {
        const checkInField = reservation.customFieldValues.find(
          (item) =>
            item.customField?.name === "Actual Check-in Time" && item.customFieldId === 76281
        );

        if (checkInField && checkInField.value) {
          const parsedDate = new Date(checkInField.value);
          if (!isNaN(parsedDate)) {
            actualCheckInTime = parsedDate.toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            });
          } else {
            actualCheckInTime = checkInField.value; // fallback if not a date
          }
        }
      }

      const reservationId = reservation.reservationId || "N/A";

      // ✅ Fetch listing data from Teable to check SmartLock status
      const hostawayListingId = reservation.listingMapId || guest.listingId || null;
      let hasSmartLock = false;
      let qrCodeDataUrl = "";

      if (hostawayListingId) {
        try {
          const teableResponse = await fetch(
            `${process.env.REACT_APP_TEABLE_TABLE_URL_SMARTLOCK}?search=${hostawayListingId}&search=Listing+IDs&search=true`,
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_TOKEN_SMARTLOCK}`,
              },
            }
          );

          if (teableResponse.ok) {
            const teableData = await teableResponse.json();
            const records = teableData.records || [];

            if (records.length > 0) {
              const listingRecord = records[0].fields || {};
              hasSmartLock = listingRecord["Has SmartLock"] === "Yes";
              console.log(
                `✅ SmartLock Status for listing ${hostawayListingId}: ${hasSmartLock ? "Yes" : "No"
                }`
              );

              // ✅ If SmartLock exists, fetch passcode from database
              if (hasSmartLock) {
                try {
                  const passcodeResponse = await fetch(
                    `${process.env.REACT_APP_TEABLE_TABLE_URL_PASSCODE}?search=${guest.reservationId}&search=Hostaway+Reservation+ID&search=true`,
                    {
                      headers: {
                        Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_PASSCODE_SEARCH}`,
                      },
                    }
                  );

                  if (passcodeResponse.ok) {
                    const passcodeData = await passcodeResponse.json();
                    const passcodeRecords = passcodeData.records || [];

                    if (passcodeRecords.length > 0) {
                      const passcodeInfo = passcodeRecords[0].fields || {};
                      const listingName = passcodeInfo["Listing Name"] || listingMapId;
                      const lockCode = passcodeInfo["Lock Code"] || "N/A";
                      const lockCodeId = passcodeInfo["Lock Code Id"] || "";
                      // Fetch QR expiration: Prioritize absolute time (10 min after actual check-in)
                      let expirationTime = null;

                      // 1. Try calculating from Hostaway Actual Check-in Time (Most robust source)
                      const hCheckInField = reservation.customFieldValues?.find(
                        (item) => item.customFieldId === 76281 || item.customField?.name === "Actual Check-in Time"
                      );

                      if (hCheckInField && hCheckInField.value) {
                        const checkInDate = new Date(hCheckInField.value);
                        if (!isNaN(checkInDate)) {
                          expirationTime = checkInDate.getTime() + 10 * 60 * 1000;
                          console.log(`✅ Calculated absolute expiration (10 min after mark check-in): ${new Date(expirationTime).toLocaleString()}`);
                        }
                      }

                      // 2. Fallback to local state OR Teable (if mark check-in just happened or time not yet in Hostaway)
                      if (!expirationTime) {
                        expirationTime = qrExpiration;

                        if (!expirationTime) {
                          try {
                            const table2Res = await fetch(process.env.REACT_APP_TEABLE_TABLE_URL_QR_EXPIRATION, {
                              headers: {
                                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_PASSCODE_QR}`,
                              }
                            });

                            if (table2Res.ok) {
                              const table2Data = await table2Res.json();
                              const qrRecord = table2Data.records?.find(r =>
                                String(r.fields['Reservation ID']) === String(guest.reservationId)
                              );

                              if (qrRecord && qrRecord.fields['QR Expiration']) {
                                expirationTime = parseInt(qrRecord.fields['QR Expiration']);
                                console.log(`✅ Using saved QR expiration from Table 2`);
                              } else {
                                expirationTime = Date.now() + 10 * 60 * 1000;
                                console.log(`⚠️ No QR expiration found in Table 2 - using temporary one (only if mark check-in missing)`);
                              }
                            } else {
                              expirationTime = Date.now() + 10 * 60 * 1000;
                              console.log(`⚠️ Failed to fetch Table 2 - using temporary expiration`);
                            }
                          } catch (err) {
                            console.error('Error fetching QR expiration from Table 2:', err);
                            expirationTime = Date.now() + 10 * 60 * 1000;
                          }
                        } else {
                          console.log(`✅ Using QR expiration from local state: ${expirationTime}`);
                        }
                      }

                      // Get check-in and check-out dates and times from Hostaway reservation
                      const checkInDate = guest.arrivalDate || "";
                      const checkOutDate = guest.departureDate || "";

                      // Get times from reservation object and ensure proper format (HH:MM)
                      const rawCheckInTime =
                        reservation?.checkInTime || guest.checkinTime || "00:00";
                      const rawCheckOutTime =
                        reservation?.checkOutTime || guest.checkoutTime || "00:00";

                      // Ensure times are in HH:MM format
                      const checkInTime = String(rawCheckInTime).includes(":")
                        ? rawCheckInTime
                        : `${rawCheckInTime}:00`;
                      const checkOutTime = String(rawCheckOutTime).includes(":")
                        ? rawCheckOutTime
                        : `${rawCheckOutTime}:00`;

                      // Combine date and time in ISO format
                      const checkInDateTime = `${checkInDate}T${checkInTime}`;
                      const checkOutDateTime = `${checkOutDate}T${checkOutTime}`;

                      // Create access URL with all parameters
                      const accessUrl = `${window.location.origin}/passcode-access?reservationId=${guest.reservationId}&expires=${expirationTime}&checkIn=${encodeURIComponent(checkInDateTime)}&checkOut=${encodeURIComponent(checkOutDateTime)}`;
                      // Generate QR code with the clean URL
                      qrCodeDataUrl = await QRCode.toDataURL(accessUrl, {
                        width: 150,
                        margin: 1,
                        color: {
                          dark: "#000000",
                          light: "#FFFFFF",
                        },
                      });

                      console.log("✅ QR code generated with passcode access link");
                    } else {
                      console.log("⚠️ No passcode found for this reservation");
                    }
                  }
                } catch (passcodeError) {
                  console.error("❌ Error fetching passcode:", passcodeError);
                }
              }
            }
          } else {
            console.warn("⚠️ Failed to fetch listing data from Teable");
          }
        } catch (error) {
          console.error("❌ Error fetching Teable listing data:", error);
        }
      }

      // 🔍 Debug: Log QR code status before rendering
      console.log("📊 QR Code Status:");
      console.log("- Has SmartLock:", hasSmartLock);
      console.log("- QR Code Data URL exists:", !!qrCodeDataUrl);
      console.log("- QR Code Data URL length:", qrCodeDataUrl?.length || 0);
      console.log("- QR Code preview:", qrCodeDataUrl ? qrCodeDataUrl.substring(0, 50) + "..." : "NONE");

      // Wait a moment to ensure QR code is fully ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Window was already opened at the start of function to prevent popup blocker
      // Fill guest details dynamically
      const htmlContent = `
    <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <link
            rel="icon"
            href="https://i.ibb.co/vC3k9ZXv/favicon-32x32.png"
            type="image/png"
          />
          <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
          <style>
            input[readonly] {
              pointer-events: none;
              user-select: none;
            }
            body {
              margin: 0;
              padding: 15px;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 80vh;
              background-color: #f0f0f0;
            }
            .form {
              width: 160mm;
              height: 240mm;
              padding: 10px;
              margin: auto;
              background-color: white;
              border-radius: 5px;
              border: 1px solid lightblue;
              box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
            }
            .logo-img {
              display: flex;
              justify-content: center;
              height: 60px;
              margin: 10px 0px 20px 0px;
            }
            .logo-img img {
              height: 100%;
              width: auto;
              object-fit: contain;
            }
            .heading-text h1 {
            



            
              font-size: 20px;
              text-align: center;
              
            }
            .form-container {
              display: flex;
              gap: 20px;
              padding: 10px;
              font-family: Helvetica;
            }
            .left-section,
            .right-section {
              flex: 1;
            }
            .form-field {
    display: flex;
    align-items: flex-start;
    margin-bottom: 15px;
    font-size: 12px;
  }

  .form-field label {
    width: 100px;
    flex-shrink: 0;
    font-weight: bold;
  }

  .form-field .field-value {
  flex: 1;
  border-bottom: 1px solid black;
  padding-bottom: 2px;
  word-break: break-word;
  font-size: 11.5px;
  min-height: 16px;
  font-family: Arial;
}

  /* To style readonly input fields consistently */
  .form-field input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid #000000;
  font-size: 11.5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

            .address-field input {
              width: calc(100% - 90px);
            }
            ul {
  padding-top: 2px;
  padding-bottom: 2px;
}

ul li {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  line-height: 1;   /* slightly increased for readability */
  margin-bottom: 6px; /* adds space between list items */
}

            .space {
              padding: 8px !important;
              font-family: Arial;
            }
            .row .row-field {
              margin-top: 5px;
}
            .row .row-field h3 {
              font-size: 13px;
              margin: 4px 0 !important;
              font-family: Arial;
            }
            .row .row-field h4 {
              font-size: 12px;
              margin: 4px 0 !important;
              text-align: right;
            }
              .download-btn {
    padding: 7px 9px;
    background: transparent;
    color: black;
    border: 1px solid black;
    border-radius: 5px;
    cursor: pointer;
    font-size: 15px;
    transition: background 0.3s, color 0.3s;
}

.download-btn:hover {
  background: black;
  color: white;
}

          </style>
        </head>
        <body>
          <div class="form">
            <div style="position: absolute; top: 5px; right: 5px; z-index: 1000;">
            <button onclick="printForm()" class="download-btn">
              Print
            </button>
  <button onclick="downloadForm()" class="download-btn">
    Download
  </button>
</div>
<div style="display: flex; flex-direction: row; margin-top: 15px; justify-content: space-between; align-items: flex-start;">
            <div style="display: flex; flex-direction: row; align-items: flex-start;">
              <div class="logo-img"
            >
              <img
                src="img/booknrent-logo.png"
                alt="Booknrent Logo"
              />
            </div>
            <div class="heading-text" style="margin: 20px 0px 0px 40px !important; flex: 1; max-width: 450px;">
              <h3 style="margin: 0; font-size: 20px; font-weight: bold; font-family: sans-serif; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.3;"> ${guestName}'s Check-in Form </h3>
              <p style="margin: 4px 0 0 0; font-size: 14px; color: #555; font-family: Arial;"><span style="color: #666;">(${guest.reservationId})</span> &nbsp; Actual Check-in Date / Time: ${actualCheckInTime}</p>
            </div>
            </div>
            
            <!-- QR Code Section - Only show if SmartLock exists -->
            ${qrCodeDataUrl
          ? `
            <div style="margin-top: 5px; margin-right: 15px; text-align: center;">
              <img src="${qrCodeDataUrl}" alt="QR Code" style="width: 95px; height: 95px; border: 1px solid #ddd; border-radius: 0px; padding: 0px; background: white;" />
            </div>
            `
          : ""
        }
            
            
            
            <!-- 
            <div style="position: absolute; margin-left: 400px; margin-top: -16px; font-family: Arial; color: #b6bfb6;">
    Printed By: ${user?.name}
  </div>
  -->
            </div>
            <div class="form-container">
  <div class="left-section">
    <div class="form-field"><label>Name:</label><div class="field-value">${guestName}</div></div>
    <div class="form-field"><label>CNIC:</label><div class="field-value">${cnic}</div></div>
    <div class="form-field"><label>Unit:</label><div class="field-value">${listingMapId}</div></div>
    <div class="form-field"><label>Type:</label><div class="field-value">${listingType}</div></div>
    ${user?.role !== "user" && !(user?.role === "custom" && hasPermission("fdoPanel", "hideContact")) ? `<div class="form-field"><label>Contact:</label><div class="field-value">${contact}</div></div>` : ""}
    <div class="form-field"><label>Total Nights:</label><div class="field-value">${duration}</div></div>
<div class="form-field">
  <label>Total Amount:</label>
  <div class="field-value">
    ${totalPrice} ${currencyLabel} 
    ${currencyLabel === "USD" ? "" : "<br> ☐ Cash / ☐ IBFT / ☐ Card"}
  </div>
</div>
    <div class="form-field"><label>Early Check-in:</label><div class="field-value">${earlyCheckIn} ${currencyLabel} </div></div>
    <div class="form-field"><label>Price/Night:</label><div class="field-value">${pricePerNight}</div></div>
    <div class="form-field"><label>Channel ID:</label><div class="field-value">${channelName}</div></div>
  </div>

  <div class="right-section">
  <div class="form-field"><label>Address:</label><div class="field-value">${address}</div></div>
  <div class="form-field"><label>Email:</label><div class="field-value">${email}</div></div>
  <div class="form-field"><label>Adults:</label><div class="field-value">${adults}</div></div>
  <div class="form-field"><label>Children:</label><div class="field-value">${children}</div></div>
  <div class="form-field"><label>Check-in Date:</label><div class="field-value">${arrival}</div></div>
  <div class="form-field"><label>Check-in Time:</label><div class="field-value">${checkInTime}</div></div>
  <div class="form-field"><label>Check-out Date:</label><div class="field-value">${departure}</div></div>
  <div class="form-field"><label>Check-out Time:</label><div class="field-value">${checkOutTime}</div></div>
  <div class="form-field" ><label>Vehicle No:</label><div class="field-value" >${vehicleNumber}</div></div>
<div class="form-field">
  <label>Security Deposit:</label>
  <div class="field-value">
    ${securityDepositFee} ${currencyLabel}
    ${currencyLabel === "USD" ? "" : "<br> ☐ Cash / ☐ IBFT / ☐ Card"}
  </div>
</div>
</div>
</div>

            
            <div class="space" style="padding: 5px">
              <div class="terms">
                <h3 style="margin: -15px 0px -15px 0px; text-align: left" >Terms and Conditions</h3>
                <ul>
                  <li><strong>At the time of checkout, you are required to visit the front desk and sign the checkout form. Please note that the security deposit will not be refunded unless the checkout form is duly signed.</strong></li>
                  <li>Original CNIC or Passport is required at the time of Check-in.</li>
                  <li>Only one car parking is allowed inside the building, Extra vehicles will be charged accordingly.</li>
                  <li>Pets are not allowed.</li>
                  <li>It is mandatory for guests to maintain a peaceful environment.</li>
                  <li>Anti-Social Behaviour and unethical activities are strictly prohibited.</li>
                  <li>Guests are requested to check out before 12:00pm on the day of check-out.</li>
                  <li><strong>Guests will bear financial liability for any damage inside the apartment and building due to their fault/negligence.</strong></li>
                  <li>Guests are requested to submit any complaints regarding the quality of services at the reception desk.</li>
                  <li>Money/Jewelry or other valuables brought to the property are at the guest's sole risk.</li>
                  </ul>
<p style="font-size: 13px; text-align: center;">
  <strong> <u>Security deposit will be refunded within 2–3 working days, after your checkout.</u></strong>
</p>

                <p style="font-family: 'Segoe UI', TTahoma, Geneva, Verdana, sans-serif;
    font-size: 11px;    margin-bottom: 2px;
    text-align: center;">I have read and understand the terms and conditions and agree to them. <br> I will be responsible for any damage or loss to the property as per list attached.</p>
              </div>
              <div class="row">
                <div class="row-field" style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 25px;">
                    <!-- LEFT: Guest Signature -->
                    <div class="inner-col" style="text-align: left; flex: 1; display: flex; flex-direction: column; align-items: flex-start;">
                      <div style="border-bottom: 1px solid black; width: 140px; margin-bottom: 6px;"></div>
                      <h3 style="font-size: 13px; margin: 0;">Guest Signature</h3>
                    </div>

                    <!-- CENTER: Admin Operations -->
                    <div class="inner-col" style="text-align: center; flex: 1; display: flex; flex-direction: column; align-items: center;">
                      <div style="border-bottom: 1px solid black; width: 140px; margin-bottom: 6px;"></div>
                      <h3 style="font-size: 13px; margin: 0 0 4px 0;">Admin Operations</h3>
                      <p style="font-size: 13px; font-weight: bold; margin: 3px 0;">Check Out Time: 12:00 NOON</p>
                      <p style="font-size: 10px; margin: 3px 0; white-space: nowrap;">(Late Check Out charges applicable @ Rs. 1000 per hour)</p>
                      <p style="font-size: 10px; margin: 3px 0;">(*Subject to Availability)</p>
                    </div>

                    <!-- RIGHT: Management Team -->
                    <div class="inner-col" style="text-align: center; flex: 1; display: flex; flex-direction: column; align-items: flex-end;">
                      <div style="border-bottom: 1px solid black; width: 140px; margin-bottom: 6px;"></div>
                      <h3 style="font-size: 13px; margin: 0;">Management Team</h3>
                      <span style="color:#383b39; font-size: 10px; white-space: nowrap; margin-top: 4px;">(${user?.name}) ${new Date().toLocaleString()}</span>
                    </div>
                  </div>

                <div style="
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px 5px 0px 4px;
    margin-top: 15px;
    margin-right: -17px;
    margin-left: -17px;
    font-size: 12px;
    font-family: 'monospace';
    background-color:rgb(0, 0, 0);
    color: white;
    ">
                  <div style="text-align: left;">
                    <h4>0300-0454711</h4>
                  </div>
                  <div style="text-align: right;">
                    <h4>30-A, Block L, Gulberg 3, Lahore</h4>
                  </div>
                </div>

              </div>
            </div>
          </div>
          <script>
            async function downloadForm() {
              const formElement = document.querySelector('.form');
              const canvas = await html2canvas(formElement, {
                scale: 2,
                logging: false,
                useCORS: true
              });
              
              const link = document.createElement('a');
              link.download = \`${guestName}'s Checkin-form (${guest.reservationId}).png\`;
              link.href = canvas.toDataURL('image/png');
              link.click();
            }
            

    // ---------- PRINT ----------
    async function printForm() {
      try {
        const el = document.querySelector('.form');
        const canvas = await html2canvas(el, {scale:3, useCORS:true, backgroundColor:'#fff'});
        const imgData = canvas.toDataURL('image/png');

        const pw = window.open('', '_blank', 'width=900,height=1000');
        pw.document.write(\`
<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Print</title>
<style>
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html,body{height:100%;background:#fff;display:flex;justify-content:center;align-items:center;}
  img{max-width:100%;max-height:100%;box-shadow:none;}
  @page{size:A4 portrait;margin:0;}
</style></head><body>
  <img src="\${imgData}" onload="setTimeout(()=>{window.print();window.close();},300);">
</body></html>\`);
        pw.document.close();
      } catch (e) { console.error(e); alert('Print failed – use Download'); }
    }
          </script>
        </body>
      </html>
  `;

      // ADD THESE 2 LINES:
      formWindow.guestName = guestName;
      formWindow.reservationId = guest.reservationId;

      formWindow.document.open();
      formWindow.document.write(htmlContent);
      formWindow.document.close();

      // ✅ After printing, update Hostaway custom field (ID 84716)
      /*
      const updatePayload = {
        customFieldValues: [
          {
            customFieldId: 84716,
            value: "Yes",
          },
        ],
      };
 
      const updateResponse = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
        body: JSON.stringify(updatePayload),
      });
 
      const updateResult = await updateResponse.json();
 
      if (!updateResponse.ok || updateResult.status === "fail") {
        console.error("❌ Failed to update Print Check In field:", updateResult);
      } else {
        console.log("✅ Successfully marked Print Check In as 'Yes' in Hostaway.");
        setCanPrintCheckIn(false);
      }
      */

      // 1. Validate guest exists
      /* DISABLED - causing unnecessary errors
      if (!guest || !guest.reservationId) {
        setSnackbar?.({
          open: true,
          message: "Could not load reservation for printing.",
          severity: "error",
        });
        return;
      }
      */
      if (guest) guest.printedCheckIn = true;
      localStorage.setItem(`printed_checkin_${guest.reservationId}`, "true");
      setCanPrintCheckIn(false);
      setShowPrintCheckIn(false);

      // ✅ Update Local DB (Postgres)
      try {
        fetch(`/api/fdo/reservations/${guest.reservationId}/print`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "checkin" })
        }).catch(err => console.error("Local DB print update failed:", err));
      } catch (e) {}

      // Note: Hostaway API doesn't support updating custom field values via browser directly due to CORS
      // The custom field is now handled by the backend
    } catch (err) {
      console.error("Error preparing check-in form:", err);
    }
  };

  const handlePrintCheckOut = async () => {
    try {
      // Prevent view_only users and custom users without complete access from printing
      if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
        console.log("❌ User does not have permission to print check-out forms");
        setSnackbar({
          open: true,
          message: "You do not have permission to print forms",
          severity: "error",
        });
        return;
      }

      // ✅ Fetch latest reservation from API
      const response = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${HOSTAWAY_TOKEN}` },
      });

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      let damageCharges = "";

      const data = await response.json();
      const reservation = data.result || {};
      const guestName = reservation.guestName || "N/A";

      const listingMapId = guest.listingName || "N/A";
      const channelName = reservation.channelName || "N/A";
      const currencyLabel = channelName === "direct" ? (reservation.currency || "") : "PKR";

      // ✅ Fetch Finance Fields from API (includes deposit and damage logic)
      const { lateCheckOutCharges, allTotalCharges, financeFields } = await getFinanceFields(
        guest.reservationId
      );

      const departure = reservation.departureDate || guest.checkoutDate || "N/A";

      const checkOutTime = reservation.checkOutTime
        ? formatTime(reservation.checkOutTime)
        : guest.checkoutTime
          ? formatTime(guest.checkoutTime)
          : "N/A";

      const vehicleNumber =
        reservation.customFieldValues?.find((field) => field.customField?.name === "Vehicle Number")
          ?.value || "Not provided";

      let CheckOutSecurityDeposit = "N/A";
      if (Array.isArray(reservation.financeField)) {
        const securityField = reservation.financeField.find(
          (field) => field.alias === "Security Deposit" && field.isDeleted === 0
        );
        if (securityField) {
          CheckOutSecurityDeposit = securityField.total ?? securityField.value ?? "0";
        }
      }

      // ✅ Get Damage Fee directly from reservation.financeField
      let CheckOutDamageDeposit = 0;
      if (Array.isArray(reservation.financeField)) {
        const damageField = reservation.financeField.find(
          (field) => field.alias === "Damage Fee" && field.isDeleted === 0
        );
        if (damageField) {
          CheckOutDamageDeposit = damageField.total ?? damageField.value ?? 0;
        }
      }

      let actualCheckOutTime = "N/A";

      if (Array.isArray(reservation.customFieldValues)) {
        const checkOutField = reservation.customFieldValues.find(
          (item) =>
            item.customField?.name === "Actual Check-out Time" && item.customFieldId === 76282
        );

        if (checkOutField && checkOutField.value) {
          const parsedDate = new Date(checkOutField.value);
          if (!isNaN(parsedDate)) {
            actualCheckOutTime = parsedDate.toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            });
          } else {
            actualCheckOutTime = checkOutField.value; // fallback if not a date
          }
        }
      }


      // ✅ Fetch Actual Check-in Time from Hostaway
      let actualCheckInTime = "N/A";

      if (Array.isArray(reservation.customFieldValues)) {
        const checkInField = reservation.customFieldValues.find(
          (item) =>
            item.customField?.name === "Actual Check-in Time"
        );

        if (checkInField && checkInField.value) {
          const parsedDate = new Date(checkInField.value);
          if (!isNaN(parsedDate)) {
            actualCheckInTime = parsedDate.toLocaleString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            });
          } else {
            actualCheckInTime = checkInField.value; // fallback if not a date
          }
        }
      }

      const reservationId = reservation.reservationId || "N/A";

      const formWindow = window.open("", "_blank");

      // Fill guest details dynamically
      const htmlContent = `
    <!DOCTYPE html>
<html lang="en">
<head>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
  <style>
    body {
      margin: 0;
      padding: 15px;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 80vh;
      background-color: #F0F0F0;
      font-family: Arial, Helvetica, sans-serif;
      color: #333;
    }
    .form {
      width: 170mm;
      height: auto;
      min-height: 240mm;
      padding: 20px;
      margin: auto;
      background-color: white;
      border-radius: 5px;
      border: 1px solid lightblue;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    }
    .logo-img {
      display: flex;
      justify-content: center;
      height: 60px;
      margin: 10px 0px 20px 0px;
    }
    .logo-img img {
      height: 100%;
      width: auto;
      object-fit: contain;
    }
    h2 {
      text-align: center;
      font-size: 20px;
      margin: 10px 0;
    }
    p {
      line-height: 1.5;
      font-size: 17px;
    }
    ul {
      list-style: none;
      padding-left: 0;
      margin: 10px 0;
    }
    ul li {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 13px;
      line-height: 1.2;
      margin-bottom: 2px;
    }
  
    .signature-section {
      margin-top: 35px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
    }
    .signature-block {
      width: 30%;
      text-align: center;
    }
    .signature-line {
      border-bottom: 1px solid #333;
      margin: 30px auto 10px;
      width: 80%;
    }
    .footer {
      font-size: 12px;
      color: #666;
      display: inline-block;
      vertical-align: top;
      width: 50%;
      margin-top: -15px;
    }
    
    .charges-breakdown {
    margin: -15px 23px 0px 0px;
      padding: 15px;
      display: inline-block;
      vertical-align: top;
    }
    .charges-breakdown p {
      margin: 5px 0;
      font-size: 12px;
      color: #333;
      line-height: 1.3;
    }
    .charges-breakdown h6 {
      margin: 5px 0;
      font-size: 16px;
      color: #333;
      line-height: 1.3;
      font-family: math;
    }
    
    /* Container to hold both elements */
    .footer-container {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: -19px;
    }
}

    .charges-breakdown p:first-child {
      font-weight: bold !important;
      color: #2c3e50;
      font-size: 14px;
    }

    .download-btn {
    padding: 7px 9px;
    background: transparent;
    color: black;
    border: 1px solid black;
    border-radius: 5px;
    cursor: pointer;
    font-size: 15px;
    transition: background 0.3s, color 0.3s;
}

    .download-btn:hover {
      background: black;
      color: white;
    }
  </style>
</head>
<body>

  <div class="form">
    <div style="position: absolute; top: 5px; right: 5px; z-index: 1000;">
    <button onclick="printForm()" class="download-btn">
              Print
            </button>
  <button onclick="downloadForm()" class="download-btn">
    Download
  </button>
</div>
<div style="display: flex; flex-direction: row;">
    <div class="logo-img">
      <img src="img/booknrent-logo.png" alt="Booknrent Logo">
    </div>
    <div class="heading-text" style="margin: 20px 0px 0px 40px !important">
  ${(() => {
          const today = new Date().toISOString().split("T")[0];
          const arrival = guest.arrivalDate ? guest.arrivalDate.split("T")[0] : null;
          const departure = guest.departureDate ? guest.departureDate.split("T")[0] : null;

          let formTitle = "Check-out Form ";
          let formSubtitle = `Actual Check-out Date / Time: ${actualCheckOutTime}`;

          if (arrival === today) {
            formTitle = "Same Day Check-out Form";
            formSubtitle = `Actual Check-out Date / Time: ${actualCheckOutTime}`;
          } else if (departure && departure !== today) {
            formTitle = "Early Check-out Form";
            formSubtitle = `Actual Check-out Date / Time: ${actualCheckOutTime}`;
          }

          return `
    <h3 style="margin: 0; font-size: 20px; font-weight: bold;">
      ${guestName}'s ${formTitle} 
      <span style="font-size: 12px; color: #666;">(${guest.reservationId})</span>
    </h3>
    <p style="margin: 4px 0 0 0; font-size: 14px; color: #555;">
      ${formSubtitle}
    </p>
  `;
        })()}
   </div>
   <!--
   <div style="position: absolute; margin-left: 400px; margin-top: -9px; font-family: monospace; color: #b6bfb6;">
    Printed By: ${user?.name}
  </div>
  -->
</div>

    <p style="text-align: center; font-size: 18px;">
      I, <strong>${guestName}</strong>, have checked out of the apartment <strong>${listingMapId}</strong> on <strong>${departure}</strong>. 
      I have checked the apartment for any personal belongings, including but not limited to:
    </p>

    <div class="single-line-layout">
  <div class="items-list">
    <div class="list-item">• Clothes</div>
    <div class="list-item">• Jewelry</div>
    <div class="list-item">• Cash</div>
    <div class="list-item">• Electronics</div>
    <div class="list-item">• Other valuables</div>
  </div>
  <div class="info-fields">
  <div class="field-group">
    <span class="field-label">Actual Check-in Date / Time:</span>
    <span class="field-value" >${actualCheckInTime || "N/A"}</span>
  </div>
  <div class="field-group">
    <span class="field-label">Vehicle Number:</span>
    <span class="field-value" >${vehicleNumber || "N/A"}</span>
  </div>

  <div class="field-group">
    <span class="field-label">Standard Check Out Date & Time:</span>
    <span class="field-value">${departure} & ${checkOutTime}</span>
  </div>

  <div class="field-group">
  <span class="field-label">Late Check out Charges (if applicable):</span>
  <span class="field-value">
    ${lateCheckOutCharges || "0"}
    ${channelName === "direct" ? currencyLabel : "PKR"}
  </span>
</div>

<div class="field-group">
  <span class="field-label">Any other Charges (if applicable):</span>
  <span class="field-value">
   ${allTotalCharges ? Math.round(allTotalCharges) : "0"}
    ${channelName === "direct" ? currencyLabel : "PKR"}
  </span>
</div>

<div class="field-group">
  <span class="field-label">Security Deposit Amount Returned:</span>
  <span class="field-value">
    ${CheckOutSecurityDeposit || "0"}
    ${channelName === "direct" ? currencyLabel : "PKR"}
    <span style="font-weight: normal;">☐ Cash / ☐ IBFT</span>
  </span>
</div>

</div>


</div>

<style>
  .single-line-layout {
    display: flex;
    gap: 20px;
    margin: 45px 0;
  }

  .items-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .list-item {
    font-size: 14px;
    white-space: nowrap;
  }

  .info-fields {
    flex: 2;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid black;
    padding: 10px 5px 10px 15px;
    margin: -8px 0px 10px 0px;
  }

  .field-group {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .field-label {
    font-size: 13px;
    white-space: nowrap;
    min-width: 200px;
  }

  .field-value {
    font-size: 13px;
    font-weight: bold;
    white-space: nowrap;
  }
</style>

    <p style="font-size: 17px; text-align: center; margin-top: -15px; margin-bottom: 45px;">
      I have found all of my belongings and have taken them with me. <br>
      I understand that the Apartment management/host is not responsible for any valuables that are left behind.
    </p>

    <div class="signature-section" style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 20px;">
      <!-- LEFT: Guest Signature -->
      <div class="signature-block" style="display: flex; flex-direction: column; align-items: flex-start; text-align: left;">
        <div class="signature-line" style="margin: 0 0 6px 0; width: 140px;"></div>
        <p style="font-size: 13px; font-weight: bold; margin: 0;">Guest Signature</p>
      </div>

      <!-- CENTER: Admin Operations + checkout info -->
      <div class="signature-block" style="display: flex; flex-direction: column; align-items: center; text-align: center;">
        <div class="signature-line" style="margin: 0 0 6px 0; width: 140px;"></div>
        <p style="font-size: 13px; margin: 0; font-weight: bold;">Admin Operations</p>
        <p style="font-size: 13px; margin: 3px 0; font-weight: bold;">Check Out Time: 12:00 NOON</p>
        <p style="font-size: 10px; margin: 3px 0; white-space: nowrap;">(Late Check Out charges applicable @ Rs. 1000 per hour)</p>
        <p style="font-size: 10px; margin: 3px 0;">(*Subject to Availability)</p>
      </div>

      <!-- RIGHT: Management Team -->
      <div class="signature-block" style="display: flex; flex-direction: column; align-items: flex-end; text-align: right;">
        <div class="signature-line" style="margin: 0 0 6px 0; width: 140px;"></div>
        <p style="font-size: 13px; margin: 0; font-weight: bold;">Management Team</p>
        <p style="font-size: 10px; margin: 4px 0 0 0; color: #383b39; white-space: nowrap;">(${user?.name}) ${new Date().toLocaleString()}</p>
      </div>
    </div>
<div class="footer-container" style="margin-top: 10px;">
  <div class="footer">
    <p>📞 0300-0454711</p>
    <p>📍 30-A, Block L, Gulberg 3, Lahore</p>
  </div>

  ${allTotalCharges > 0
          ? `
    <div class="charges-breakdown">
      <h6 style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">Charges Breakdown:</h6>
      ${financeFields.baseRate > 0
            ? `<p>• <strong>Base Rate:</strong> ${financeFields.baseRate.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.cleaningFeeValue > 0
            ? `<p>• <strong>Cleaning Fee:</strong> ${financeFields.cleaningFeeValue.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.additionalCleaningFee > 0
            ? `<p>• <strong>Cleaning Fee:</strong> ${financeFields.additionalCleaningFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.midstayCleaningFee > 0
            ? `<p>• <strong>Midstay Cleaning Fee:</strong> ${financeFields.midstayCleaningFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
     ${CheckOutDamageDeposit !== 0
            ? `<p>• <strong>Damage Deposit:</strong> ${CheckOutDamageDeposit} ${currencyLabel}</p>`
            : ""
          }
${CheckOutSecurityDeposit !== "0"
            ? `<p>• <strong>Security Deposit:</strong> ${CheckOutSecurityDeposit} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.salesTax > 0
            ? `<p>• <strong>Sales Tax:</strong> ${financeFields.salesTax.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.earlyCheckinFee > 0
            ? `<p>• <strong>Early Check-in Fee:</strong> ${financeFields.earlyCheckinFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.bedLinenFee > 0
            ? `<p>• <strong>Bed Linen Fee:</strong> ${financeFields.bedLinenFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.extraBedsFee > 0
            ? `<p>• <strong>Extra Beds Fee:</strong> ${financeFields.extraBedsFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.lateCheckoutFee > 0
            ? `<p>• <strong>Late Checkout Fee:</strong> ${financeFields.lateCheckoutFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.damageDeposit > 0
            ? `<p>• <strong>Damage Deposit:</strong> ${financeFields.damageDeposit.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.parkingFee > 0
            ? `<p>• <strong>Parking Fee:</strong> ${financeFields.parkingFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.serviceFee > 0
            ? `<p>• <strong>Service Fee:</strong> ${financeFields.serviceFee.toFixed(
              2
            )}  ${currencyLabel}</p>`
            : ""
          }
      ${financeFields.towelChangeFee > 0
            ? `<p>• <strong>Towel Change Fee:</strong> ${financeFields.towelChangeFee.toFixed(
              2
            )} ${currencyLabel}</p>`
            : ""
          }
    </div>
    `
          : ""
        }
</div>
<div>
<span style="margin-left: -19px;"> ✂-------------------------------------------------------------------------------------------------------------------------
</span>
</div>
<div>
<div>
<div style="margin: 10px 0; padding: 10px; border: 1px solid #ccc; border-radius: 5px;">
  <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
    <div style="flex: 1;">
      <p style="margin: 5px 0;"><strong>Guest Name:</strong> <br> ${guestName || "N/A"}</p>
    </div>
    <div style="flex: 1;">
      <p style="margin: 5px 0;">
  <strong>Vehicle Number:</strong> <br>
  <span>
    ${vehicleNumber || "N/A"}
  </span>
</p>

    </div>
  </div>
  <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
    
    <div style="flex: 1;">
      <p style="margin: 5px 0;"><strong>Apartment:</strong> <br> ${listingMapId || "N/A"}</p>
    </div>
    <div style="flex: 1;">
      <p style="margin: 5px 0;"><strong>Departure Date and Time:</strong> <br> ${actualCheckOutTime || "N/A"
        }</p>
    </div>
  </div>
  
    </div>
</div>
<p style="text-align: center; margin: -2px 0px -6px 0px;">Thank you for staying with <img src="img/booknrent-logo2.png" alt="Booknrent Logo" style="width: 95px; object-fit: contain; margin-bottom: -2px;">, Good Bye!</p>
</div>

  <script>
  async function downloadForm() {
    const formElement = document.querySelector('.form');
    const canvas = await html2canvas(formElement, {
      scale: 2,
      logging: false,
      useCORS: true
    });
    
    const link = document.createElement('a');
    link.download = \`${guestName}'s Checkout-form ${guest.reservationId}.png\`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }
  
  // ---------- PRINT ----------
    async function printForm() {
      try {
        const el = document.querySelector('.form');
        const canvas = await html2canvas(el, {scale:3, useCORS:true, backgroundColor:'#fff'});
        const imgData = canvas.toDataURL('image/png');

        const pw = window.open('', '_blank', 'width=900,height=1000');
        pw.document.write(\`
<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Print</title>
<style>
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html,body{height:100%;background:#fff;display:flex;justify-content:center;align-items:center;}
  img{max-width:100%;max-height:100%;box-shadow:none;}
  @page{size:A4 portrait;margin:0;}
</style></head><body>
  <img src="\${imgData}" onload="setTimeout(()=>{window.print();window.close();},300);">
</body></html>\`);
        pw.document.close();
      } catch (e) { console.error(e); alert('Print failed – use Download'); }
    }
</script>
</body>
</html>
  `;

      formWindow.document.open();
      formWindow.document.write(htmlContent);
      formWindow.document.close();

      // ✅ 3. After printing, update Hostaway custom field (ID 84717)
      /*
      const updatePayload = {
        customFieldValues: [
          {
            customFieldId: 84717,
            value: "Yes",
          },
        ],
      };
 
      const updateResponse = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
        body: JSON.stringify(updatePayload),
      });
      */

      // ✅ Update Local DB (Postgres)
      try {
        fetch(`/api/fdo/reservations/${guest.reservationId}/print`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "checkout" })
        }).catch(err => console.error("Local DB print update failed:", err));
      } catch (e) {}

      if (guest) guest.printedCheckOut = true;
      localStorage.setItem(`printed_checkout_${guest.reservationId}`, "true");
      setCanPrintCheckOut(false);
    } catch (err) {
      console.error("Error preparing check-in form:", err);
    }
  };

  // New function for Mark Check In
  const handleMarkCheckIn = async () => {
    console.log(" Starting check-in process...");
    setIsCheckingIn(true);

    // Define the Table 2 URL at the top to avoid scoping issues
    const teableUrl = process.env.REACT_APP_TEABLE_TABLE_URL_QR_EXPIRATION || `https://teable.namuve.com/api/table/tblewe7HY1LFBRGsl7T/record`;
    const table2Url = teableUrl; // For backward compatibility within this function

    // Prevent view_only users and custom users without complete access from marking check-in
    if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
      console.log(" User does not have permission to mark check-in");
      setSnackbar({
        open: true,
        message: "You do not have permission to mark check-in",
        severity: "error",
      });
      setIsCheckingIn(false);
      return;
    }

    // Make sure setSnackbar exists (for safety)
    if (!setSnackbar) {
      console.warn(" setSnackbar not provided to ReservationCard!");
    }

    try {
      const getResUrl = `${HOSTAWAY_API}/${guest.reservationId}`;
      const res = await fetch(getResUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch reservation");

      const resData = await res.json();

      const getCustomFieldValue = (name) => {
        return resData.result?.customFieldValues?.find(field => field.customField?.name === name)?.value || "";
      };

      const existingField = resData.result?.customFieldValues?.find(
        (field) => field.customFieldId === 76281 && field.value && field.value.trim() !== ""
      );

      // Check if check-in was manually cleared - if so, clear QR expiration in Teable
      if (!existingField) {
        // Hostaway check-in is empty, clear QR expiration
        try {
          const teableSearchUrl = `${teableUrl}?search=${guest.reservationId}&search=Reservation+ID&search=true`;
          const teableRes = await fetch(teableSearchUrl, {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_FDO}`,
            }
          });

          if (teableRes.ok) {
            const teableData = await teableRes.json();
            // Matching record from search results
            const record = teableData.records?.[0];

            if (record) {
              // Clear QR Expiration field
              await fetch(`https://teable.namuve.com/api/table/tblewe7HY1LFBRGsl7T/record/${record.id}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_FDO}`,
                },
                body: JSON.stringify({
                  fieldKeyType: 'name',
                  record: {
                    fields: {
                      "QR Expiration": null  // Clear the expiration
                    }
                  }
                })
              });
              console.log('✅ Cleared QR Expiration in Teable because check-in was manually cleared');
            }
          }
        } catch (err) {
          console.error('Error clearing QR expiration:', err);
        }
      }

      if (existingField) {
        console.log(`🛑 Check-in already recorded: ${existingField.value}`);
        setIsCheckedIn(true);

        // ✅ Snackbar: Already checked-in
        setSnackbar({
          open: true,
          message: `Already checked in at ${existingField.value}`,
          severity: "warning",
        });
        return;
      }

      const now = new Date();
      const formattedDateTime = now.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      const apiUrl = `${HOSTAWAY_API}/${guest.reservationId}?forceOverbooking=1`;

      const updateRes = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
        body: JSON.stringify({
          guestName: resData.result?.guestName || guest.guestName || "Guest Name",
          customFieldValues: [
            {
              customFieldId: 76281,
              value: formattedDateTime,
            },
          ],
        }),
      });

      if (!updateRes.ok) throw new Error("Failed to update reservation");

      console.log(`✅ Check-in time saved: ${formattedDateTime}`);
      setIsCheckedIn(true);
      setShowPrintCheckIn(true);

      // ✅ Save QR expiration to Teable (Table 2)
      try {
        const expirationTime = Date.now() + 10 * 60 * 1000;
        setQrExpiration(expirationTime);
        console.log('🔄 Saving QR expiration to Teable after mark check-in:', expirationTime);

        // Search Table 2 for existing QR record
        const table2SearchUrl = `${table2Url}?search=${guest.reservationId}&search=Reservation+ID&search=true`;
        const searchRes = await fetch(table2SearchUrl, {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_FDO}`,
          }
        });

        if (searchRes.ok) {
          const searchData = await searchRes.json();
          // Record from search results
          const existingRecord = searchData.records?.[0];

          if (existingRecord) {
            // Record exists → PATCH (update)
            console.log('📝 Updating existing QR record...');
            console.log('🔍 Record ID:', existingRecord.id);
            console.log('🔍 Expiration:', expirationTime);
            console.log('🔍 Pakistan Time:', new Date().toLocaleString('en-US', {
              timeZone: 'Asia/Karachi',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            }));

            const updateRes = await fetch(`https://teable.namuve.com/api/table/tblewe7HY1LFBRGsl7T/record/${existingRecord.id}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_FDO}`,
              },
              body: JSON.stringify({
                fieldKeyType: 'name',
                record: {
                  fields: {
                    "QR Expiration": String(expirationTime),
                    "Create Date and Time": new Date().toLocaleString('en-US', {
                      timeZone: 'Asia/Karachi',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })
                  }
                }
              })
            });

            if (updateRes.ok) {
              console.log('✅ QR expiration updated in Teable');
            } else {
              const errorText = await updateRes.text();
              console.error('❌ Failed to update QR expiration');
              console.error('Status:', updateRes.status);
              console.error('Error details:', errorText);
              console.error('Payload sent:', JSON.stringify({
                fieldKeyType: 'name',
                fields: {
                  "QR Expiration": String(expirationTime),
                  "Create Date and Time": new Date().toLocaleString('en-US', {
                    timeZone: 'Asia/Karachi',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })
                }
              }));
            }
          } else {
            // Record doesn't exist → POST (create new)
            console.log('📝 Creating new QR record...');
            const createRes = await fetch(table2Url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_API_TOKEN_FDO}`,
              },
              body: JSON.stringify({
                fieldKeyType: 'name',
                records: [
                  {
                    fields: {
                      "Reservation ID": String(guest.reservationId),
                      "QR Expiration": String(expirationTime),
                      "Create Date and Time": new Date().toLocaleString('en-US', {
                        timeZone: 'Asia/Karachi',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      })
                    }
                  }
                ]
              })
            });

            if (createRes.ok) {
              console.log('✅ QR expiration created in Teable');
            } else {
              console.error('❌ Failed to create QR expiration:', await createRes.text());
            }
          }
        }
      } catch (err) {
        console.error('❌ Error saving QR expiration:', err);
      }

      // ✅ Snackbar: success message
      setSnackbar({
        open: true,
        message: `Check-in recorded at ${formattedDateTime}`,
        severity: "success",
      });

      // ✅ Trigger webhook on n8n after successful check-in
      const webhookBody = {
        reservation_id: guest.reservationId,
        guest_name: guest.guestName || "Unknown Guest",
        room_number: guest.listingName || "Unknown Listing",
        check_in_date: guest.arrivalDate ? guest.arrivalDate.split("T")[0] : "Unknown Date",
        actual_check_in_local: formattedDateTime,
        event: "Checked-In",
      };

      const checkinWebhookUrl = process.env.REACT_APP_N8N_WEBHOOK_CHECKIN_CHECKOUT || "https://n8n.namuve.com/webhook/196e4e1e-4c7a-420b-8fe5-a3674403395b";
      fetch(checkinWebhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookBody),
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.message || "Failed to trigger Webhook");
          }
          console.log("✅ Webhook triggered successfully:", data);
        })
        .catch((error) => {
          console.error("❌ Error triggering Webhook:", error);
        });

      // ✅ Send data to local trigger checkin endpoint

      const triggerCheckinData = {
        "Reservation ID": guest.reservationId,
        "CNIC#": getCustomFieldValue("CNIC#") || getCustomFieldValue("ID card Number/ Passport number"),
        "Name": resData.result?.guestName || guest.guestName || "",
        "Father Name": getCustomFieldValue("Father's Name") || getCustomFieldValue("Father Name"),
        "Address": getCustomFieldValue("Address"),
        "Gender": getCustomFieldValue("Gender"),
        "Cell Phone#": resData.result?.phone || guest.phone || "",
        "Province": getCustomFieldValue("Province"),
        "District": getCustomFieldValue("District") || getCustomFieldValue("City/District") || "",
        "Total# of Accompaning Guest": resData.result?.numberOfGuests || guest.numberOfGuests || "",
        "Children": resData.result?.children || guest.children || 0,
        "Purpose of Visit": getCustomFieldValue("Purpose of Travel") || getCustomFieldValue("Purpose Of Travel"),
        "Check-out Date": resData.result?.departureDate ? resData.result.departureDate.split("T")[0] : (guest.departureDate ? guest.departureDate.split("T")[0] : ""),
        "Check-out Time": resData.result?.checkOutTime || "",
        "Room#": guest.listingName || "",
        "raw_custom_fields": resData.result?.customFieldValues || []
      };

      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_HOTEL_EYE_CHECKIN || "https://n8n.namuve.com/webhook/935f4d72-c78d-4c92-b49c-4392f038feba";

      fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerCheckinData),
      })
        .then(async (response) => {
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.message || "Failed to trigger webhook");
          }
          console.log("✅ Webhook triggered successfully");
        })
        .catch((error) => {
          console.error("❌ Error triggering webhook:", error);
        });

      // ✅ Trigger Mattermost API for check-in
      const checkinChannelsEnv = process.env.REACT_APP_MATTERMOST_CHECKIN_CHANNELS || "3arj5444gigz9n9pe8q7mdb3ke,y65jwezgg3rdmcj39nqiyehzta";
      const mattermostChannels = checkinChannelsEnv.split(",").map(c => c.trim()).filter(Boolean);
      const mattermostMessage = `📥 Check-In Alert – ${guest.guestName} for [${guest.reservationId}](https://dashboard.hostaway.com/reservations/${guest.reservationId})
👤 Checked in to 🏠 ${guest.listingName || "Unknown Listing"} at 🕒 ${formattedDateTime}, processed by ${user?.name || "Unknown User"}.`;

      const checkinToken = process.env.REACT_APP_MATTERMOST_CHECKIN_CHECKOUT_TOKEN || "tysrghkibpbifqb7dzramd8pcr";
      const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";

      mattermostChannels.forEach(channel_id => {
        const payload = { channel_id, message: mattermostMessage };
        fetch(mmUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${checkinToken}`
          },
          body: JSON.stringify(payload),
        })
          .then(async (response) => {
            const text = await response.text().catch(() => "");
            if (!response.ok) {
              throw new Error(`Mattermost API failed for channel ${channel_id}: ${response.status} - ${text}`);
            }
            console.log(`✅ Mattermost message posted successfully for check-in to channel ${channel_id}`);
          })
          .catch((error) => {
            console.error(`❌ Error posting to Mattermost channel ${channel_id}:`, error);
          });
      });

      // ✅ Send message to Google Chat
      const chatMessage = [
        `📥 Check-In Alert for ${guest.guestName}!`,
        `👤 ${guest.guestName || "Guest"} has checked in to 🏠 ${guest.listingName || "Unknown Listing"
        } at 🕒 ${formattedDateTime} by ${user?.name || "Unknown User"}.`,
        "",
        `🔗 https://dashboard.hostaway.com/reservations/${guest.reservationId}`,
      ].join("\n");

      /* fetch(
        //"https://chat.googleapis.com/v1/spaces/AAQAulzBppE/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=EASUdNTxA361HwuL4D4nlkGiJCEQ8COSY6wU3DST-Lw".// Testing
        "https://chat.googleapis.com/v1/spaces/AAQANIuKBgw/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=oSxAMzHxA2VKi4AoEK_6Wf335vxr1H5Cd7XNxzeyS-o", //Production
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: chatMessage,
          }),
        }
      )
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.message || "Failed to send Chat message");
          console.log("✅ Google Chat message sent successfully:", data);
        })
        .catch((error) => {
          console.error("❌ Error sending message to Google Chat:", error);
        }); */

      // ✅ Prepare Teable record
      const teablePayload = {
        records: [
          {
            fields: {
              User: user?.name,
              "Button Clicked": "Mark Check In",
              "Date & Time": formattedDateTime,
            },
          },
        ],
      };

      // ✅ Send record to Teable
      const teableRes = await fetch(TEABLE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TEABLE_API_TOKEN}`,
        },
        body: JSON.stringify(teablePayload),
      });

      if (!teableRes.ok) {
        const errText = await teableRes.text();
        throw new Error(`Failed to log to Teable: ${errText}`);
      }

      console.log("✅ Logged Check-In action to Teable");

      // ✅ Check SmartLock status and store it
      try {
        const hostawayListingId = resData.result?.listingMapId || guest.listingId || null;

        if (hostawayListingId) {
          const smartlockBaseUrl = process.env.REACT_APP_TEABLE_TABLE_URL_SMARTLOCK || "https://teable.namuve.com/api/table/tblZ9gqNbhCXDHm3nQK/record";
          const teableResponse = await fetch(
            `${smartlockBaseUrl}?search=${hostawayListingId}&search=Listing+IDs&search=true`,
            {
              headers: {
                Authorization: `Bearer ${process.env.REACT_APP_TEABLE_TOKEN_SMARTLOCK}`,
              },
            }
          );

          if (teableResponse.ok) {
            const teableData = await teableResponse.json();
            const records = teableData.records || [];

            if (records.length > 0) {
              const listingRecord = records[0].fields || {};
              const smartLockStatus = listingRecord["Has SmartLock"] === "Yes";

              console.log(`🔒 SmartLock Status: ${smartLockStatus ? "Yes" : "No"}`);

              // Store SmartLock status for later use
              setHasSmartLock(smartLockStatus);

              if (smartLockStatus) {
                console.log("✅ SmartLock detected - QR code will be available on Print Check In");
              }
            }
          }
        }
      } catch (qrError) {
        console.error("❌ Error checking SmartLock:", qrError);
        setHasSmartLock(false);
      }

      await handleWebhook();

      // ✅ Wait for Teable database to sync before refreshing button status
      // This ensures QR expiration data is available for printing
      await new Promise(resolve => setTimeout(resolve, 1000));

      // ✅ Refresh print button status after check-in
      await fetchPrintButtonStatus(guest.reservationId);

      // Send double reservation alert if applicable
      if (hasDoubleReservation) {
        // await sendDoubleReservationAlert("Check-In");
      }
    } catch (err) {
      console.error("❌ Error in handleMarkCheckIn:", err);

      // ✅ Snackbar: error message
      setSnackbar({
        open: true,
        message: `Error during check-in: ${err.message}`,
        severity: "error",
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Function to open QR code in new window
  const handleOpenQRCode = () => {
    if (!qrCodeData) return;

    const { dataUrl, info, formattedDateTime } = qrCodeData;

    const qrWindow = window.open("", "_blank", "width=450,height=600");
    qrWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8" />
        <title>SmartLock QR Code</title>
        <style>
          body {
            margin: 0;
            padding: 20px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f5f5f5;
            font-family: 'Arial', sans-serif;
          }
          .container {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            text-align: center;
            max-width: 380px;
          }
          h2 {
            color: #333;
            margin: 0 0 10px 0;
            font-size: 24px;
          }
          .guest-name {
            color: #047857;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 20px;
          }
          img {
            width: 280px;
            height: 280px;
            border: 3px solid #047857;
            border-radius: 12px;
            padding: 10px;
            background: white;
            margin: 20px 0;
          }
          .info {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 10px;
            margin-top: 20px;
            font-size: 14px;
            color: #666;
            text-align: left;
          }
          .info p {
            margin: 8px 0;
          }
          .info strong {
            color: #333;
          }
          .btn {
            margin-top: 20px;
            padding: 12px 30px;
            background: #047857;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            font-weight: bold;
            transition: background 0.3s;
          }
          .btn:hover {
            background: #065f46;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <h2>🔐 SmartLock Access</h2>
          <div class="guest-name">${info.guestName}</div>
          <img src="${dataUrl}" alt="QR Code" />
          <div class="info">
            <p><strong>Apartment:</strong> ${info.apartment}</p>
            <p><strong>Check-In:</strong> ${formattedDateTime}</p>
            <p><strong>Reservation:</strong> ${info.reservationId}</p>
          </div>
          <p style="margin-top: 15px; color: #666; font-size: 12px;">Scan this QR code to access the SmartLock</p>
          <button class="btn" onclick="window.print()">Print QR Code</button>
        </div>
      </body>
      </html>
    `);
    qrWindow.document.close();

    // Close the modal
    setShowCheckInOptions(false);
  };

  const handleCheckInClick = async () => {
    // Always show confirmation popup
    setActionType("Check-In");
    setConfirmationOpen(true);
    setCheckInMissingFields([]);
    setHotelEyeInputData({});
    setIsFetchingCheckInDetails(true);

    try {
      const getResUrl = `${HOSTAWAY_API}/${guest.reservationId}`;
      const res = await fetch(getResUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch reservation");

      const resData = await res.json();
      checkInReservationDataRef.current = resData.result;

      const getCustomFieldValue = (name) => {
        return resData.result?.customFieldValues?.find(field => field.customField?.name === name)?.value || "";
      };

      const missingFields = [];
      const checkField = (fieldName, value) => {
        if (value === "" || value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
          missingFields.push(fieldName);
        }
      };

      const guestCountry = (resData.result?.guestCountry || getCustomFieldValue("Country") || "").trim().toLowerCase();

      // If country is specified and is not Pakistan, skip validation
      if (guestCountry && guestCountry !== "pakistan" && guestCountry !== "pk") {
        setCheckInMissingFields([]);
      } else {
        checkField("Reservation ID", guest.reservationId);
        checkField("CNIC#", getCustomFieldValue("CNIC#") || getCustomFieldValue("ID card Number/ Passport number"));
        checkField("Name", resData.result?.guestName || guest.guestName);
        checkField("Father Name", getCustomFieldValue("Father's Name") || getCustomFieldValue("Father Name"));
        checkField("Address", getCustomFieldValue("Address"));
        checkField("Gender", getCustomFieldValue("Gender"));
        checkField("Cell Phone#", resData.result?.phone || guest.phone);
        checkField("Province", getCustomFieldValue("Province"));
        checkField("District", getCustomFieldValue("District") || getCustomFieldValue("City/District"));
        checkField("Total# of Accompaning Guest", resData.result?.numberOfGuests || guest.numberOfGuests);
        checkField("Children", resData.result?.children !== undefined && resData.result?.children !== null ? resData.result?.children : (guest.children !== undefined && guest.children !== null ? guest.children : 0));
        checkField("Purpose of Visit", getCustomFieldValue("Purpose of Travel") || getCustomFieldValue("Purpose Of Travel"));
        checkField("Check-out Date", resData.result?.departureDate ? resData.result.departureDate.split("T")[0] : (guest.departureDate ? guest.departureDate.split("T")[0] : ""));
        checkField("Check-out Time", resData.result?.checkOutTime);
        checkField("Room#", guest.listingName);

        setCheckInMissingFields(missingFields);
        const initialInputData = {};
        if (missingFields.includes("Cell Phone#")) {
          initialInputData["Cell Phone#"] = "+92";
        }
        setHotelEyeInputData(initialInputData);
      }
    } catch (err) {
      console.error("Error fetching reservation for validation:", err);
    } finally {
      setIsFetchingCheckInDetails(false);
    }
  };
  const handleSaveHotelEyeAndCheckIn = async () => {
    // Validate inputs
    const missingStill = checkInMissingFields.filter(f => f !== "Reservation ID" && f !== "Room#").filter(f => !hotelEyeInputData[f] || String(hotelEyeInputData[f]).trim() === "");
    if (missingStill.length > 0) {
      if (setSnackbar) setSnackbar({ open: true, message: `Please fill all required fields: ${missingStill.join(", ")}`, severity: "warning" });
      return;
    }

    setIsSavingHotelEyeData(true);
    try {
      const resData = checkInReservationDataRef.current;
      if (!resData) throw new Error("Reservation data not found. Please try again.");

      // Fetch all custom fields dynamically to get their IDs
      const customFieldsRes = await fetch(`${HOSTAWAY_BASE_URL}/customFields`, {
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
      });

      if (!customFieldsRes.ok) {
        throw new Error("Failed to fetch custom fields metadata from Hostaway.");
      }

      const customFieldsData = await customFieldsRes.json();
      const customFieldDefinitions = customFieldsData.result || [];

      const getCustomFieldId = (name) => {
        // Try finding it in the current reservation first
        let id = resData.customFieldValues?.find(field => field.customField?.name === name)?.customFieldId;
        if (id) return id;
        // Fallback to searching the global custom field definitions
        id = customFieldDefinitions.find(field => field.name === name)?.id;
        return id;
      };

      // Construct Hostaway PUT body
      const putBody = {};
      const customFieldsToUpdate = [];

      if (hotelEyeInputData["CNIC#"]) {
        const id = getCustomFieldId("CNIC#") || getCustomFieldId("ID card Number/ Passport number");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["CNIC#"] });
      }
      if (hotelEyeInputData["Father Name"]) {
        const id = getCustomFieldId("Father's Name") || getCustomFieldId("Father Name");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["Father Name"] });
      }
      if (hotelEyeInputData["Address"]) {
        const id = getCustomFieldId("Address");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["Address"] });
      }
      if (hotelEyeInputData["Gender"]) {
        const id = getCustomFieldId("Gender");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["Gender"] });
      }
      if (hotelEyeInputData["Province"]) {
        const id = getCustomFieldId("Province");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["Province"] });
      }
      if (hotelEyeInputData["District"]) {
        const id = getCustomFieldId("District") || getCustomFieldId("City/District");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["District"] });
      }
      if (hotelEyeInputData["Purpose of Visit"]) {
        const id = getCustomFieldId("Purpose of Travel") || getCustomFieldId("Purpose Of Travel");
        if (id) customFieldsToUpdate.push({ customFieldId: id, value: hotelEyeInputData["Purpose of Visit"] });
      }

      // Map standard fields
      if (hotelEyeInputData["Name"]) putBody.guestName = hotelEyeInputData["Name"];
      if (hotelEyeInputData["Cell Phone#"]) putBody.phone = hotelEyeInputData["Cell Phone#"];
      if (hotelEyeInputData["Total# of Accompaning Guest"]) putBody.numberOfGuests = Number(hotelEyeInputData["Total# of Accompaning Guest"]);
      if (hotelEyeInputData["Children"]) putBody.children = Number(hotelEyeInputData["Children"]);
      if (hotelEyeInputData["Check-out Date"]) putBody.departureDate = hotelEyeInputData["Check-out Date"];
      if (hotelEyeInputData["Check-out Time"]) putBody.checkOutTime = hotelEyeInputData["Check-out Time"];

      if (customFieldsToUpdate.length > 0) {
        putBody.customFieldValues = customFieldsToUpdate;
      }

      // Send to Hostaway
      const putRes = await fetch(`${HOSTAWAY_BASE_URL}/reservations/${guest.reservationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`
        },
        body: JSON.stringify(putBody)
      });

      if (!putRes.ok) {
        const errorText = await putRes.text();
        throw new Error(`Failed to update Hostaway: ${errorText}`);
      }

      if (setSnackbar) setSnackbar({ open: true, message: "Hostaway fields updated successfully!", severity: "success" });

      // Proceed with Check-in
      setConfirmationOpen(false);
      handleMarkCheckIn();
    } catch (err) {
      console.error("Error saving Hotel Eye data to Hostaway:", err);
      if (setSnackbar) setSnackbar({ open: true, message: `Error saving data: ${err.message}`, severity: "error" });
    } finally {
      setIsSavingHotelEyeData(false);
    }
  };

  const handleCheckOutClick = () => {
    // Always show confirmation popup
    setActionType("Check-Out");
    setConfirmationOpen(true);
  };

  //✅ New function for Mark Check Out
  const handleMarkCheckOut = async () => {
    try {
      console.log("🔄 Starting Check-Out process...");
      setIsCheckingOut(true);

      // Prevent view_only users and custom users without complete access from marking check-out
      if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
        console.log("❌ User does not have permission to mark check-out");
        setSnackbar({
          open: true,
          message: "You do not have permission to mark check-out",
          severity: "error",
        });
        return;
      }

      const getResUrl = `${HOSTAWAY_API}/${guest.reservationId}`;
      const res = await fetch(getResUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch reservation");

      const resData = await res.json();

      const existingField = resData.result?.customFieldValues?.find(
        (field) => field.customFieldId === 76282 && field.value && field.value.trim() !== ""
      );

      if (existingField) {
        console.log(`🛑 Check-Out already recorded: ${existingField.value}`);
        setIsCheckedOut(true);

        if (typeof setSnackbar === "function") {
          setSnackbar({
            open: true,
            message: "Guest already checked out.",
            severity: "warning",
          });
        } else {
          console.warn("⚠️ setSnackbar prop missing in ReservationCard (Check-Out)");
        }

        return;
      }

      const now = new Date();
      const formattedDateTime = now.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      const apiUrl = `${HOSTAWAY_API}/${guest.reservationId}?forceOverbooking=1`;

      const updateRes = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
        body: JSON.stringify({
          guestName: resData.result?.guestName || guest.guestName || "Guest Name",
          customFieldValues: [
            {
              customFieldId: 76282,
              value: formattedDateTime,
            },
          ],
        }),
      });

      if (!updateRes.ok) throw new Error("Failed to update reservation");

      console.log(`✅ Check-Out time saved: ${formattedDateTime}`);
      setIsCheckedOut(true);

      // ✅ Auto-generate cleaning task for this room
      try {
        const cleaningRoomName = guest.listingName || "";
        if (cleaningRoomName) {
          const token = localStorage.getItem("token") || localStorage.getItem("authToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          const finalAptName = guest.reservationId ? `${cleaningRoomName} (${guest.reservationId})` : cleaningRoomName;
          const cleaningBody = {
            apartmentName: finalAptName,
            agent: "",
            stopwatchTimer: 0,
            toDo: "To do",
            inProgress: "",
            hold: "",
            clean: "",
            createdBy: user?.name || user?.username || "System (FDO Checkout)",
            timestamp: formattedDateTime,
          };

          fetch("/api/cleaning/save-record", {
            method: "POST",
            headers: headers,
            body: JSON.stringify(cleaningBody),
          })
            .then((res) => {
              if (res.ok) {
                console.log(`✅ Auto-generated cleaning task for room ${finalAptName}`);
                // ✅ Send Mattermost notification for the auto-generated cleaning task
                try {
                  const formattedDateTimeStr = new Date().toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  });

                  const creatorName = user?.name || user?.username || "System (FDO Checkout)";
                  const baseRoomName = cleaningRoomName.match(/\s+\((\d+)\)$/) ? cleaningRoomName.substring(0, cleaningRoomName.length - cleaningRoomName.match(/\s+\((\d+)\)$/)[0].length).trim() : cleaningRoomName;
                  const mattermostMessage = `🧹 **New Cleaning Task Created**\n🏠 Room: **${baseRoomName}**\n📌 Status: 🔴 **TO DO**\n🕒 Time: **${formattedDateTimeStr}**\n👤 Created by: **${creatorName}**`;

                  const payload = {
                    channel_id: process.env.REACT_APP_MATTERMOST_CLEANING_CHANNEL_ID || "96k3rz5uyibkjmja3n47bzfmmh",
                    message: mattermostMessage
                  };

                  const cleanToken = process.env.REACT_APP_MATTERMOST_CLEANING_TOKEN || "st18ydrda7gd5euqeyotie5h5c";
                  const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";

                  fetch(mmUrl, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      "Authorization": `Bearer ${cleanToken}`
                    },
                    body: JSON.stringify(payload),
                  })
                    .then((mmRes) => {
                      if (!mmRes.ok) {
                        console.error("❌ Failed to send auto-cleaning task Mattermost notification:", mmRes.status);
                      } else {
                        console.log("✅ Auto-cleaning task Mattermost notification sent");
                      }
                    })
                    .catch((err) => {
                      console.error("❌ Network error sending auto-cleaning task Mattermost notification:", err);
                    });
                } catch (mmError) {
                  console.error("❌ Error triggering auto-cleaning task Mattermost notification:", mmError);
                }
              } else {
                console.error("❌ Failed to auto-generate cleaning task:", res.status);
              }
            })
            .catch((err) => {
              console.error("❌ Error auto-generating cleaning task:", err);
            });
        }
      } catch (cleanErr) {
        console.error("❌ Error in cleaning task auto-generation block:", cleanErr);
      }

      if (typeof setSnackbar === "function") {
        setSnackbar({
          open: true,
          message: `Check-out recorded successfully: ${formattedDateTime}`,
          severity: "success",
        });
      }

      // ✅ Trigger webhook on n8n after successful check-out
      const webhookBody = {
        reservation_id: guest.reservationId,
        guest_name: guest.guestName || "Unknown Guest",
        room_number: guest.listingName || "Unknown Listing",
        check_in_date: guest.arrivalDate ? guest.arrivalDate.split("T")[0] : "Unknown Date",
        actual_check_out_local: formattedDateTime, // ✅ local check-out time
        event: "Checked-Out", // ✅ event type
      };

      const checkoutWebhookUrl = process.env.REACT_APP_N8N_WEBHOOK_CHECKIN_CHECKOUT || "https://n8n.namuve.com/webhook/196e4e1e-4c7a-420b-8fe5-a3674403395b";
      fetch(checkoutWebhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookBody),
      })
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.message || "Failed to trigger Webhook");
          }
          console.log("✅ Webhook triggered successfully:", data);
        })
        .catch((error) => {
          console.error("❌ Error triggering Webhook:", error);
        });

      // ✅ Calculate stay duration
      let stayDurationText = "";
      if (guest.actualCheckin && guest.actualCheckin !== "N/A") {
        const checkInDate = new Date(guest.actualCheckin);
        const checkOutDate = new Date(); // right now
        if (!isNaN(checkInDate.getTime())) {
          const diffMs = checkOutDate - checkInDate;
          if (diffMs > 0) {
            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            const durationParts = [];
            if (days > 0) durationParts.push(`${days} Day${days > 1 ? 's' : ''}`);
            if (hours > 0) durationParts.push(`${hours} Hour${hours > 1 ? 's' : ''}`);
            if (minutes > 0) durationParts.push(`${minutes} Minute${minutes > 1 ? 's' : ''}`);

            if (durationParts.length > 0) {
              stayDurationText = `\n⏳ Stay Duration: ${durationParts.join(', ')}.`;
            } else {
              stayDurationText = `\n⏳ Stay Duration: Less than a minute.`;
            }
          }
        }
      }

      // ✅ Trigger Mattermost API for check-out
      const checkoutChannelsEnv = process.env.REACT_APP_MATTERMOST_CHECKOUT_CHANNELS || "3arj5444gigz9n9pe8q7mdb3ke";
      const checkoutToken = process.env.REACT_APP_MATTERMOST_CHECKIN_CHECKOUT_TOKEN || "tysrghkibpbifqb7dzramd8pcr";
      const mmUrl = process.env.REACT_APP_MATTERMOST_WEBHOOK_URL || "https://chat.team.namuve.com/api/v4/posts";

      const mattermostPayload = {
        channel_id: checkoutChannelsEnv.split(",")[0].trim() || "3arj5444gigz9n9pe8q7mdb3ke",
        message: `📤 Check-Out Alert – ${guest.guestName} for [${guest.reservationId}](https://dashboard.hostaway.com/reservations/${guest.reservationId})
👤 Checked out from 🏠 ${guest.listingName || "Unknown Listing"} at 🕒 ${formattedDateTime}, processed by ${user?.name || "Unknown User"}.${stayDurationText}`
      };

      fetch(mmUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${checkoutToken}`
        },
        body: JSON.stringify(mattermostPayload),
      })
        .then(async (response) => {
          const text = await response.text().catch(() => "");
          if (!response.ok) {
            throw new Error(`Mattermost API failed: ${response.status} - ${text}`);
          }
          console.log("✅ Mattermost message posted successfully for check-out");
        })
        .catch((error) => {
          console.error("❌ Error posting to Mattermost:", error);
        });

      // ✅ Send message to Google Chat
      const chatMessage = [
        `📤 Check-Out Alert for ${guest.guestName}!`,
        `👤 ${guest.guestName || "Guest"} has checked out from 🏠 ${guest.listingName || "Unknown Listing"} at 🕒 ${formattedDateTime} by ${user?.name || "Unknown User"}.${stayDurationText}`,
        "",
        `🔗 https://dashboard.hostaway.com/reservations/${guest.reservationId}`,
      ].join("\n");

      /* fetch(
        "https://chat.googleapis.com/v1/spaces/AAQANIuKBgw/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=oSxAMzHxA2VKi4AoEK_6Wf335vxr1H5Cd7XNxzeyS-o",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: chatMessage,
          }),
        }
      )
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.message || "Failed to send Chat message");
          console.log("✅ Google Chat message sent successfully:", data);
        })
        .catch((error) => {
          console.error("❌ Error sending message to Google Chat:", error);
        }); */

      // ✅ Prepare Teable record
      const teablePayload = {
        records: [
          {
            fields: {
              User: user?.name,
              "Button Clicked": "Mark Check Out",
              "Date & Time": formattedDateTime,
            },
          },
        ],
      };

      // ✅ Send record to Teable
      const teableRes = await fetch(TEABLE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TEABLE_API_TOKEN}`,
        },
        body: JSON.stringify(teablePayload),
      });

      if (!teableRes.ok) {
        const errText = await teableRes.text();
        throw new Error(`Failed to log to Teable: ${errText}`);
      }

      console.log("✅ Logged Check-Out action to Teable");
      await handleWebhook();

      // Send double reservation alert if applicable
      if (hasDoubleReservation) {
        // await sendDoubleReservationAlert("Check-Out");
      }
    } catch (err) {
      console.error("❌ Error in handleMarkCheckOut:", err);

      if (typeof setSnackbar === "function") {
        setSnackbar({
          open: true,
          message: `Error during check-out: ${err.message}`,
          severity: "error",
        });
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  const now = new Date();
  const formattedDateTime = now.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";

    // Ensure it's always a string
    const str = String(timeString);

    // Handle cases like "15" → treat as "15:00"
    const normalized = str.includes(":") ? str : `${str}:00`;

    const [hoursStr, minutesStr = "00"] = normalized.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr.padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // Convert to 12-hour format
    return `${hours}:${minutes} ${ampm}`;
  };

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    setLoadingDetails(true);

    // Instantly populate initial details from guest object so basic fields are ready
    setReservationDetails((prev) => ({
      ...prev,
      id: guest.reservationId,
      guestName: guest.guestName || "Guest",
      phone: guest.phone || "N/A",
      arrivalDate: guest.arrivalDate ? String(guest.arrivalDate).split("T")[0] : "N/A",
      departureDate: guest.departureDate ? String(guest.departureDate).split("T")[0] : "N/A",
      totalPrice: guest.totalPrice || guest.amount || 0,
      currency: guest.currency || "PKR",
      nights: guest.nights || "N/A",
    }));

    // Fetch live Hostaway & Teable details concurrently in background
    await Promise.allSettled([
      fetchLiveHostawayDetails(),
      fetchPaymentDetails(),
      fetchUpsellDetails()
    ]);

    setLoadingDetails(false);
  };

  const fetchLiveHostawayDetails = async () => {
    try {
      const response = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
        },
      });

      if (!response.ok) return;

      const data = await response.json();
      const result = data.result || {};

      // Extract Price/Night custom field
      let pricePerNight = "N/A";
      if (Array.isArray(result.customFieldValues)) {
        const priceField = result.customFieldValues.find(
          (field) => field.customField?.id === 63430
        );
        if (priceField) {
          pricePerNight = priceField.value || "N/A";
        }
      }

      // Extract Early Checkin Charges custom field
      let earlyCheckinCharges = "N/A";
      if (Array.isArray(result.financeField)) {
        const earlyCheckinField = result.financeField.find(
          (field) => field.alias === "Early Checkin Charges per hour" && field.isDeleted === 0
        );
        if (earlyCheckinField) {
          earlyCheckinCharges = earlyCheckinField.total ?? earlyCheckinField.value ?? "0";
        }
      }

      // 🔹 Extract Security Deposit (from financeField array)
      let securityDeposit = "N/A";
      if (Array.isArray(result.financeField)) {
        const securityField = result.financeField.find(
          (field) => field.alias === "Security Deposit" && field.isDeleted === 0
        );
        if (securityField) {
          securityDeposit = securityField.total ?? securityField.value ?? "0";
        }
      }

      // 🔹 Calculate Remaining Balance
      let totalPaid = 0;
      let remainingBalance = "N/A";
      const totalPrice = result.totalPrice || 0;

      if (Array.isArray(result.financeField)) {
        const totalPaidField = result.financeField.find(
          (field) => field.name === "totalPaid" && field.isDeleted === 0
        );
        if (totalPaidField) {
          totalPaid = totalPaidField.total ?? totalPaidField.value ?? 0;
        }
        const refundField = result.financeField.find(
          (field) => field.name === "refundExcludedSum" && field.isDeleted === 0
        );
        if (refundField) {
          totalPaid += (refundField.total ?? refundField.value ?? 0);
        }
      }

      if (totalPrice && totalPaid >= 0) {
        const balance = totalPrice - totalPaid;
        remainingBalance = balance > 0 ? balance : "0";
      }

      setReservationDetails({
        ...result,
        pricePerNight,
        earlyCheckinCharges,
        securityDeposit,
        remainingBalance,
        totalPaid,
      });
    } catch (err) {
      console.warn("Notice fetching live Hostaway reservation details:", err);
    }
  };

  // ✅ Helper Function: Fetch Finance Fields for a Reservation
  async function getFinanceFields(reservationId) {
    try {
      const response = await fetch(
        `${HOSTAWAY_BASE_URL}/financeStandardField/reservation/${reservationId}`,
        {
          headers: {
            Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch reservation finance fields");
        return {
          securityDepositFee: "",
          lateCheckOutCharges: "",
          allTotalCharges: "",
          financeFields: {},
          CheckOutSecurityDeposit: 0,
          CheckOutDamageDeposit: 0,
        };
      }

      const data = await response.json();
      const reservation = data.result || {};
      const financeFieldArray = data.result?.financeField || [];

      let CheckOutDamageDeposit = 0;

      // ✅ Find "Damage Fee" entry
      const damageDepositEntry = financeFieldArray.find((item) => item.alias === "Damage Fee");
      if (damageDepositEntry) {
        if (damageDepositEntry.isDeleted === 1) {
          CheckOutDamageDeposit = 0;
        } else {
          CheckOutDamageDeposit = damageDepositEntry.total ?? damageDepositEntry.value ?? 0;
        }
      }

      // ✅ Compute standard finance fields
      const financeFields = {
        baseRate: reservation.baseRate || 0,
        cleaningFeeValue: reservation.cleaningFeeValue || 0,
        additionalCleaningFee: reservation.additionalCleaningFee || 0,
        midstayCleaningFee: reservation.midstayCleaningFee || 0,
        otherFees: reservation.otherFees || 0,
        salesTax: reservation.salesTax || 0,
        earlyCheckinFee: reservation.earlyCheckinFee || 0,
        bedLinenFee: reservation.bedLinenFee || 0,
        extraBedsFee: reservation.extraBedsFee || 0,
        lateCheckoutFee: reservation.lateCheckoutFee || 0,
        damageDeposit: reservation.damageDeposit || 0,
        parkingFee: reservation.parkingFee || 0,
        serviceFee: reservation.serviceFee || 0,
        towelChangeFee: reservation.towelChangeFee || 0,
        allTotalCharges:
          (reservation.baseRate || 0) +
          (reservation.cleaningFeeValue || 0) +
          (reservation.additionalCleaningFee || 0) +
          (reservation.midstayCleaningFee || 0) +
          (reservation.otherFees || 0) +
          (reservation.salesTax || 0) +
          (reservation.earlyCheckinFee || 0) +
          (reservation.bedLinenFee || 0) +
          (reservation.extraBedsFee || 0) +
          (reservation.lateCheckoutFee || 0) +
          (reservation.damageDeposit || 0) +
          (reservation.parkingFee || 0) +
          (reservation.serviceFee || 0) +
          (reservation.towelChangeFee || 0),
        channelId: reservation.channelId,
      };

      // ✅ Convert USD → PKR for specific channels
      if (financeFields.channelId === 2018 || financeFields.channelId === 2013) {
        try {
          const exchangeResponse = await fetch(
            "https://v6.exchangerate-api.com/v6/e528361fb75219dbc48899b1/latest/USD"
          );
          const exchangeData = await exchangeResponse.json();
          const usdToPkrRate = exchangeData.conversion_rates.PKR;

          const convertedFields = { ...financeFields };
          Object.keys(convertedFields).forEach((key) => {
            if (typeof convertedFields[key] === "number") {
              convertedFields[key] = Number((convertedFields[key] * usdToPkrRate).toFixed(2));
            }
          });

          // FORCE PKR LABEL IN BREAKDOWN
          const currencyLabel = "PKR"; // This overrides any incoming USD

          return {
            securityDepositFee: convertedFields.otherFees || "",
            lateCheckOutCharges: convertedFields.lateCheckoutFee || "",
            allTotalCharges: convertedFields.allTotalCharges || "",
            financeFields: convertedFields,
            CheckOutDamageDeposit,
          };
        } catch (exchangeError) {
          console.error("Error fetching exchange rate:", exchangeError);
        }
      }

      // ✅ Default return (no conversion)
      return {
        securityDepositFee: financeFields.otherFees || "",
        lateCheckOutCharges: financeFields.lateCheckoutFee || "",
        allTotalCharges: financeFields.allTotalCharges || "",
        financeFields,
        CheckOutDamageDeposit,
      };
    } catch (error) {
      console.error("Error fetching finance fields:", error);
      return {
        securityDepositFee: "",
        lateCheckOutCharges: "",
        allTotalCharges: "",
        financeFields: {},
        CheckOutSecurityDeposit: 0,
        CheckOutDamageDeposit: 0,
      };
    }
  }

  const handleClose = () => {
    setOpen(false);
    if (setOpenPreviewReservationId) setOpenPreviewReservationId(null);
  };

  const refreshCard = () => {
    // your actual card refresh logic (API call, reload, etc.)
    window.location.reload();
  };

  // inside your FdoPanel component
  const handleWebhook = async (reservationId) => {
    // Prevent view_only users and custom users without complete access from triggering webhook
    if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
      console.log("⚠️ Sync action blocked: Insufficient permissions");
      setSnackbar({
        open: true,
        message: "You don't have permission to sync reservations",
        severity: "error",
      });
      return;
    }

    const today = new Date().toISOString().split("T")[0];
    setIsSyncingCard(true);

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(
        `/api/fdo/reservations/${guest.reservationId}/sync`,
        {
          method: "POST",
          headers: headers
        }
      );

      if (response.ok) {
        console.log(`✅ Webhook triggered for reservation ${reservationId}!`);

        // Since the backend syncs synchronously now, we can refresh the UI immediately
        if (onRefresh) onRefresh();
      } else {
        const errorData = await response.json();
        console.error("❌ Sync failed:", errorData);
        setSnackbar({
          open: true,
          message: `Sync failed: ${errorData.error || "Please try again."}`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("⚠️ Error sending webhook:", error);
      setSnackbar({
        open: true,
        message: "Network error during sync.",
        severity: "error",
      });
    } finally {
      setIsSyncingCard(false);
    }
  };

  const fetchPrintButtonStatus = async (reservationId) => {
    if (!reservationId) {
      setCanPrintCheckIn(false);
      setCanPrintCheckOut(false);
      return;
    }

    // Rely entirely on local DB for print status, remove Hostaway fallback
    const localCheckIn = localStorage.getItem(`printed_checkin_${reservationId}`) === "true";
    const localCheckOut = localStorage.getItem(`printed_checkout_${reservationId}`) === "true";

    // If it's explicitly marked as printed in the DB (true), then disable printing.
    // Otherwise (false or missing), enable printing (unless overridden by local storage).
    const isPrintedCheckIn = guest.printedCheckIn === true || guest.printedCheckIn === "true";
    const isPrintedCheckOut = guest.printedCheckOut === true || guest.printedCheckOut === "true";

    setCanPrintCheckIn(localCheckIn ? false : !isPrintedCheckIn);
    setCanPrintCheckOut(localCheckOut ? false : !isPrintedCheckOut);
  };

  const setLocalBookingDate = () => {
    if (guest?.booking_date) {
      setBookingDate(guest.booking_date);
    } else {
      setBookingDate(null);
    }
  };

  const [cardCleaningStatus, setCardCleaningStatus] = useState(null);

  // Fetch live cleaning status for this reservation card for checkout and unknown stacks
  useEffect(() => {
    if (!guest?.listingName) return;
    const isRelevantStack = stack === "Same Day Checked Out" || stack === "Early Checked Out" || stack === "Checked Out" || stack === "Unknown" || stack === "Unknown Stack";
    if (!isRelevantStack) return;

    const resIdStr = guest.reservationId ? String(guest.reservationId) : "";
    const taskName = resIdStr ? `${guest.listingName} (${resIdStr})` : guest.listingName;

    const fetchCleaningRecord = async () => {
      try {
        let res = await fetch(`/api/cleaning/get-record/${encodeURIComponent(taskName)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        let data = await res.json();

        // Fallback to base listing name search if specific reservation ID record not found
        if (!data.success || !data.data) {
          res = await fetch(`/api/cleaning/get-record/${encodeURIComponent(guest.listingName)}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });
          data = await res.json();
        }

        if (data.success && data.data) {
          let col = "To do";
          const isRecordClean = data.data["Clean"] === "Clean" ||
            data.data["Cleaning Status"] === "Clean" ||
            data.data["Cleaning Status"] === "Cleaned" ||
            data.data["Cleaning Status"] === "Cleaned ✅";
          if (isRecordClean) {
            col = "Clean";
          } else if (data.data["Hold"] === "Hold") {
            col = "Hold";
          } else if (data.data["In Progress"] === "In progress") {
            col = "In progress";
          } else if (data.data["TO DO"] === "To do") {
            col = "To do";
          }

          localStorage.setItem(`cleaning_kanban_column_${taskName}`, col);
          localStorage.setItem(`cleaning_kanban_column_${guest.listingName}`, col);
          setCardCleaningStatus(col);
        }
      } catch (err) { }
    };

    fetchCleaningRecord();
  }, [guest?.listingName, guest?.reservationId]);

  const [upsellDetails, setUpsellDetails] = useState([]);
  const [loadingUpsellDetails, setLoadingUpsellDetails] = useState(false);

  const fetchUpsellDetails = async () => {
    const targetResId = guest?.reservationId || guest?.id;
    if (!targetResId) return;

    const cleanResId = String(targetResId).replace(/[^0-9]/g, "");

    const mapRecord = (r) => {
      const f = r.fields || {};
      const cat = r.category || f["Upsells Category "] || f["Upsells Category"] || f["Category"] || "Service fee";
      const name = r.upsellName || f["Upsells Name "] || f["Upsells Name"] || f["Upsell Name "] || f["Upsell Name"] || f["Name"] || "Upsell Fee";
      const cur = r.currency || f["Currency"] || f["currency"] || "PKR";
      const q = Number(r.quantity ?? f["Quantity"] ?? f["Qty"] ?? f["qty"] ?? 1);
      const pr = Number(
        r.totalPrice ??
        r.price ??
        f["Total Price"] ??
        f["Total price"] ??
        f["Total amount"] ??
        f["Total Amount"] ??
        f["Price "] ??
        f["Price"] ??
        f["Amount"] ??
        f["Rate"] ??
        0
      );
      const ppu = Number(
        r.pricePerUnit ??
        f["Price Per Unit "] ??
        f["Price Per Unit"] ??
        f["Price per unit"] ??
        f["pricePerUnit"] ??
        (q > 0 ? parseFloat((pr / q).toFixed(2)) : pr)
      );

      return {
        id: r.id,
        reservationId: r.reservationId || f["Reservation ID "] || f["Reservation ID"] || cleanResId,
        guestName: r.guestName || f["Guest Name "] || f["Guest Name"] || "Guest",
        category: cat,
        upsellName: name,
        dateTime: r.dateTime || f["Date and Time"] || r.createdTime || new Date().toISOString(),
        currency: cur,
        quantity: q,
        price: pr,
        totalPrice: pr,
        pricePerUnit: ppu,
        addedBy: r.addedBy || f["Added By"] || f["Added by"] || f["Created By"] || f["User"] || "-",
      };
    };

    // ⚡ 1. INSTANT CACHE LOOKUP (< 1ms)
    try {
      const cachedStr = sessionStorage.getItem("cached_db_upsell_records");
      if (cachedStr) {
        const cached = JSON.parse(cachedStr);
        const cachedMatching = cached.filter((r) => {
          const rResId = String(r.reservationId || r.fields?.["Reservation ID "] || r.fields?.["Reservation ID"] || "").replace(/[^0-9]/g, "");
          return rResId && rResId === cleanResId;
        }).map(mapRecord);

        if (cachedMatching.length > 0 && cachedMatching.some((x) => x.price > 0)) {
          setUpsellDetails(cachedMatching);
          setLoadingUpsellDetails(false);
          return;
        }
      }
    } catch (e) {}

    // ⚡ 2. Single Unified API Call (Cached & Shared across all FDO panel cards)
    try {
      setLoadingUpsellDetails(true);
      const records = await (async () => {
        if (window.__cachedFdoUpsellRecords && window.__cachedFdoUpsellRecords.length > 0) {
          return window.__cachedFdoUpsellRecords;
        }
        if (window.__fdoUpsellPromise) {
          return window.__fdoUpsellPromise;
        }
        window.__fdoUpsellPromise = (async () => {
          try {
            const backendUrl = `${API_BASE_URL || ""}/api/upsells/teable-records`;
            const res = await fetch(backendUrl);
            if (res.ok) {
              const data = await res.json();
              if (data.records && Array.isArray(data.records)) {
                window.__cachedFdoUpsellRecords = data.records;
                try { sessionStorage.setItem("cached_db_upsell_records", JSON.stringify(data.records)); } catch (e) {}
                return data.records;
              }
            }
          } catch (bErr) {}

          try {
            const upsellUrl = process.env.REACT_APP_TEABLE_TABLE_URL_UPSELLS || "https://teable.namuve.com/api/table/tblVlCzoQlP35ui0MZH/record";
            const upsellToken = process.env.REACT_APP_TEABLE_API_TOKEN_UPSELLS || process.env.REACT_APP_TEABLE_BEARER_TOKEN || process.env.REACT_APP_TEABLE_API_TOKEN || "";
            const teableAuth = upsellToken.startsWith("Bearer ") ? upsellToken : `Bearer ${upsellToken}`;
            const res = await fetch(`${upsellUrl.split("?")[0]}?take=1000`, {
              headers: { "Authorization": teableAuth, "Content-Type": "application/json" },
            });
            if (res.ok) {
              const data = await res.json();
              const records = data.records || data.data || [];
              window.__cachedFdoUpsellRecords = records;
              try { sessionStorage.setItem("cached_db_upsell_records", JSON.stringify(records)); } catch (e) {}
              return records;
            }
          } catch (tErr) {}

          return [];
        })().finally(() => {
          setTimeout(() => { window.__fdoUpsellPromise = null; }, 15000);
        });

        return window.__fdoUpsellPromise;
      })();

      if (records && records.length > 0) {
        const matching = records.filter((r) => {
          const rResId = String(r.reservationId || r.fields?.["Reservation ID "] || r.fields?.["Reservation ID"] || "").replace(/[^0-9]/g, "");
          return rResId && rResId === cleanResId;
        }).map(mapRecord);
        setUpsellDetails(matching);
      }
    } catch (err) {
      console.warn("Notice fetching guest upsells:", err.message);
    } finally {
      setLoadingUpsellDetails(false);
    }
  };

  // Fetch when component mounts or guest changes
  useEffect(() => {
    if (guest?.reservationId) {
      fetchPrintButtonStatus(guest.reservationId);
      // Call the local function directly
      setLocalBookingDate();
      fetchUpsellDetails();
    }
  }, [guest]);

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <MDBox p={2}>
        {/* Reservation ID at top */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ gap: 0.5 }}>
          <MDBox display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="4" y1="9" x2="20" y2="9" />
              <line x1="4" y1="15" x2="20" y2="15" />
              <line x1="10" y1="3" x2="8" y2="21" />
              <line x1="16" y1="3" x2="14" y2="21" />
            </svg>
            <MDTypography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem", margin: 1 }}>
              Reservation ID
            </MDTypography>
          </MDBox>
          <MDTypography variant="body2" sx={{ fontSize: "0.85rem", margin: 0 }}>
            {guest.reservationId ? (
              <MDTypography
                component="span"
                color="dark"
                sx={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  "&:hover": { textDecoration: "underline" },
                  fontSize: "0.85rem",
                }}
                onClick={() =>
                  window.open(
                    `https://dashboard.hostaway.com/reservations/${guest.reservationId}`,
                    "_blank"
                  )
                }
              >
                {guest.reservationId}
                <OpenInNewIcon sx={{ ml: 0.5, fontSize: "14px" }} />
              </MDTypography>
            ) : (
              "N/A"
            )}
          </MDTypography>
        </MDBox>
        {/* Guest Info */}
        <MDBox display="flex" alignItems="center" mt={1} mb={1} justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <PersonIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: "0.85rem" }}>
              {guest.guestName || "N/A"}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center">
            <ApartmentIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="body2" fontWeight="regular" sx={{ fontSize: "0.85rem" }}>
              {guest.listingName || "N/A"}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center" mt={1} mb={1}>
          <EventIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
          <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
            Booking Date: {bookingDate ? dayjs(bookingDate).format("D MMM, YYYY") : "Not provided"}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" alignItems="center" mt={1} mb={1}>
          <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
          <MDTypography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {guest.arrivalDate && guest.departureDate ? (
              <>
                {`${dayjs(guest.arrivalDate).format("D MMM")} – ${dayjs(guest.departureDate).format(
                  "D MMM, YYYY"
                )}`}
              </>
            ) : (
              "N/A"
            )}
          </MDTypography>
        </MDBox>
        {/* Apartment Status 
        <MDBox display="flex" alignItems="center" mb={0}>
          <ApartmentIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
          <MDTypography variant="body2" color="text">
            {guest.aptStatus || "N/A"}
          </MDTypography>
        </MDBox>
        */}
        {/* Stay Duration 
        <MDBox mt={0} mb={0}>
          <MDTypography variant="body2">
            {guest.stayDuration || "N/A"}
          </MDTypography>
        </MDBox>
        */}
        {/* Check-in / Check-out */}
        <MDBox mt={0}>
          {guest.actualCheckin && guest.actualCheckin !== "N/A" && (
            <MDBox display="flex" alignItems="center" mb={0.5}>
              <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: "#17621B" }} />
              <MDTypography
                variant="body2"
                sx={{ fontSize: "0.85rem", color: "#17621B", fontWeight: 500 }}
              >
                Check-In: {guest.actualCheckin.includes("T") ? dayjs(guest.actualCheckin).format("D MMM, YYYY - h:mm A") : guest.actualCheckin}
              </MDTypography>
            </MDBox>
          )}

          {guest.actualCheckout && guest.actualCheckout !== "N/A" && (
            <MDBox display="flex" alignItems="center">
              <ExitToAppIcon fontSize="small" sx={{ mr: 1, color: "#951718" }} />
              <MDTypography
                variant="body2"
                sx={{ fontSize: "0.85rem", color: "#951718", fontWeight: 500 }}
              >
                Check-Out: {guest.actualCheckout.includes("T") ? dayjs(guest.actualCheckout).format("D MMM, YYYY - h:mm A") : guest.actualCheckout}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
        {/* Tags + Comment Icon */}
        <MDBox display="flex" mt={1}>
          {/* Tags on the left */}
          <MDBox display="flex" flexWrap="wrap">
            <Chip
              label={hotelEyeEntry === "done" ? "✅ Hotel Eye: Done" : "⏳ Hotel Eye: Pending"}
              size="small"
              sx={{
                mr: 0.5,
                mb: 0.5,
                bgcolor: hotelEyeEntry === "done" ? "#1e7a20" : "#ffb300",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.71rem",
              }}
            />
            {(() => {
              const resIdStr = guest.reservationId ? String(guest.reservationId) : "";
              const taskName = resIdStr ? `${guest.listingName} (${resIdStr})` : (guest.listingName || "");
              const cleanTaskName = guest.listingName || "";

              const localTaskCol = localStorage.getItem(`cleaning_kanban_column_${taskName}`);
              const localCleanCol = localStorage.getItem(`cleaning_kanban_column_${cleanTaskName}`);

              // Helper to check if any localStorage key for this base room is Cleaned
              const checkStorageForRoom = (listingName) => {
                if (!listingName) return null;
                const baseClean = listingName.replace(/\s+\([^\)]+\)$/, "").toLowerCase().trim();
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key && key.startsWith("cleaning_kanban_column_")) {
                    const rawName = key.replace("cleaning_kanban_column_", "");
                    const rawBase = rawName.replace(/\s+\([^\)]+\)$/, "").toLowerCase().trim();
                    if (rawBase === baseClean) {
                      const val = localStorage.getItem(key);
                      if (val === "Clean" || val === "Cleaned" || val === "Cleaned ✅") {
                        return "Clean";
                      }
                    }
                  }
                }
                return null;
              };

              const storageRoomStatus = checkStorageForRoom(cleanTaskName);

              // Only mark Clean if explicit cleaning status is Clean / Cleaned
              const isClean = [cardCleaningStatus, localTaskCol, localCleanCol, storageRoomStatus, guest.cleaningStatus, guest["Cleaning Status"], guest["Clean"]]
                .some(s => s && (s === "Clean" || s === "Cleaned" || s === "Cleaned ✅"));

              const isCheckoutStack = stack === "Same Day Checked Out" || stack === "Early Checked Out" || stack === "Checked Out" || stack === "Unknown" || stack === "Unknown Stack";

              let resolvedStatus = "To do";
              if (isClean || !isCheckoutStack) {
                resolvedStatus = "Clean";
              } else {
                const rawStatus = cardCleaningStatus || localTaskCol || localCleanCol || guest.cleaningStatus || guest["Cleaning Status"] || "To do";
                if (rawStatus === "In progress" || rawStatus === "In progress 🟢") {
                  resolvedStatus = "In progress";
                } else if (rawStatus === "Hold" || rawStatus === "Hold ⏸") {
                  resolvedStatus = "Hold";
                }
              }

              const statusConfig = {
                "Clean": { label: "Cleaned ✅", color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
                "To do": { label: "To do 🔴", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
                "In progress": { label: "In progress 🟢", color: "#2563eb", bg: "#eff6ff", border: "#93c5fd" },
                "Hold": { label: "Hold ⏸", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
              }[resolvedStatus] || { label: "To do 🔴", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" };

              return (
                <Chip
                  label={statusConfig.label}
                  size="small"
                  sx={{
                    mr: 0.5,
                    mb: 0.5,
                    bgcolor: statusConfig.bg,
                    color: statusConfig.color,
                    border: `1.5px solid ${statusConfig.border}`,
                    fontWeight: "bold",
                    fontSize: "0.71rem",
                  }}
                />
              );
            })()}
            {guest.tags?.length > 0 && guest.tags
              .filter((tag) => tag !== "Not Cleaned ❌" && tag !== "Cleaned ✅")
              .map((tag, index) => {
                const tagColors = {
                  Urgent: "#007bff",
                  Normal: "#ffec99",
                  Paid: "#28a745",
                  "Partially paid": "#a3cca3",
                  Due: "#ae0814",
                  Unknown: "#404040",
                  "Not Cleaned ❌": "#c4c4c4",
                  "Cleaned ✅": "#a3cca3",
                  Unpaid: "#ccaa2f",
                };

                let bgColor = tagColors[tag] || "#808080";
                if (!tagColors[tag]) {
                  const lowerTag = tag.toLowerCase();
                  if (lowerTag.startsWith("due")) bgColor = tagColors["Due"];
                  else if (lowerTag.startsWith("unpaid")) bgColor = tagColors["Unpaid"];
                  else if (lowerTag.startsWith("partially paid")) bgColor = tagColors["Partially paid"];
                }

                return (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    sx={{
                      mr: 0.5,
                      mb: 0.5,
                      bgcolor: bgColor,
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "0.71rem",
                    }}
                  />
                );
              })}
          </MDBox>
        </MDBox>
        {/* === INPUT + CONDITIONAL COMMENT ICON === */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1} gap={1}>
          {/* INPUT WITH POST INSIDE */}
          <MDBox flex={1} bgcolor="#f8f9fa" borderRadius="16px" sx={{ position: "relative" }}>
            <MDInput
              fullWidth
              multiline
              rows={1}
              placeholder="Write comment here"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey && newComment.trim()) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading}
              InputProps={{
                endAdornment: newComment.trim() && (
                  <MDTypography
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!loading) handleSend();
                    }}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      color: "#5e6b47",
                      textDecoration: "underline",
                      cursor: loading ? "not-allowed" : "pointer",
                      userSelect: "none",
                      opacity: loading ? 0.6 : 1,
                      mr: 1,
                      alignSelf: "flex-end",
                      pb: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loading ? "..." : "Post"}
                  </MDTypography>
                ),
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  color: "#495057",
                  pr: newComment.trim() ? 9 : 2,
                  py: 0.75, // ← Less height
                  lineHeight: 1.4,
                },
                "& .MuiInputBase-root": {
                  borderRadius: "20px",
                  bgcolor: "transparent",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#d1d5db", // Default gray
                  },
                  "&:hover fieldset": {
                    borderColor: "#5e6b47", // Hover green
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#5e6b47", // Focus green
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.75rem", // ← SMALLER
                  color: "#9ca3af", // ← LIGHTER GRAY
                  opacity: 1,
                },
              }}
            />
          </MDBox>

          {/* COMMENT ICON — ONLY SHOWS IF commentCount > 0 */}
          {commentCount > 0 && (
            <MDBox>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleOpen}
                sx={{ p: 0.5 }}
              >
                <Badge badgeContent={commentCount} color="error">
                  <CommentIcon fontSize="small" />
                </Badge>
              </IconButton>
            </MDBox>
          )}
        </MDBox>
        <Dialog
          open={showComments}
          onClose={() => {
            setOpenCommentReservationId(null);
            fetchCommentCount(guest.reservationId);
          }}
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <MDTypography variant="h6" fontWeight="bold">
              Reservation ID - {guest.reservationId}
            </MDTypography>
            <IconButton
              onClick={() => {
                setOpenCommentReservationId(null);
                fetchCommentCount(guest.reservationId);
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <CommentSection guest={guest} bookingDate={bookingDate} sseTrigger={sseTrigger} />
          </DialogContent>
        </Dialog>
        <Divider sx={{ my: 1, borderWidth: "2px" }} /> {/* my: 1 adds margin top and bottom */}
        {/* Preview Button */}
        <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {/* 👁️ View Details */}
            <IconButton
              onClick={handleOpen}
              sx={{
                color: "#28282B",
                border: "1.5px solid #28282B",
                borderRadius: "12px",
                padding: "4px 6px",
                fontSize: "0.85rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transition: "all 0.25s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                  backgroundColor: "rgba(0,0,0,0.06)",
                },
              }}
            >
              <VisibilityIcon sx={{ fontSize: "1rem" }} />
            </IconButton>

            {/* 🔄 Webhook Trigger (card sync button) */}
            <IconButton
              onClick={() => handleWebhook(guest.reservationId)}
              disabled={
                isSyncingCard || isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))
              }
              sx={{
                color: "#28282B",
                border: "1.5px solid #28282B",
                borderRadius: "12px",
                padding: "4px 6px",
                fontWeight: "bold",
                fontSize: "0.85rem",
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                transition: "all 0.25s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
                "&:disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
              }}
            >
              <SyncIcon sx={{
                fontSize: "1rem",
                animation: isSyncingCard ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }} />
            </IconButton>

            {/* 🔔 Reminder Button (only show in Upcoming Checkout) - Commented out per request */}
            {/* 
            {stack === "Upcoming Checkout" && (
              <IconButton
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const response = await fetch(`${HOSTAWAY_API}/${guest.reservationId}`, {
                      headers: {
                        Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
                      },
                    });

                    if (!response.ok) throw new Error("Failed to fetch details");

                    const data = await response.json();
                    const result = data.result || {};
                    const phone = result.phone || result.guestPhone || guest.phone;

                    if (!phone) {
                      setSnackbar({
                        open: true,
                        message: "Phone number not found",
                        severity: "error",
                      });
                      return;
                    }

                    const cleanPhone = phone.replace(/[^0-9+]/g, "");
                    const guestName = guest.guestName || "Guest";
                    const message = `Dear ${guestName},
We hope you enjoyed your stay with us. This is a reminder that checkout time is 12:00PM. Please let us know your planned checkout time. A late checkout fee of Rs. 1000 per hour applies after 12:00PM.

Thank you for staying with Namuve, and we wish you safe travels!

Team Namuve`;

                    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
                    window.open(url, "_blank");
                  } catch (error) {
                    console.error("Error opening WhatsApp:", error);
                    setSnackbar({
                      open: true,
                      message: "Failed to open WhatsApp",
                      severity: "error",
                    });
                  }
                }}
                sx={{
                  color: "#28282B",
                  border: "1.5px solid #28282B",
                  borderRadius: "12px",
                  padding: "4px 6px",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                  transition: "all 0.25s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.05)",
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <NotificationsIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
            */}
            {/* 🖨️ Reprint Options (Show if actualCheckin exists) */}
            {guest.actualCheckin && guest.actualCheckin !== "N/A" && (
              <>
                <IconButton
                  onClick={handleReprintMenuOpen}
                  disabled={isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))}
                  sx={{
                    color: "#28282B",
                    border: "1.5px solid #28282B",
                    borderRadius: "12px",
                    padding: "4px 6px",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                    transition: "all 0.25s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                      cursor: "not-allowed",
                    },
                  }}
                >
                  <PrintIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
                <Menu
                  anchorEl={reprintMenuAnchor}
                  open={Boolean(reprintMenuAnchor)}
                  onClose={handleReprintMenuClose}
                >
                  <MenuItem onClick={() => { handlePrintCheckIn(); handleReprintMenuClose(); }}>
                    Print Check In
                  </MenuItem>
                  {guest.actualCheckout && guest.actualCheckout !== "N/A" && (
                    <MenuItem onClick={() => { handlePrintCheckOut(); handleReprintMenuClose(); }}>
                      Print Check Out
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </MDBox>

          {/* Mark Check in Button - only show in Upcoming Stay */}
          {(!showPrintCheckIn && (!guest.actualCheckin || guest.actualCheckin === "N/A")) ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleCheckInClick}
              disabled={
                isViewOnly() ||
                (isCustom() && !hasPermission("fdoPanel", "complete")) ||
                isRoomOccupiedWithCheckedInGuest ||
                isCheckingIn ||
                isFetchingCheckInDetails
              }
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "bold",
                boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                color: isCheckingIn ? "#888" : "#17621B",
                border: isCheckingIn ? "2px solid #888" : "2px solid #17621B",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: isCheckingIn ? "transparent" : "#1e7a20",
                  borderColor: isCheckingIn ? "#888" : "#145517",
                  color: isCheckingIn ? "#888" : "#fff",
                },
                "&:focus": {
                  backgroundColor: isCheckingIn ? "transparent" : "#145517",
                  color: isCheckingIn ? "#888" : "#fff",
                },
                "&:active": {
                  backgroundColor: isCheckingIn ? "transparent" : "#145517",
                  color: isCheckingIn ? "#888" : "#fff",
                },
              }}
            >
              {isCheckingIn ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />
                  Checking In...
                </>
              ) : (
                "Mark Check In"
              )}
            </Button>
          ) : (
            (canPrintCheckIn || showPrintCheckIn) && (
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrintCheckIn}
                disabled={isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "bold",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  color: "#17621B",
                  border: "2px solid #17621B",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#1e7a20",
                    borderColor: "#145517",
                    color: "#fff",
                  },
                  "&:focus": {
                    backgroundColor: "#145517",
                    color: "#fff",
                  },
                  "&:active": {
                    backgroundColor: "#145517",
                    color: "#fff",
                  },
                }}
              >
                Print Check In
              </Button>
            )
          )}

          {/* Mark Check Out Button - show in all stacks apart from Upcoming Stay */}
          {/* ✅ MARK CHECK OUT BUTTON */}
          {guest.actualCheckin &&
            guest.actualCheckin !== "N/A" &&
            (!guest.actualCheckout || guest.actualCheckout === "N/A") &&
            !canPrintCheckIn && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleCheckOutClick}
                disabled={
                  isViewOnly() ||
                  (isCustom() && !hasPermission("fdoPanel", "complete")) ||
                  isRoomOccupiedWithCheckedInGuest ||
                  isCheckingOut
                }
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "bold",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  color: isCheckingOut ? "#888" : "#951718",
                  border: isCheckingOut ? "2px solid #888" : "2px solid #951718",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isCheckingOut ? "transparent" : "#b21c1d",
                    borderColor: isCheckingOut ? "#888" : "#831415ff",
                    color: isCheckingOut ? "#888" : "#fff",
                  },
                  "&:focus": {
                    backgroundColor: isCheckingOut ? "transparent" : "#7a1213",
                    color: isCheckingOut ? "#888" : "#fff",
                  },
                  "&:active": {
                    backgroundColor: isCheckingOut ? "transparent" : "#7a1213",
                    color: isCheckingOut ? "#888" : "#fff",
                  },
                }}
              >
                {isCheckingOut ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} />
                    Checking Out...
                  </>
                ) : (
                  "Mark Check Out"
                )}
              </Button>
            )}

          {/* ✅ PRINT CHECK OUT BUTTON */}
          {guest.actualCheckout &&
            guest.actualCheckout !== "N/A" &&
            !canPrintCheckIn &&
            canPrintCheckOut && (
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrintCheckOut}
                disabled={isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "bold",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  color: "#951718",
                  border: "2px solid #951718",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#b21c1d",
                    borderColor: "#7a1213",
                    color: "#fff",
                  },
                  "&:focus": {
                    backgroundColor: "#7a1213",
                    color: "#fff",
                  },
                  "&:active": {
                    backgroundColor: "#7a1213",
                    color: "#fff",
                  },
                }}
              >
                Print Check Out
              </Button>
            )}

          {/* ✅ Checked Out Label */}
          {guest.actualCheckin &&
            guest.actualCheckin !== "N/A" &&
            guest.actualCheckout &&
            guest.actualCheckout !== "N/A" &&
            !canPrintCheckOut && (
              <Button
                variant="outlined"
                size="small"
                disabled
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "bold",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                  color: "#808080",
                  border: "2px solid #808080",
                  backgroundColor: "#f5f5f5", // ✅ light gray background for disabled look
                  opacity: 0.6, // ✅ faded to look inactive
                  cursor: "not-allowed", // ✅ cursor indicates not clickable
                }}
              >
                Guest Has Checked Out
              </Button>
            )}
        </MDBox>
      </MDBox>

      {/* Preview Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            <span style={{ fontSize: "1rem", fontWeight: 400, color: "#5e6b47" }}>
              Reservation Details Of{" "}
            </span>
            <strong style={{ color: "#5e6b47" }}>{reservationDetails?.guestName}</strong>
          </span>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: "#5e6b47",
              "&:hover": { color: "#4a5438" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress />
              <MDTypography variant="body2" sx={{ marginTop: 2, color: "#555", fontWeight: 500 }}>
                Loading...
              </MDTypography>
            </MDBox>
          ) : error ? (
            <MDTypography variant="body2" color="error">
              Error: {error}
            </MDTypography>
          ) : (
            <>
              <Row>
                {/* Left Column: Reservation Details */}
                <Col md={8}>
                  <Row>
                    {/* Left Sub-Column */}
                    <Col md={6}>
                      <Table striped bordered hover size="sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Reservation ID:</strong>
                            </td>
                            <td>{guest.reservationId}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>CNIC</strong>
                            </td>
                            <td>
                              {reservationDetails?.customFieldValues?.find(
                                (field) => field.customField?.name === "ID card Number/ Passport number"
                              )?.value || "Not provided"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Unit</strong>
                            </td>
                            <td>{guest.listingName || "N/A"}</td>
                          </tr>
                          {user?.role !== "user" && !(user?.role === "custom" && hasPermission("fdoPanel", "hideContact")) && (
                            <tr>
                              <td>
                                <strong>Contact</strong>
                              </td>
                              <td>{reservationDetails?.phone || "N/A"}</td>
                            </tr>
                          )}
                          <tr>
                            <td>
                              <strong>Total Nights</strong>
                            </td>
                            <td>{reservationDetails?.nights || "N/A"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Total Amount</strong>
                            </td>
                            <td>
                              {reservationDetails?.totalPrice != null
                                ? Number(reservationDetails.totalPrice)
                                : "N/A"}{" "}
                              {reservationDetails?.currency || ""}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Remaining / Paid / Total</strong>
                            </td>
                            <td>
                              {reservationDetails
                                ? `${reservationDetails.remainingBalance !== undefined &&
                                  reservationDetails.remainingBalance !== null
                                  ? reservationDetails.currency === "USD"
                                    ? Number(reservationDetails.remainingBalance).toFixed(2)
                                    : reservationDetails.remainingBalance
                                  : "N/A"
                                } ${reservationDetails.currency || ""} / ${reservationDetails.totalPaid !== undefined &&
                                  reservationDetails.totalPaid !== null
                                  ? reservationDetails.currency === "USD"
                                    ? Number(reservationDetails.totalPaid).toFixed(2)
                                    : reservationDetails.totalPaid
                                  : "N/A"
                                } ${reservationDetails.currency || ""} / ${reservationDetails.totalPrice !== undefined &&
                                  reservationDetails.totalPrice !== null
                                  ? reservationDetails.currency === "USD"
                                    ? Number(reservationDetails.totalPrice).toFixed(2)
                                    : reservationDetails.totalPrice
                                  : "N/A"
                                } ${reservationDetails.currency || ""}`
                                : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Early Check-in</strong>
                            </td>
                            <td>
                              {reservationDetails?.earlyCheckinCharges &&
                                !isNaN(Number(reservationDetails.earlyCheckinCharges))
                                ? Number(reservationDetails.earlyCheckinCharges)
                                : "0"}{" "}
                              {reservationDetails?.currency || ""}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Price/Night</strong>
                            </td>
                            <td>
                              {reservationDetails?.pricePerNight &&
                                !isNaN(parseFloat(reservationDetails.pricePerNight))
                                ? `${parseFloat(reservationDetails.pricePerNight)} ${reservationDetails?.currency || ""
                                }`
                                : "Not provided"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Vehicle No</strong>
                            </td>
                            <td>
                              {reservationDetails?.customFieldValues?.find(
                                (field) => field.customField?.name === "Vehicle Number"
                              )?.value ||
                                guest.vehicleNo ||
                                "Not provided"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>

                    {/* Right Sub-Column */}
                    <Col md={6}>
                      <Table striped bordered hover size="sm">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Channel ID</strong>
                            </td>
                            <td>{reservationDetails?.channelName || "N/A"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Email</strong>
                            </td>
                            <td>{reservationDetails?.guestEmail || "Not provided"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Adults</strong>
                            </td>
                            <td>{reservationDetails?.numberOfGuests || "N/A"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Children</strong>
                            </td>
                            <td>{reservationDetails?.children || "0"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Payment Status</strong>
                            </td>
                            <td
                              style={{
                                color: (() => {
                                  const statusColors = {
                                    Paid: "#28a745",
                                    "Partially paid": "#a3cca3",
                                    Due: "#ae0814",
                                    Unpaid: "#ccaa2f",
                                  };

                                  const status =
                                    reservationDetails?.paymentStatus === "Unknown"
                                      ? "Due"
                                      : reservationDetails?.paymentStatus || "N/A";

                                  return statusColors[status] || "#000";
                                })(),
                                fontWeight: "bold",
                              }}
                            >
                              {reservationDetails?.paymentStatus === "Unknown"
                                ? "Due"
                                : reservationDetails?.paymentStatus || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Check-in Date</strong>
                            </td>
                            <td>{reservationDetails?.arrivalDate || "N/A"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Check-in Time</strong>
                            </td>
                            <td>
                              {reservationDetails?.checkInTime
                                ? formatTime(reservationDetails.checkInTime)
                                : guest.checkinTime
                                  ? formatTime(guest.checkinTime)
                                  : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Check-out Date</strong>
                            </td>
                            <td>{reservationDetails?.departureDate || "N/A"}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Check-out Time</strong>
                            </td>
                            <td>
                              {reservationDetails?.checkOutTime
                                ? formatTime(reservationDetails.checkOutTime)
                                : guest.checkoutTime
                                  ? formatTime(guest.checkoutTime)
                                  : "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Security Deposit</strong>
                            </td>
                            <td>
                              {reservationDetails?.securityDeposit &&
                                !isNaN(Number(reservationDetails.securityDeposit))
                                ? Number(reservationDetails.securityDeposit)
                                : "0"}{" "}
                              {reservationDetails?.currency || ""}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                  {/* Full-width row for Address */}
                  <Row style={{ marginTop: "-16px" }}>
                    <Col md={12}>
                      <Table striped bordered hover size="sm">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                fontWeight: "600",
                                border: "1px solid #ddd",
                                width: "220px",
                                textAlign: "left",
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              Address
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px 12px",
                                wordBreak: "break-word",
                                backgroundColor: "#fff",
                              }}
                            >
                              {reservationDetails?.customFieldValues?.find(
                                (field) => field.customField?.name === "Address"
                              )?.value || "Not provided"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                  {/* Full-width row for Hotel Eye Entry */}
                  <Row style={{ marginTop: "16px" }}>
                    <Col md={12}>
                      <Table striped bordered hover size="sm">
                        <tbody>
                          <tr>
                            <td
                              style={{
                                fontWeight: "600",
                                border: "1px solid #ddd",
                                width: "220px",
                                textAlign: "left",
                                backgroundColor: "#f9fafb",
                              }}
                            >
                              Hotel Eye Entry
                            </td>
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px 12px",
                                backgroundColor: "#fff",
                              }}
                            >
                              <MDBox display="flex" alignItems="center" gap={2}>
                                <Select
                                  value={hotelEyeEntry}
                                  onChange={handleHotelEyeChange}
                                  size="small"
                                  sx={{
                                    width: "160px",
                                    borderRadius: "12px",
                                    backgroundColor: hotelEyeEntry === "done" ? "#e6f4ea" : "#fff8e1",
                                    color: hotelEyeEntry === "done" ? "#1e7a20" : "#b28900",
                                    fontWeight: "bold",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                    transition: "all 0.3s ease",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: hotelEyeEntry === "done" ? "#a3cca3" : "#ffe082",
                                      borderWidth: "1.5px",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: hotelEyeEntry === "done" ? "#28a745" : "#ffb300",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: hotelEyeEntry === "done" ? "#17621B" : "#ff8f00",
                                      borderWidth: "2px",
                                    },
                                    "& .MuiSelect-icon": {
                                      color: hotelEyeEntry === "done" ? "#1e7a20" : "#b28900",
                                    }
                                  }}
                                >
                                  <MenuItem value="pending" sx={{ fontWeight: "600", color: "#b28900" }}>
                                    ⏳ Pending
                                  </MenuItem>
                                  <MenuItem value="done" sx={{ fontWeight: "600", color: "#1e7a20" }}>
                                    ✅ Done
                                  </MenuItem>
                                </Select>
                                {hotelEyeEntryUser && hotelEyeEntryTime && (
                                  <MDTypography variant="caption" color="textSecondary" sx={{ fontSize: "0.75rem", fontStyle: "italic", whiteSpace: "nowrap" }}>
                                    Updated by <strong>{hotelEyeEntryUser}</strong> on {new Date(hotelEyeEntryTime).toLocaleString()}
                                  </MDTypography>
                                )}
                                {hotelEyeEntry === "pending" && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSendHotelEyeData}
                                    disabled={isSendingHotelEye || !guest.actualCheckin || guest.actualCheckin === "N/A"}
                                    sx={{
                                      ml: 2,
                                      bgcolor: "#5e6b47",
                                      color: "#fff",
                                      textTransform: "none",
                                      "&:hover": { bgcolor: "#4b5639" }
                                    }}
                                  >
                                    {isSendingHotelEye ? <CircularProgress size={16} sx={{ color: "inherit", mr: 1 }} /> : "Trigger Hotel Eye"}
                                  </Button>
                                )}
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleCreateOpusTicket}
                                  disabled={isCreatingOpusTicket || loadingDetails}
                                  sx={{
                                    ml: 2,
                                    bgcolor: "#8f7d3a",
                                    color: "#fff",
                                    textTransform: "none",
                                    "&:hover": { bgcolor: "#73642d" }
                                  }}
                                >
                                  {isCreatingOpusTicket ? <CircularProgress size={16} sx={{ color: "inherit", mr: 1 }} /> : "Opus Ticket"}
                                </Button>
                              </MDBox>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>

                </Col>

                {/* Right Column: Chat Section */}
                <Col md={4} style={{ borderLeft: "1px solid #e0e0e0", paddingLeft: "16px" }}>
                  <CommentSection guest={guest} bookingDate={bookingDate} chatMode={true} sseTrigger={sseTrigger} />
                </Col>
              </Row>

              {/* Upsells Details Section (Show ONLY if there are upsells for this reservation) */}
              {upsellDetails.length > 0 && (
                <MDBox mt={4} pt={2} borderTop="1px solid #e0e0e0">
                  <MDTypography variant="h6" sx={{ color: "#5e6b47", mb: 2 }}>
                    Upsells Details
                  </MDTypography>
                  <Table responsive bordered hover size="sm" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th>Upsells Category</th>
                        <th>Upsells Name</th>
                        <th style={{ textAlign: "right" }}>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upsellDetails.map((up, index) => (
                        <tr key={up.id || index}>
                          <td>{up.category}</td>
                          <td>{up.upsellName}</td>
                          <td style={{ textAlign: "right" }}>
                            {up.currency === "PKR" ? `${Number(up.price).toLocaleString()} PKR` : `$${Number(up.price).toFixed(2)} USD`}
                          </td>
                        </tr>
                      ))}
                      {(() => {
                        const totalUpsellAmount = upsellDetails.reduce((sum, up) => sum + (Number(up.price) || 0), 0);
                        const curr = upsellDetails[0]?.currency || "PKR";
                        return (
                          <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                            <td colSpan={2} style={{ textAlign: 'right', paddingRight: '15px' }}>
                              Total Amount
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {curr === "PKR" ? `${totalUpsellAmount.toLocaleString()} PKR` : `$${totalUpsellAmount.toFixed(2)} USD`}
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </Table>
                </MDBox>
              )}

              {/* Payment Details Section */}
              {paymentDetails.length > 0 && (
                <MDBox mt={4} pt={2} borderTop="1px solid #e0e0e0">
                  <MDTypography variant="h6" sx={{ color: "#5e6b47", mb: 2 }}>
                    Payment Details
                  </MDTypography>
                  <Table responsive bordered hover size="sm" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th>CHARGE ID</th>
                        <th>REFUND ID</th>
                        <th>Transaction Type</th>
                        <th>Description</th>
                        <th>Charge Date &amp; Time</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentDetails.map((record, index) => {
                        const fields = record.fields || record.raw || record || {};
                        const currency = fields.Currency || fields.currency || "PKR";
                        const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ''));
                        const displayAmt = isNaN(amtVal) || amtVal === 0 ? "-" : `${amtVal.toLocaleString()} ${currency}`;
                        const originalChargeId = getOriginalChargeId(record, paymentDetails);
                        const typeStr = String(fields.Type || fields.type || "").toLowerCase().trim();
                        const statusVal = String(fields.Status || fields.status || "").toLowerCase().trim();
                        const isRefund = typeStr === 'refund' || statusVal === 'refunded' || (!isNaN(amtVal) && amtVal < 0);

                        let refundIdVal = "-";
                        let isSixDigitRefund = false;
                        let isMoreThanSixRefund = false;
                        if (isRefund) {
                          // 0. Use backend-provided refundIdMap (most reliable)
                          let pRefId = null;
                          const recIdNum = Number(fields.id || record.id || fields.ID || record.ID || 0);
                          const recIdStr = String(fields.id || record.id || fields.ID || record.ID || "").trim();
                          if (recIdNum && fdoRefundIdMap[recIdNum]) {
                            pRefId = String(fdoRefundIdMap[recIdNum]);
                          } else if (recIdStr && fdoRefundIdMap[recIdStr]) {
                            pRefId = String(fdoRefundIdMap[recIdStr]);
                          }

                          // 1. Direct "Refund Id" (from Teable or Hostaway enriched rows)
                          if (!pRefId) {
                            const rawRef =
                              fields["Refund Id"] ||
                              fields["Refund ID"] ||
                              fields["refundId"] ||
                              fields["refund_id"] ||
                              fields["Refund id"] ||
                              fields["RefundId"] ||
                              record["Refund Id"] ||
                              record["Refund ID"] ||
                              record.refundId ||
                              record.refund_id ||
                              fields.paymentProviderRefundId ||
                              record.paymentProviderRefundId ||
                              fields.paymentProviderChargeId ||
                              record.paymentProviderChargeId ||
                              fields.refundInfo?.id ||
                              record.refundInfo?.id ||
                              fields.raw?.refundInfo?.id ||
                              record.raw?.refundInfo?.id ||
                              fields.raw?.paymentProviderChargeId ||
                              record.raw?.paymentProviderChargeId ||
                              fields.raw?.paymentProviderRefundId ||
                              record.raw?.paymentProviderRefundId ||
                              fields["Payment Provider Charge ID"] ||
                              fields["Payment Provider Refund ID"] ||
                              fields["paymentProviderChargeId"] ||
                              fields["paymentProviderRefundId"];
                            if (rawRef && String(rawRef).trim() !== "" && String(rawRef).trim() !== "-" && String(rawRef).trim() !== "null") {
                              const cleanRef = String(rawRef).trim();
                              pRefId = fdoRefundIdMap[cleanRef] || fdoRefundIdMap[Number(cleanRef)] || cleanRef;
                            }
                          }

                          if (!pRefId) {
                            const msg = fields.paymentProviderMessage || record.paymentProviderMessage || fields.raw?.paymentProviderMessage || "";
                            const match = String(msg).match(/Payment Provider charge ID:\s*([0-9]+)/i);
                            if (match && match[1]) {
                              pRefId = match[1];
                            }
                          }

                          if (!pRefId) {
                            for (const p of paymentDetails) {
                              const pf = p.fields || p.raw || p || {};
                              const rIds = pf.refundIds || p.refundIds || (p.raw && p.raw.refundIds);
                              const ppRIds = pf.paymentProviderRefundIds || p.paymentProviderRefundIds || (p.raw && p.raw.paymentProviderRefundIds);
                              if (Array.isArray(rIds) && Array.isArray(ppRIds)) {
                                const idx = rIds.indexOf(recIdNum);
                                if (idx !== -1 && ppRIds[idx]) {
                                  pRefId = ppRIds[idx];
                                  break;
                                }
                              }
                            }
                          }

                          if (!pRefId) {
                            const parentCharge = paymentDetails.find((p) => {
                              const pf = p.fields || p.raw || p || {};
                              const pid = String(pf.id || p.id || "").trim();
                              return pid === String(originalChargeId);
                            });
                            if (parentCharge) {
                              const pf = parentCharge.fields || parentCharge.raw || parentCharge || {};
                              const ppRIds = pf.paymentProviderRefundIds || parentCharge.paymentProviderRefundIds || (parentCharge.raw && parentCharge.raw.paymentProviderRefundIds);
                              const rIds = pf.refundIds || parentCharge.refundIds || (parentCharge.raw && parentCharge.raw.refundIds);
                              if (Array.isArray(ppRIds) && ppRIds.length > 0) {
                                if (Array.isArray(rIds)) {
                                  const rIndex = rIds.indexOf(recIdNum);
                                  if (rIndex !== -1 && ppRIds[rIndex]) {
                                    pRefId = ppRIds[rIndex];
                                  }
                                }
                              }
                            }
                          }

                          if (pRefId && String(pRefId).trim() !== "" && String(pRefId) !== "0" && String(pRefId) !== "-") {
                            const cleanCandidate = String(pRefId).trim();
                            if (/^\d{6}$/.test(cleanCandidate)) {
                              refundIdVal = cleanCandidate;
                              isSixDigitRefund = true;
                            } else if (cleanCandidate.length > 6) {
                              const mapped = fdoRefundIdMap[cleanCandidate] || fdoRefundIdMap[Number(cleanCandidate)];
                              if (mapped && /^\d{6}$/.test(String(mapped).trim())) {
                                refundIdVal = String(mapped).trim();
                                isSixDigitRefund = true;
                              } else {
                                refundIdVal = cleanCandidate;
                                isMoreThanSixRefund = true;
                              }
                            } else {
                              refundIdVal = cleanCandidate;
                            }
                          }
                        }

                        return (
                          <tr key={index}>
                            <td>{originalChargeId}</td>
                            <td>
                              {isMoreThanSixRefund ? (
                                <span
                                  title={`Error: #${refundIdVal} has more than 6 digits! Expected 6-digit provider ID. Click to re-verify from Hostaway.`}
                                  onClick={() => fetchPaymentDetails()}
                                  style={{
                                    display: 'inline-block',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    backgroundColor: '#fee2e2',
                                    color: '#b91c1c',
                                    border: '1px solid #fca5a5',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Error: #{refundIdVal} (&gt;6 digits)
                                </span>
                              ) : (
                                <span style={{
                                  color: isSixDigitRefund ? '#059669' : refundIdVal !== '-' ? '#c2410c' : '#64748b',
                                  fontWeight: isSixDigitRefund ? 600 : refundIdVal !== '-' ? '600' : 'normal',
                                  fontFamily: isSixDigitRefund ? 'monospace' : 'inherit'
                                }}>
                                  {refundIdVal}
                                </span>
                              )}
                            </td>
                            <td>{fields.Title || fields.title || fields.Type || fields.type || "-"}</td>
                            <td>{fields.Description || fields.description || "-"}</td>
                            <td>{formatPakistanDateTime(fields["Charge Date"] || fields.chargeDate || fields.date || fields.dateTime || fields.date_time)}</td>
                            <td>{displayAmt}</td>
                            <td>
                              <Chip
                                label={(() => {
                                  let s = fields.Status || fields.status || "-";
                                  if (typeof s === 'object' && s !== null) s = s.name || s.value || String(s);
                                  let labelStr = String(s);
                                  const tStr = String(fields.Type || fields.type || "").toLowerCase().trim();
                                  if (tStr === 'refund' && ['succeeded', 'successded'].includes(labelStr.toLowerCase())) {
                                    labelStr = 'Refunded';
                                  }
                                  return labelStr;
                                })()}
                                size="small"
                                color={(() => {
                                  let s = fields.Status || fields.status || "";
                                  if (typeof s === 'object' && s !== null) s = s.name || s.value || String(s);
                                  const statusStr = String(s).toLowerCase().trim();
                                  const tStr = String(fields.Type || fields.type || "").toLowerCase().trim();
                                  if (tStr === 'refund') return 'error';
                                  return ['paid', 'successded', 'succeeded', 'refunded'].includes(statusStr) ? 'success' : 'default';
                                })()}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {(() => {
                        const totalAmount = paymentDetails.reduce((sum, record) => {
                          const fields = record.fields || record.raw || record || {};
                          const amtVal = Number(String(fields.Amount || fields.amount || "0").replace(/[^0-9.-]/g, ''));
                          return sum + (isNaN(amtVal) ? 0 : amtVal);
                        }, 0);
                        return (
                          <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                            <td colSpan={5} style={{ textAlign: 'right' }}>Total Amount</td>
                            <td>{totalAmount.toLocaleString()}</td>
                            <td></td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </Table>
                </MDBox>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            style={{ backgroundColor: "#5e6b47", color: "#fff" }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>



      {/* Confirmation Dialog for Double Reservation */}
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: 3, padding: 1, minWidth: { xs: "90%", sm: "400px" } }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            {hasDoubleReservation ? "Double Reservation Detected" : "Check-In Confirmation"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          {isFetchingCheckInDetails && actionType === "Check-In" ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
              <CircularProgress color="primary" size={40} />
              <MDTypography variant="caption" color="text" sx={{ mt: 2 }}>
                Validating Hotel Eye Details...
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
              {actionType === "Check-In" && checkInMissingFields.length > 0 ? (
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <MDTypography variant="body2" color="warning" fontWeight="bold" sx={{ mb: 1 }}>
                      Hotel Eye details are missing!
                    </MDTypography>
                    <MDTypography variant="caption" color="warning">
                      Please provide the missing details below to proceed with the Check-In.
                    </MDTypography>
                  </Alert>

                  <MDBox display="flex" flexWrap="wrap" gap={2} mt={1}>
                    {checkInMissingFields.filter(f => f !== "Reservation ID" && f !== "Room#").map((field) => {
                      if (field === "Gender" || field === "Province") {
                        return (
                          <MDBox key={field} width={{ xs: "100%", sm: "48%" }}>
                            <MDInput
                              select
                              label={field}
                              fullWidth
                              value={hotelEyeInputData[field] || ""}
                              onChange={(e) => setHotelEyeInputData({ ...hotelEyeInputData, [field]: e.target.value })}
                              SelectProps={{
                                displayEmpty: true,
                                renderValue: (val) => val === "" ? `Select ${field}` : val,
                                sx: { height: 44, paddingY: 1.5 }
                              }}
                              InputLabelProps={{ shrink: true }}
                            >
                              <MenuItem value="" disabled>Select {field}</MenuItem>
                              {(field === "Gender" ? GENDER_OPTIONS : PROVINCE_OPTIONS).map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </MDInput>
                          </MDBox>
                        );
                      }

                      const isDate = field.includes("Date");
                      const isTime = field.includes("Time");
                      const isNumber = field === "Total# of Accompaning Guest" || field === "Children";

                      return (
                        <MDBox key={field} width={{ xs: "100%", sm: "48%" }}>
                          <MDInput
                            label={field}
                            type={isDate ? "date" : isTime ? "time" : isNumber ? "number" : "text"}
                            InputLabelProps={(isDate || isTime) ? { shrink: true } : {}}
                            fullWidth
                            value={hotelEyeInputData[field] || ""}
                            onChange={(e) => setHotelEyeInputData({ ...hotelEyeInputData, [field]: e.target.value })}
                          />
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </MDBox>
              ) : (
                <Alert severity={hasDoubleReservation ? "warning" : "info"} sx={{ borderRadius: 2 }}>
                  <MDTypography variant="body2" color="text">
                    {hasDoubleReservation
                      ? `This room has a double reservation. Are you sure you want to proceed with ${actionType}?`
                      : `Are you sure you want to proceed with ${actionType}?`}
                  </MDTypography>
                </Alert>
              )}

              <Card sx={{ mt: 1, p: 2, bgcolor: "#f8f9fa", boxShadow: "none", border: "1px solid #e0e0e0" }}>
                <MDBox display="flex" alignItems="center" mb={1.5}>
                  <PersonIcon color="action" sx={{ mr: 1.5 }} />
                  <MDTypography variant="body2" fontWeight="medium">
                    {guest.guestName || "N/A"}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 1 }} />
                <MDBox display="flex" alignItems="center" mb={1.5}>
                  <ApartmentIcon color="action" sx={{ mr: 1.5 }} />
                  <MDTypography variant="body2">
                    Room: <strong>{guest.listingName || "N/A"}</strong>
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 1 }} />
                <MDBox display="flex" alignItems="center">
                  <EventIcon color="action" sx={{ mr: 1.5 }} />
                  <MDTypography variant="body2">
                    Reservation ID: <strong>{guest.reservationId || "N/A"}</strong>
                  </MDTypography>
                </MDBox>
              </Card>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmationOpen(false)} color="secondary" sx={{ textTransform: "none", fontWeight: "bold" }}>
            Cancel
          </Button>
          {!isFetchingCheckInDetails && (
            <Button
              onClick={() => {
                if (actionType === "Check-In" && checkInMissingFields.length > 0) {
                  handleSaveHotelEyeAndCheckIn();
                } else {
                  setConfirmationOpen(false);
                  if (actionType === "Check-In") handleMarkCheckIn();
                  if (actionType === "Check-Out") handleMarkCheckOut();
                }
              }}
              variant="contained"
              disabled={isCheckingIn || isCheckingOut || isSavingHotelEyeData}
              style={{ backgroundColor: (isCheckingIn || isCheckingOut || isSavingHotelEyeData) ? "#ccc" : "#5e6b47", color: "#fff", textTransform: "none", fontWeight: "bold", marginLeft: "16px" }}
              autoFocus
            >
              {isSavingHotelEyeData ? <CircularProgress size={20} sx={{ color: "white", mr: 1 }} /> : null}
              {actionType === "Check-In" && checkInMissingFields.length > 0 ? "Save & Check-In" : `Confirm ${actionType}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// Define PropTypes for ReservationCard
ReservationCard.propTypes = {
  guest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    guestName: PropTypes.string,
    reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    aptStatus: PropTypes.string,
    stayDuration: PropTypes.string,
    actualCheckin: PropTypes.string,
    actualCheckout: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    stack: PropTypes.string,
    cnic: PropTypes.string,
    unit: PropTypes.string,
    type: PropTypes.string,
    contact: PropTypes.string,
    totalNights: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    earlyCheckin: PropTypes.string,
    pricePerNight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.string,
    email: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checkinDate: PropTypes.string,
    checkinTime: PropTypes.string,
    checkoutDate: PropTypes.string,
    checkoutTime: PropTypes.string,
    vehicleNo: PropTypes.string,
    security: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listingName: PropTypes.string,
  }).isRequired,
  setSnackbar: PropTypes.func.isRequired,
  stack: PropTypes.string.isRequired,
  isViewOnly: PropTypes.func.isRequired,
  isCustom: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

function KanbanView() {
  const {
    user,
    isAuthenticated,
    loading: authLoading,
    isViewOnly,
    isCustom,
    hasPermission,
    getAllowedDateRange,
  } = useAuth();
  const location = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const getInitialReservations = () => {
    try {
      const cached = localStorage.getItem("fdo_cached_combined_records") || sessionStorage.getItem("fdo_cached_combined_records");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return [];
  };

  const [reservations, setReservations] = useState(getInitialReservations);
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem("fdo_cached_combined_records") || sessionStorage.getItem("fdo_cached_combined_records");
      return !cached;
    } catch (e) {
      return false;
    }
  });
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSummary, setShowSummary] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [prevCount, setPrevCount] = useState(0);
  const [sseTrigger, setSseTrigger] = useState(0);

  // 🔔 Notification Stack State
  const [snackPack, setSnackPack] = useState([]);

  // ✅ Tab functionality for admin users
  const [activeTab, setActiveTab] = useState(0);
  const [listingSections, setListingSections] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [todayCheckIn, setTodayCheckIn] = useState([]);
  const [todayCheckOut, setTodayCheckOut] = useState([]);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [errorCheck, setErrorCheck] = useState(null);
  const anchorRef = useRef(null);

  // 📅 Date Range Filter State
  const { rangeLimit, canChangeDate } = getAllowedDateRange();

  const todayStr = new Date(new Date().getTime() + (5 * 60 * 60 * 1000)).toISOString().split('T')[0]; // PKT Approx
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const startDateRef = useRef(todayStr);
  const endDateRef = useRef(todayStr);

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    setStartDate(val);
    startDateRef.current = val;
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    setEndDate(val);
    endDateRef.current = val;
  };

  const handleApplyDateFilter = () => {
    const startObj = new Date(startDate);
    const endObj = new Date(endDate);

    if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
      setSnackbar({ open: true, message: "Please enter valid dates.", severity: "warning" });
      return;
    }

    const diff = (endObj - startObj) / (1000 * 60 * 60 * 24);

    if (diff < 0) {
      setSnackbar({ open: true, message: "End Date cannot be before Start Date.", severity: "warning" });
      return;
    }

    if (diff > rangeLimit) {
      const displayDays = rangeLimit === 30 ? 31 : (rangeLimit === 6 ? 7 : rangeLimit);
      setSnackbar({ open: true, message: `Date range cannot exceed ${displayDays} days.`, severity: "warning" });
      return;
    }

    fetchReservations(true);
  };
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastNotifiedId, setLastNotifiedId] = useState(null);
  const [latestNotification, setLatestNotification] = useState(null);
  const notifiedRecordIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true); // Track initial load
  const [openCommentReservationId, setOpenCommentReservationId] = useState(null);
  const [openPreviewReservationId, setOpenPreviewReservationId] = useState(null); // ✅ New state for preview modal
  const [commentInputs, setCommentInputs] = useState({});
  const [hotelEyeEntriesMap, setHotelEyeEntriesMap] = useState({});
  const [lastRefreshTime, setLastRefreshTime] = useState(null); // ✅ Track last data refresh time

  // 🔄 Sync Menu & Dialog State
  const [syncMenuAnchor, setSyncMenuAnchor] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncReservationInput, setSyncReservationInput] = useState("");
  const [syncRequestLoading, setSyncRequestLoading] = useState(false);

  // ✅ Responsive Deep Linking
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const linkedResId = searchParams.get("reservationId");

    if (linkedResId && reservations.length > 0) {
      const targetRes = reservations.find(r => r.reservationId === linkedResId || r.reservationId === Number(linkedResId));
      if (targetRes) {
        console.log("🔗 Deep linking (Effect) to:", linkedResId);
        setOpenPreviewReservationId(targetRes.reservationId);
      }
    }
  }, [location.search, reservations]);

  const handleCommentChange = (id, value) => {
    setCommentInputs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSendComment = async (row) => {
    const comment = commentInputs[row.id];
    if (!comment || !comment.trim()) {
      alert("Please enter a comment.");
      return;
    }

    try {
      const res = await fetch("/api/fdo/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reservation_id: row.id,
          comment: comment,
          user: user?.name || user?.username || "Unknown FDO",
        }),
      });

      if (res.ok) {
        // Success
      } else {
        const errData = await res.json();
        alert("Failed to send comment: " + (errData.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error sending comment:", error);
      alert("Network error while sending comment.");
    }
  };

  const fetchExistingHotelEyeEntries = async (reservationIds) => {
    if (!reservationIds || reservationIds.length === 0) return;

    try {
      const res = await fetch(`/api/fdo/hoteleye?_=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;

      const data = await res.json();
      const records = data.records || [];
      const entriesMap = {};

      records.forEach((record) => {
        const fetchedResId = record.reservation_id;
        const status = record.status;
        const userStr = record.user;
        const timeStr = record.createdAt;
        if (fetchedResId && status) {
          if (!entriesMap[fetchedResId] || timeStr > entriesMap[fetchedResId].time) {
            entriesMap[fetchedResId] = { status, user: userStr, time: timeStr };
          }
        }
      });

      setHotelEyeEntriesMap((prev) => {
        const updated = { ...prev };
        Object.keys(entriesMap).forEach((resId) => {
          updated[resId] = entriesMap[resId];
        });
        return updated;
      });
    } catch (error) {
      console.error("Error fetching existing Hotel Eye entries:", error);
    }
  };

  const fetchExistingComments = async (reservationIds) => {
    if (!reservationIds || reservationIds.length === 0) return;

    try {
      const res = await fetch(`/api/fdo/comments?_=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;

      const data = await res.json();
      const records = data.records || [];
      const commentsMap = {};

      records.forEach((record) => {
        const resId = record.reservation_id;
        const comment = record.comment;
        if (resId && comment) {
          if (!commentsMap[resId] || record.createdAt > commentsMap[resId].time) {
            commentsMap[resId] = { comment, time: record.createdAt };
          }
        }
      });

      setCommentInputs((prev) => {
        const updated = { ...prev };
        Object.keys(commentsMap).forEach((resId) => {
          updated[resId] = commentsMap[resId].comment;
        });
        return updated;
      });
    } catch (error) {
      console.error("Error fetching existing comments:", error);
    }
  };

  const handleNotificationClick = (reservationId) => {
    if (reservationId) {
      console.log("🔔 Notification clicked for:", reservationId);
      setOpenPreviewReservationId(reservationId);
      setNotificationsOpen(false);
    }
  };

  // API Configuration
  const API_ENDPOINT = process.env.REACT_APP_TEABLE_TABLE_URL_FDO_MAIN || "https://teable.namuve.com/api/table/tbliOdo8ldmMO8rrYyN/record";
  const API_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;
  const HOSTAWAY_TOKEN_LOCAL = process.env.REACT_APP_HOSTAWAY_API_TOKEN;
  const NOTIFICATION_API = `/api/fdo/notifications`;
  const TEABLE_TOKEN = process.env.REACT_APP_TEABLE_API_TOKEN_FDO;


  // Add function
  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3"); // Put in public/
    audio.volume = 0.4;
    audio.play().catch(() => { });
  };

  // ✅ Fetch reservations data from LOCAL PostgreSQL backend
  const fetchReservations = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);


      let records = [];
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("authToken");
        const queryParams = new URLSearchParams({
          _: Date.now(),
          startDate: startDateRef.current,
          endDate: endDateRef.current
        });

        const response = await fetch(`/api/fdo/reservations?${queryParams.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          cache: "no-store",
        });

        if (response.ok) {
          const data = await response.json();
          const apiRecords = data.data || data.records || [];
          records = apiRecords.map((r) => {
            if (r.fields) return r;
            return {
              id: String(r.reservation_id || r.id),
              fields: {
                "Guest Name": r.guest_name,
                "Reservation ID": r.reservation_id,
                "Listing Name": r.listing_name,
                "Arrival Date": r.arrival_date,
                "Departure Date": r.departure_date,
                "Status": r.status,
                "Actual Checkin": r.actual_checkin,
                "Actual Checkout": r.actual_checkout,
                "Payment Status": r.payment_status,
                "Total Price": r.total_price,
                "Total Paid": r.total_paid,
                "Currency": r.currency,
                "ADR": r.adr,
                "Can Print Check In": r.can_print_checkin,
                "Can Print Check Out": r.can_print_checkout,
                "Hotel Eye Status": r.hotel_eye_status,
                "Hotel Eye User": r.hotel_eye_user,
                "Hotel Eye Time": r.hotel_eye_time,
                "Phone": r.phone,
                "Number of Guests": r.number_of_guests,
                "Channel Name": r.channel_name,
                "Booking Date": r.booking_date,
                "Printed Check In": r.printed_checkin,
                "Printed Check Out": r.printed_checkout,
              }
            };
          });
        }
      } catch (e) {
        console.warn("Notice: /api/fdo/reservations failed, trying combined data:", e.message);
      }

      // Removed the fallback to /api/fdo/combined-data because it ignores date filters 
      // and dumps the entire historical database (300+ past checkouts) onto the board.
      // Filter double reservations on the same day for a room helper
      const filterDoubleReservations = (list) => {
        if (!list || list.length === 0) return [];

        const parseDate = (d) => {
          if (!d) return "";
          const clean = String(d).trim();
          if (clean.toLowerCase() === "n/a" || clean === "") return "";
          if (clean.includes("/")) {
            const parts = clean.split(",")[0].trim().split("/");
            if (parts.length === 3) {
              const month = parts[0].padStart(2, "0");
              const day = parts[1].padStart(2, "0");
              const year = parts[2];
              return `${year}-${month}-${day}`;
            }
          } else if (clean.includes("-")) {
            return clean.split("T")[0];
          }
          return clean;
        };

        const normRoom = (name) => {
          if (!name) return "";
          return String(name).replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
        };

        const getProp = (r, propName) => {
          if (!r) return "";
          const f = r.fields || {};
          if (propName === "room") {
            return r.listingName || r.apartment || f["Listing Name"] || f["Apartment Name"] || f["Apartment Name "] || "";
          }
          if (propName === "arrival") {
            return r.arrivalDate || r.arrival || f["Arrival Date"] || f["Arrival Date "] || "";
          }
          if (propName === "departure") {
            return r.departureDate || r.departure || f["Departure Date"] || f["Departure Date "] || "";
          }
          if (propName === "checkin") {
            return r.actualCheckin || f["Actual Checkin"] || "";
          }
          if (propName === "checkout") {
            return r.actualCheckout || f["Actual Checkout"] || "";
          }
          if (propName === "id") {
            return r.reservationId || r.id || f["Reservation ID"] || f["Reservation ID "] || "";
          }
          return "";
        };

        // Group by reservation ID if available, otherwise fallback to room and stay dates
        const groups = {};
        list.forEach((r) => {
          const room = normRoom(getProp(r, "room"));
          const arrival = parseDate(getProp(r, "arrival"));
          const departure = parseDate(getProp(r, "departure"));
          const resId = getProp(r, "id");
          
          // Use reservation ID in the key to prevent merging different valid bookings
          const key = resId ? `res_${resId}` : `${room}_${arrival}_${departure}`;
          if (!groups[key]) groups[key] = [];
          groups[key].push(r);
        });

        const toRemove = new Set();

        Object.values(groups).forEach((group) => {
          if (group.length <= 1) return;

          // Check if there is one with check-in populated and checkout NOT populated
          const activeGuest = group.find((r) => {
            const checkinVal = getProp(r, "checkin");
            const checkoutVal = getProp(r, "checkout");
            const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
            const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
            return checkin && !checkout;
          });

          if (activeGuest) {
            const activeId = getProp(activeGuest, "id");
            group.forEach((r) => {
              if (getProp(r, "id") !== activeId) {
                toRemove.add(getProp(r, "id"));
              }
            });
            return;
          }

          // Otherwise, check if there is one with both check-in and checkout populated
          const completedGuest = group.find((r) => {
            const checkinVal = getProp(r, "checkin");
            const checkoutVal = getProp(r, "checkout");
            const checkin = checkinVal && checkinVal !== "N/A" && String(checkinVal).trim() !== "";
            const checkout = checkoutVal && checkoutVal !== "N/A" && String(checkoutVal).trim() !== "";
            return checkin && checkout;
          });

          if (completedGuest) {
            const completedId = getProp(completedGuest, "id");
            group.forEach((r) => {
              if (getProp(r, "id") !== completedId) {
                toRemove.add(getProp(r, "id"));
              }
            });
          }
        });

        return list.filter((r) => !toRemove.has(getProp(r, "id")));
      };

      // Map API records to reservation format
      const mappedReservations = records.map((record) => mapRecordToReservation(record));

      // Filter out duplicate reservations
      const filteredReservations = filterDoubleReservations(mappedReservations);

      // Update state with new data if we got records, or if no cache exists
      if (filteredReservations.length > 0) {
        setReservations(filteredReservations);
        try {
          localStorage.setItem("fdo_cached_combined_records", JSON.stringify(filteredReservations));
        } catch (e) {}
      } else {
        // Only set to empty if we didn't have cached data to prevent blank board on network failure
        setReservations((prev) => (prev.length > 0 ? prev : []));
      }

      // Deep Linking: Check query param after fetching

      // Fetch existing comments for all reservation IDs
      const activeList = filteredReservations.length > 0 ? filteredReservations : reservations;
      const reservationIds = activeList.map((r) => r.reservationId);
      if (reservationIds.length > 0) {
        await fetchExistingComments(reservationIds);
        await fetchExistingHotelEyeEntries(reservationIds);
      }

      // ✅ Update last refresh timestamp
      setLastRefreshTime(new Date());

    } catch (err) {
      console.error("❌ Error fetching reservations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-refresh: Fetch data on mount and every 2 minutes
  useEffect(() => {
    // Initial fetch in background if cached data is already displayed
    const hasCache = Boolean(localStorage.getItem("fdo_cached_combined_records") || sessionStorage.getItem("fdo_cached_combined_records"));
    fetchReservations(!hasCache);

    // Set up polling interval (2 minutes = 120000ms)
    const refreshInterval = setInterval(() => {
      console.log("⏰ Auto-refresh triggered (2-minute interval)");
      fetchReservations(false); // Don't show loading spinner for background refresh
    }, 120000);

    // Connect to Server-Sent Events (SSE) for real-time updates
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const eventSource = new EventSource(`/api/fdo/stream?token=${token || ''}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type !== 'connected') {
          // Trigger a background refresh
          fetchReservations(false);
          // Tell children components (like CommentSection) to refresh
          setSseTrigger(prev => prev + 1);
        }
      } catch (err) {
        console.error("SSE parse error", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("SSE connection error", err);
    };

    // Cleanup interval and SSE on component unmount
    return () => {
      clearInterval(refreshInterval);
      eventSource.close();
    };
  }, []); // Empty dependency array = run once on mount

  // useEffect
  useEffect(() => {
    const fetchNotificationUpdate = async () => {
      // 🛑 If user is NOT admin, let UserLayout handle notifications
      if (user?.role === "user") return;

      try {
        const res = await fetch(`/api/fdo/notifications?_=${Date.now()}`, { cache: "no-store" });

        if (!res.ok) return;

        const data = await res.json();
        const records = data.records || [];

        // === Get only NEW records (not seen before) ===
        const newRecords = records.filter((record) => !notifiedRecordIdsRef.current.has(record.id));

        if (newRecords.length === 0) {
          return; // ← Keep badge unchanged
        }

        // === Badge:INCREMENT by number of new records (ALWAYS, even on initial load) ===
        setUnreadCount((prev) => prev + newRecords.length);

        // 🔄 On initial load, just mark all as seen without showing snackbars
        if (isInitialLoadRef.current) {
          newRecords.forEach((record) => {
            notifiedRecordIdsRef.current.add(record.id);
          });
          isInitialLoadRef.current = false;
          console.log(
            "🔕 Initial load: marked",
            newRecords.length,
            "notifications as seen (no snackbars)"
          );
          return; // Don't show snackbars on first load
        }

        const currentUserNames = [user?.name, user?.username, user?.email?.split("@")[0]]
          .filter(Boolean)
          .map((s) => s.trim().toLowerCase());

        const newOtherComments = [];

        for (const record of newRecords) {
          const sender = record?.fields?.User?.trim() || record?.user?.trim() || record?.user_name?.trim() || "";
          const senderLower = sender.toLowerCase();
          const cleanSender = senderLower
            .replace(/\s*\(you\)$/i, "")
            .replace(/\s*-\s*fdo$/i, "")
            .replace(/\s*admin$/i, "")
            .trim();

          const isOwnComment = currentUserNames.some((name) => {
            const cleanName = name.replace(/[@.]/g, " ");
            // Use strict equality to avoid false positives
            return cleanSender === name || cleanSender === cleanName;
          });

          notifiedRecordIdsRef.current.add(record.id);

          if (!isOwnComment) {
            newOtherComments.push({
              sender,
              fields: record.fields || {
                "Guest Name": record.guest_name,
                APT: record.apt,
                User: record.user || record.user_name,
                Type: record.type,
                Message: record.text || record.message
              },
            });
          }
        }

        // Show ALL new comments — stacked
        if (newOtherComments.length > 0) {
          newOtherComments.forEach((comment, index) => {
            const newId = new Date().getTime() + Math.random();

            // Add to stack with a slight delay for visual separation if multiple arrive at once
            setTimeout(() => {
              setSnackPack((prev) => [
                ...prev,
                {
                  id: newId,
                  message: "New Comment",
                  severity: "info",
                  richContent: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CommentIcon sx={{ fontSize: 18 }} />
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: "#fff",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{comment.sender}</span>
                        <span style={{ color: "#dbeafe" }}>commented on</span>
                        <span style={{ color: "#fbbf24", fontWeight: 600 }}>
                          {comment.fields["Guest Name"] || "Guest"}
                        </span>
                        <span style={{ color: "#34d399", fontWeight: 600 }}>
                          {comment.fields.APT || "Unit"}
                        </span>
                      </Typography>
                    </Box>
                  ),
                },
              ]);
              playNotificationSound();

              // Auto-dismiss after 5 seconds
              setTimeout(() => {
                setSnackPack((prev) => prev.filter((item) => item.id !== newId));
              }, 5000);
            }, index * 300); // 300ms stagger
          });
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    fetchNotificationUpdate();
    const interval = setInterval(fetchNotificationUpdate, 30000);
    return () => clearInterval(interval);
  }, [user?.name, user?.username, user?.email, sseTrigger]);

  const stacks = [
    "Upcoming Stay",
    "Checked In",
    "Early Checked In",
    "Staying Guest",
    "Upcoming Checkout",
    "Checked Out",
    "Same Day Checked Out",
    "Early Checked Out",
    "No Show",
    "Unknown",
  ];

  // 🎨 Stack color mapping
  const stackStyles = {
    "Upcoming Stay": {
      backgroundColor: "#EFF6FF", // bg-blue-50
      border: "1px solid #BFDBFE", // border-blue-200
      "&.dark": {
        backgroundColor: "rgba(30,58,138,0.3)", // dark:bg-blue-950/30
        borderColor: "#1E3A8A", // dark:border-blue-800
      },
    },
    "Checked In": {
      backgroundColor: "#ECFDF5",
      border: "1px solid #A7F3D0",
      "&.dark": { backgroundColor: "rgba(6,78,59,0.3)", borderColor: "#065F46" },
    },
    "Early Checked In": {
      backgroundColor: "#F0FDF4", // green-50
      border: "1px solid #86EFAC", // green-300
      "&.dark": { backgroundColor: "rgba(20,83,45,0.3)", borderColor: "#166534" },
    },
    "Staying Guest": {
      backgroundColor: "#FFFBEB",
      border: "1px solid #FDE68A",
      "&.dark": { backgroundColor: "rgba(120,53,15,0.3)", borderColor: "#92400E" },
    },
    "Upcoming Checkout": {
      backgroundColor: "#FDF2F8",
      border: "1px solid #FBCFE8",
      "&.dark": { backgroundColor: "rgba(131,24,67,0.3)", borderColor: "#9D174D" },
    },
    "Checked Out": {
      backgroundColor: "#FAF5FF",
      border: "1px solid #E9D5FF",
      "&.dark": { backgroundColor: "rgba(59,7,100,0.3)", borderColor: "#6B21A8" },
    },
    "Same Day Checked Out": {
      backgroundColor: "#FEF2F2",
      border: "1px solid #FECACA",
      "&.dark": { backgroundColor: "rgba(127,29,29,0.3)", borderColor: "#991B1B" },
    },
    "Early Checked Out": {
      backgroundColor: "#F1F5F9",
      border: "1px solid #CBD5E1",
      "&.dark": { backgroundColor: "rgba(51,65,85,0.3)", borderColor: "#475569" },
    },
    "No Show": {
      backgroundColor: "#FFF7ED",
      border: "1px solid #FED7AA",
      "&.dark": { backgroundColor: "rgba(124,45,18,0.3)", borderColor: "#9A3412" },
    },
    Unknown: {
      backgroundColor: "#F9FAFB",
      border: "1px solid #E5E7EB",
      "&.dark": { backgroundColor: "rgba(17,24,39,0.3)", borderColor: "#1F2937" },
    },
  };

  // Field mappings from API response
  const FIELD_MAP = {
    guestName: "Guest Name", // fldrVBpLpF2tgV0x6Ej
    reservationId: "Reservation ID", // fld86bKCKbHUjwct1kH
    listingName: "Listing Name",
    arrivalDate: "Arrival Date",
    departureDate: "Departure Date",
    aptStatus: "Apt Status", // fld1eUwsfQm1Q7Ohjbw
    stayDuration: "Stay Duration", // fld51m5EBER5vxwDZSL
    actualCheckin: "Actual Checkin", // fldTuBkzfSTgE8MamHG
    actualCheckout: "Actual Checkout", // fldjrY4P4JgPDxhQ4Tl
    tags: "Tags", // fldXAbO0T3KFSXClVcF
    stack: "Status", // fldUCJESFtQspNSVHLs
    listingName: "Listing Name",
  };

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // PDF Download state
  const [pdfLoading, setPdfLoading] = useState(false);

  const premiumApartmentIds = [288688, 305055, 309909, 323227];

  // ✅ Apartment Status Filter State
  const [statusFilter, setStatusFilter] = useState({
    available: true,
    reserved: true,
    blocked: true,
  });

  // ✅ FINAL COMPLETE SOLUTION - VERTICAL LAYOUT, FITS ON ONE PAGE, ALL COMBINATIONS
  const generateApartmentStatusPDF = async () => {
    try {
      setPdfLoading(true);

      // Fetch apartment data
      const apartmentResponse = await fetch(
        `${HOSTAWAY_BASE_URL}/listings?country=Pakistan`,
        {
          headers: {
            Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );

      let apartmentData = [];
      if (apartmentResponse.ok) {
        const apartmentResult = await apartmentResponse.json();
        apartmentData = apartmentResult.result || [];
      }

      // Get today's date
      const today = new Date().toISOString().split("T")[0];

      // Fetch calendar data for each apartment
      const apartmentDetails = await Promise.all(
        apartmentData.map(async (apartment) => {
          try {
            const calResponse = await fetch(
              `https://api.hostaway.com/v1/listings/${apartment.id}/calendar?startDate=${today}&endDate=${today}`,
              {
                headers: {
                  Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (calResponse.ok) {
              const calData = await calResponse.json();
              const todayStatus = calData.result?.[0]?.status || "unknown";

              let roomType = "Other";

              try {
                const details = apartment;

                const nameLower = (details.name || details.internalListingName || "").toLowerCase();
                const isPremium = premiumApartmentIds.includes(apartment.id);
                const isStudio = nameLower.includes("studio") || nameLower.includes("(s)");

                if (isStudio) {
                  roomType = "Studio";
                } else if (isPremium) {
                  roomType = "2BR Premium";
                } else {
                  const bedroomsNum = details.bedroomsNumber !== undefined && details.bedroomsNumber !== null
                    ? details.bedroomsNumber
                    : (details.bedrooms !== undefined && details.bedrooms !== null ? details.bedrooms : null);

                  if (bedroomsNum === 0) roomType = "Studio";
                  else if (bedroomsNum === 1) roomType = "1BR";
                  else if (bedroomsNum === 2) roomType = "2BR";
                  else if (bedroomsNum >= 3) roomType = "3BR";
                }
              } catch (e) {
                console.warn(`Failed to process listing details for category:`, e);
              }

              return {
                id: apartment.id,
                name:
                  apartment.internalListingName || apartment.name || `Apartment ${apartment.id}`,
                status: todayStatus,
                type: roomType,
              };
            }
            return null;
          } catch (error) {
            return null;
          }
        })
      );

      // Filter and group apartments
      const validApartments = apartmentDetails.filter(Boolean);
      const groupedApartments = {
        Studio: validApartments.filter((apt) => apt.type === "Studio"),
        "1BR": validApartments.filter((apt) => apt.type === "1BR"),
        "2BR": validApartments.filter((apt) => apt.type === "2BR"),
        "2BR Premium": validApartments.filter((apt) => apt.type === "2BR Premium"),
        "3BR": validApartments.filter((apt) => apt.type === "3BR"),
      };

      // ✅ Create PDF - LANDSCAPE A4
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const PAGE_WIDTH = 297;
      const PAGE_HEIGHT = 210;
      const MARGIN_L = 10;
      const MARGIN_R = 10;
      const MARGIN_T = 20;
      const MARGIN_B = 10;
      const USABLE_WIDTH = PAGE_WIDTH - MARGIN_L - MARGIN_R;

      const currentDate = new Date().toLocaleString();
      const username = user?.name || user?.username || "Unknown User";

      // ✅ HEADER
      pdf.setFontSize(14);
      pdf.setTextColor(20, 20, 20);
      pdf.setFont(undefined, "bold");
      pdf.text("APARTMENT STATUS REPORT", MARGIN_L, 12);

      pdf.setFontSize(7);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, "normal");
      pdf.text(`${currentDate} | ${username}`, MARGIN_L, 17);

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(MARGIN_L, 18.5, PAGE_WIDTH - MARGIN_R, 18.5);

      // ✅ NEW LAYOUT: Studio (full) | 1BR (full) | 2BR+2BR Premium (side-by-side) | 3BR (full)
      const selectedCount = [
        statusFilter.available,
        statusFilter.reserved,
        statusFilter.blocked,
      ].filter(Boolean).length;
      const categoryOrder = ["Studio", "1BR", "2BR", "2BR Premium", "3BR"];

      const PAGE_CONTENT_START = MARGIN_T; // 20mm
      const PAGE_CONTENT_END = PAGE_HEIGHT - MARGIN_B; // 200mm
      const AVAILABLE_HEIGHT = PAGE_CONTENT_END - PAGE_CONTENT_START; // 180mm

      // Collect apartments by type
      const apartmentsByType = {};
      categoryOrder.forEach((type) => {
        const apartments = groupedApartments[type] || [];
        const matching = apartments.filter(
          (apt) =>
            (statusFilter.available && apt.status === "available") ||
            (statusFilter.reserved && apt.status === "reserved") ||
            (statusFilter.blocked && apt.status === "blocked")
        );
        if (matching.length > 0) {
          apartmentsByType[type] = matching;
        }
      });

      const typesWithData = Object.keys(apartmentsByType);
      const numTypes = typesWithData.length;

      if (numTypes > 0) {
        // NEW LAYOUT: Studio (full) | 1BR (full) | 2BR+2BR Premium (2-col) | 3BR (full)
        const SECTION_HEADER_HEIGHT = 7; // Type name header
        const COL_HEADER_HEIGHT = 5.5; // Column header (Apartment | Status)
        const ROW_HEIGHT = 3.8; // Height per apartment row
        const SECTION_SPACING = 4; // Better spacing between sections

        let currentY = PAGE_CONTENT_START;

        // Render Studio (full width)
        if (apartmentsByType["Studio"]) {
          const apartments = apartmentsByType["Studio"];
          const available = apartments.filter((apt) => apt.status === "available").length;
          const reserved = apartments.filter((apt) => apt.status === "reserved").length;
          const blocked = apartments.filter((apt) => apt.status === "blocked").length;

          // SECTION HEADER
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(MARGIN_L, currentY, USABLE_WIDTH, SECTION_HEADER_HEIGHT, "FD");

          pdf.setFontSize(14);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Studio", MARGIN_L + 1, currentY + 4);

          pdf.setFontSize(8.5);
          pdf.setFont(undefined, "normal");
          const statsText = `Total: ${apartments.length} | Available: ${available} | Reserved: ${reserved} | Blocked: ${blocked}`;
          pdf.setTextColor(100, 100, 100);
          pdf.text(statsText, MARGIN_L + USABLE_WIDTH - 1, currentY + 3, { align: "right" });

          currentY += SECTION_HEADER_HEIGHT;

          // COLUMN HEADER
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(MARGIN_L, currentY, USABLE_WIDTH, COL_HEADER_HEIGHT, "FD");

          pdf.setFontSize(9.5);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Apartment Name", MARGIN_L + 1, currentY + 3.5);

          const statusHeaderX = MARGIN_L + USABLE_WIDTH * 0.65 + (USABLE_WIDTH * 0.35) / 2;
          pdf.text("Status", statusHeaderX, currentY + 3.5, { align: "center" });

          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.15);
          pdf.line(
            MARGIN_L + USABLE_WIDTH * 0.65,
            currentY,
            MARGIN_L + USABLE_WIDTH * 0.65,
            currentY + COL_HEADER_HEIGHT
          );

          currentY += COL_HEADER_HEIGHT;

          // DATA ROWS
          apartments.forEach((apt, rowIdx) => {
            const rowStartY = currentY;

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(MARGIN_L, rowStartY, MARGIN_L + USABLE_WIDTH, rowStartY);

            if (rowIdx % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              pdf.rect(MARGIN_L, rowStartY, USABLE_WIDTH, ROW_HEIGHT, "F");
            }

            pdf.setFontSize(8.5);
            pdf.setTextColor(20, 20, 20);
            pdf.setFont(undefined, "normal");
            pdf.text(apt.name, MARGIN_L + 1, rowStartY + 2.8);

            let statusColor = [20, 20, 20];
            if (apt.status === "available") {
              statusColor = [16, 185, 129];
            } else if (apt.status === "reserved") {
              statusColor = [220, 38, 38];
            } else if (apt.status === "blocked") {
              statusColor = [75, 85, 99];
            }

            pdf.setTextColor(...statusColor);
            pdf.setFont(undefined, "bold");
            const statusText = apt.status.charAt(0).toUpperCase() + apt.status.slice(1);
            const statusColumnStart = MARGIN_L + USABLE_WIDTH * 0.65;
            const statusColumnWidth = USABLE_WIDTH * 0.35;
            const statusX = statusColumnStart + statusColumnWidth / 2;
            pdf.text(statusText, statusX, rowStartY + 2.4, { align: "center" });

            currentY += ROW_HEIGHT;
          });

          currentY += SECTION_SPACING;
        }

        // Render 1BR (full width)
        if (apartmentsByType["1BR"]) {
          const apartments = apartmentsByType["1BR"];
          const available = apartments.filter((apt) => apt.status === "available").length;
          const reserved = apartments.filter((apt) => apt.status === "reserved").length;
          const blocked = apartments.filter((apt) => apt.status === "blocked").length;

          // SECTION HEADER
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(MARGIN_L, currentY, USABLE_WIDTH, SECTION_HEADER_HEIGHT, "FD");

          pdf.setFontSize(14);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("1BR", MARGIN_L + 1, currentY + 4);

          pdf.setFontSize(8.5);
          pdf.setFont(undefined, "normal");
          const statsText = `Total: ${apartments.length} | Available: ${available} | Reserved: ${reserved} | Blocked: ${blocked}`;
          pdf.setTextColor(100, 100, 100);
          pdf.text(statsText, MARGIN_L + USABLE_WIDTH - 1, currentY + 3, { align: "right" });

          currentY += SECTION_HEADER_HEIGHT;

          // COLUMN HEADER
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(MARGIN_L, currentY, USABLE_WIDTH, COL_HEADER_HEIGHT, "FD");

          pdf.setFontSize(9.5);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Apartment Name", MARGIN_L + 1, currentY + 3.5);

          const statusHeaderX = MARGIN_L + USABLE_WIDTH * 0.65 + (USABLE_WIDTH * 0.35) / 2;
          pdf.text("Status", statusHeaderX, currentY + 3.5, { align: "center" });

          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.15);
          pdf.line(
            MARGIN_L + USABLE_WIDTH * 0.65,
            currentY,
            MARGIN_L + USABLE_WIDTH * 0.65,
            currentY + COL_HEADER_HEIGHT
          );

          currentY += COL_HEADER_HEIGHT;

          // DATA ROWS
          apartments.forEach((apt, rowIdx) => {
            const rowStartY = currentY;

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(MARGIN_L, rowStartY, MARGIN_L + USABLE_WIDTH, rowStartY);

            if (rowIdx % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              pdf.rect(MARGIN_L, rowStartY, USABLE_WIDTH, ROW_HEIGHT, "F");
            }

            pdf.setFontSize(8.5);
            pdf.setTextColor(20, 20, 20);
            pdf.setFont(undefined, "normal");
            pdf.text(apt.name, MARGIN_L + 1, rowStartY + 2.8);

            let statusColor = [20, 20, 20];
            if (apt.status === "available") {
              statusColor = [16, 185, 129];
            } else if (apt.status === "reserved") {
              statusColor = [220, 38, 38];
            } else if (apt.status === "blocked") {
              statusColor = [75, 85, 99];
            }

            pdf.setTextColor(...statusColor);
            pdf.setFont(undefined, "bold");
            const statusText = apt.status.charAt(0).toUpperCase() + apt.status.slice(1);
            const statusColumnStart = MARGIN_L + USABLE_WIDTH * 0.65;
            const statusColumnWidth = USABLE_WIDTH * 0.35;
            const statusX = statusColumnStart + statusColumnWidth / 2;
            pdf.text(statusText, statusX, rowStartY + 2.4, { align: "center" });

            currentY += ROW_HEIGHT;
          });

          currentY += SECTION_SPACING;
        }

        // Render 2BR and 2BR Premium (side-by-side)
        const col1Width = (USABLE_WIDTH - 2) / 2; // 2mm gap
        const col2Width = (USABLE_WIDTH - 2) / 2;
        const col1X = MARGIN_L;
        const col2X = MARGIN_L + col1Width + 2;

        let col1Y = currentY;
        let col2Y = currentY;

        // Render 2BR (left column)
        if (apartmentsByType["2BR"]) {
          const apartments = apartmentsByType["2BR"];
          const available = apartments.filter((apt) => apt.status === "available").length;
          const reserved = apartments.filter((apt) => apt.status === "reserved").length;
          const blocked = apartments.filter((apt) => apt.status === "blocked").length;

          // SECTION HEADER
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col1X, col1Y, col1Width, SECTION_HEADER_HEIGHT, "FD");

          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("2BR", col1X + 1, col1Y + 3.5);

          pdf.setFontSize(7);
          pdf.setFont(undefined, "normal");
          const statsText = `Total: ${apartments.length} | Available: ${available} | Reserved: ${reserved} | Blocked: ${blocked}`;
          pdf.setTextColor(100, 100, 100);
          pdf.text(statsText, col1X + col1Width - 1, col1Y + 2.8, { align: "right" });

          col1Y += SECTION_HEADER_HEIGHT;

          // COLUMN HEADER
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col1X, col1Y, col1Width, COL_HEADER_HEIGHT, "FD");

          pdf.setFontSize(7.5);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Apartment Name", col1X + 1, col1Y + 3);

          const statusHeaderX1 = col1X + col1Width * 0.6 + (col1Width * 0.4) / 2;
          pdf.text("Status", statusHeaderX1, col1Y + 3, { align: "center" });

          col1Y += COL_HEADER_HEIGHT;

          // DATA ROWS
          apartments.forEach((apt, rowIdx) => {
            const rowStartY = col1Y;

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(col1X, rowStartY, col1X + col1Width, rowStartY);

            if (rowIdx % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              pdf.rect(col1X, rowStartY, col1Width, ROW_HEIGHT, "F");
            }

            pdf.setFontSize(7);
            pdf.setTextColor(20, 20, 20);
            pdf.setFont(undefined, "normal");
            pdf.text(apt.name, col1X + 1, rowStartY + 2.3);

            let statusColor = [20, 20, 20];
            if (apt.status === "available") {
              statusColor = [16, 185, 129];
            } else if (apt.status === "reserved") {
              statusColor = [220, 38, 38];
            } else if (apt.status === "blocked") {
              statusColor = [75, 85, 99];
            }

            pdf.setTextColor(...statusColor);
            pdf.setFont(undefined, "bold");
            const statusText = apt.status.charAt(0).toUpperCase() + apt.status.slice(1);
            const statusColumnStart = col1X + col1Width * 0.6;
            const statusColumnWidth = col1Width * 0.4;
            const statusX = statusColumnStart + statusColumnWidth / 2;
            pdf.text(statusText, statusX, rowStartY + 2, { align: "center" });

            col1Y += ROW_HEIGHT;
          });
        }

        // Render 2BR Premium (right column)
        if (apartmentsByType["2BR Premium"]) {
          const apartments = apartmentsByType["2BR Premium"];
          const available = apartments.filter((apt) => apt.status === "available").length;
          const reserved = apartments.filter((apt) => apt.status === "reserved").length;
          const blocked = apartments.filter((apt) => apt.status === "blocked").length;

          // Keep 2BR Premium header on same line as 2BR (no extra spacing)

          // SECTION HEADER
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col2X, col2Y, col2Width, SECTION_HEADER_HEIGHT, "FD");

          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("2BR Premium", col2X + 1, col2Y + 3);

          pdf.setFontSize(7);
          pdf.setFont(undefined, "normal");
          const statsText = `Total: ${apartments.length} | Available: ${available} | Reserved: ${reserved} | Blocked: ${blocked}`;
          pdf.setTextColor(100, 100, 100);
          pdf.text(statsText, col2X + col2Width - 1, col2Y + 2.8, { align: "right" });

          col2Y += SECTION_HEADER_HEIGHT;

          // COLUMN HEADER
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col2X, col2Y, col2Width, COL_HEADER_HEIGHT, "FD");

          pdf.setFontSize(7.5);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Apartment Name", col2X + 1, col2Y + 2.8);

          const statusHeaderX2 = col2X + col2Width * 0.6 + (col2Width * 0.4) / 2;
          pdf.text("Status", statusHeaderX2, col2Y + 2.8, { align: "center" });

          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.15);
          pdf.line(
            col2X + col2Width * 0.6,
            col2Y,
            col2X + col2Width * 0.6,
            col2Y + COL_HEADER_HEIGHT
          );

          col2Y += COL_HEADER_HEIGHT;

          // DATA ROWS
          apartments.forEach((apt, rowIdx) => {
            const rowStartY = col2Y;

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(col2X, rowStartY, col2X + col2Width, rowStartY);

            if (rowIdx % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              pdf.rect(col2X, rowStartY, col2Width, ROW_HEIGHT, "F");
            }

            pdf.setFontSize(7);
            pdf.setTextColor(20, 20, 20);
            pdf.setFont(undefined, "normal");
            pdf.text(apt.name, col2X + 1, rowStartY + 2);

            let statusColor = [20, 20, 20];
            if (apt.status === "available") {
              statusColor = [16, 185, 129];
            } else if (apt.status === "reserved") {
              statusColor = [220, 38, 38];
            } else if (apt.status === "blocked") {
              statusColor = [75, 85, 99];
            }

            pdf.setTextColor(...statusColor);
            pdf.setFont(undefined, "bold");
            const statusText = apt.status.charAt(0).toUpperCase() + apt.status.slice(1);
            const statusColumnStart = col2X + col2Width * 0.6;
            const statusColumnWidth = col2Width * 0.4;
            const statusX = statusColumnStart + statusColumnWidth / 2;
            pdf.text(statusText, statusX, rowStartY + 2, { align: "center" });

            col2Y += ROW_HEIGHT;
          });
        }

        // Render 3BR (right column) - below 2BR Premium
        if (apartmentsByType["3BR"]) {
          const apartments = apartmentsByType["3BR"];
          const available = apartments.filter((apt) => apt.status === "available").length;
          const reserved = apartments.filter((apt) => apt.status === "reserved").length;
          const blocked = apartments.filter((apt) => apt.status === "blocked").length;

          // Use col2Y position (right column) - starts where 2BR Premium ends
          let col3Y = col2Y + SECTION_SPACING;

          // SECTION HEADER
          pdf.setFillColor(255, 255, 255);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col2X, col3Y, col2Width, SECTION_HEADER_HEIGHT, "FD");

          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("3BR", col2X + 1, col3Y + 3);

          pdf.setFontSize(7);
          pdf.setFont(undefined, "normal");
          const statsText = `Total: ${apartments.length} | Available: ${available} | Reserved: ${reserved} | Blocked: ${blocked}`;
          pdf.setTextColor(100, 100, 100);
          pdf.text(statsText, col2X + col2Width - 1, col3Y + 2.8, { align: "right" });

          col3Y += SECTION_HEADER_HEIGHT;

          // COLUMN HEADER
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(col2X, col3Y, col2Width, COL_HEADER_HEIGHT, "FD");

          pdf.setFontSize(7.5);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont(undefined, "bold");
          pdf.text("Apartment Name", col2X + 1, col3Y + 2.8);

          const statusHeaderX3 = col2X + col2Width * 0.6 + (col2Width * 0.4) / 2;
          pdf.text("Status", statusHeaderX3, col3Y + 2.8, { align: "center" });

          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.15);
          pdf.line(
            col2X + col2Width * 0.6,
            col3Y,
            col2X + col2Width * 0.6,
            col3Y + COL_HEADER_HEIGHT
          );

          col3Y += COL_HEADER_HEIGHT;

          // DATA ROWS
          apartments.forEach((apt, rowIdx) => {
            const rowStartY = col3Y;

            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(col2X, rowStartY, col2X + col2Width, rowStartY);

            if (rowIdx % 2 === 1) {
              pdf.setFillColor(248, 248, 248);
              pdf.rect(col2X, rowStartY, col2Width, ROW_HEIGHT, "F");
            }

            pdf.setFontSize(7);
            pdf.setTextColor(20, 20, 20);
            pdf.setFont(undefined, "normal");
            pdf.text(apt.name, col2X + 1, rowStartY + 2);

            let statusColor = [20, 20, 20];
            if (apt.status === "available") {
              statusColor = [16, 185, 129];
            } else if (apt.status === "reserved") {
              statusColor = [220, 38, 38];
            } else if (apt.status === "blocked") {
              statusColor = [75, 85, 99];
            }

            pdf.setTextColor(...statusColor);
            pdf.setFont(undefined, "bold");
            const statusText = apt.status.charAt(0).toUpperCase() + apt.status.slice(1);
            const statusColumnStart = col2X + col2Width * 0.6;
            const statusColumnWidth = col2Width * 0.4;
            const statusX = statusColumnStart + statusColumnWidth / 2;
            pdf.text(statusText, statusX, rowStartY + 2, { align: "center" });

            col3Y += ROW_HEIGHT;
          });
        }

        currentY = Math.max(col1Y, col2Y) + SECTION_SPACING;

        // Save PDF
        const fileName = `Apartment_Status_${new Date().toISOString().split("T")[0]}.pdf`;
        pdf.save(fileName);

        console.log("═══════════════════════════════════════════════════════════════");
        console.log("✅ PDF GENERATED - BULLETPROOF SOLUTION");
        console.log("═══════════════════════════════════════════════════════════════");
        console.log("📄 Format: A4 Landscape (297mm × 210mm)");
        console.log("📐 Margins: 10mm (L/R), 20mm (T), 10mm (B)");
        console.log("📊 Usable Width: 277mm | Available Height: 180mm");
        console.log("🎯 Selected Statuses: " + selectedCount);
        console.log("📋 Layout: VERTICAL SECTIONS (Studio, 1BR, 2BR, 2BR Premium, 3BR)");
        console.log("✅ Sections: " + typesWithData.length);
        console.log("✅ Rendering: SIMPLE TEXT (No autoTable)");
        console.log("✅ Section Header: 10pt, White background with statistics");
        console.log("✅ Statistics: Total | Available | Reserved | Blocked");
        console.log("✅ Column Header: 8pt, Light gray background");
        console.log("✅ Column Header: Status centered in column");
        console.log("✅ Data Rows: 7pt, Color-coded status");
        console.log("✅ Apartment Name: Left-aligned, no stretch, truncated if long");
        console.log("✅ Status: Center-aligned in column");
        console.log("✅ Row Height: 3.2mm (better spacing)");
        console.log("✅ Section Spacing: 1mm");
        console.log("✅ Apartments per Section: ALL (no limit)");
        console.log("✅ Text Spacing: FIXED (no stretching)");
        console.log("✅ Text Visibility: 7pt, bold status, clear spacing");
        console.log("✅ Missing Apartments: NONE (all shown)");
        console.log("✅ Overlap Prevention: GUARANTEED (no autoTable)");
        console.log("✅ Text Override: PREVENTED (simple text)");
        console.log("✅ Spacing: PRECISE (manual Y tracking)");
        console.log("✅ Single Page: GUARANTEED");
        console.log("✅ Print Ready: YES");
        console.log("📁 File: " + fileName);
        console.log("═══════════════════════════════════════════════════════════════");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      setSnackbar({
        open: true,
        message: `Failed to generate PDF: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  // Countdown Timer for Cooldown
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);



  // ✅ Fetch when Apartment Status tab opens (for admin users) - MUST BE BEFORE CONDITIONAL RETURNS
  useEffect(() => {
    if (user?.role === "user") return; // Skip for regular users
    if (activeTab !== 1) return; // Only fetch for tab 1 (Apartment Status)

    // Prevent multiple rapid triggers
    if (window.__aptStatusCooldown && Date.now() - window.__aptStatusCooldown < 5000) {
      console.log("⏳ Skipping apartment fetch — still in cooldown");
      return;
    }

    window.__aptStatusCooldown = Date.now();
    setLoading(true);
    setError(null);
    setListingSections({});

    const timer = setTimeout(() => {
      console.log("🏢 Fetching Apartment Status for admin");
      fetchListings();
    }, 1000); // Reduced delay for better UX

    return () => {
      clearTimeout(timer);
      window.__aptStatusCooldown = null;
    };
  }, [activeTab, user?.role]);

  // ✅ Fetch Today's Check-In/Out data when tab 2 is active (for admin users)
  useEffect(() => {
    if (user?.role === "user") return; // Skip for regular users
    if (activeTab !== 2) return; // Only fetch for tab 2 (Today's Check-In/Out)

    // Prevent multiple rapid triggers
    if (window.__checkInOutCooldown && Date.now() - window.__checkInOutCooldown < 5000) {
      console.log("⏳ Skipping Check-In/Out fetch — still in cooldown");
      return;
    }

    window.__checkInOutCooldown = Date.now();
    setLoadingCheck(true);
    setErrorCheck(null);
    setTodayCheckIn([]);
    setTodayCheckOut([]);

    const timer = setTimeout(() => {
      console.log("📅 Fetching Today's Check-In/Out for admin");

      const fetchTodayCheckInOut = async () => {
        try {
          const res = await fetch(`${HOSTAWAY_BASE_URL}/listings`, {
            headers: {
              Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
              "Content-Type": "application/json",
            },
          });

          if (!res.ok) {
            throw new Error(`Failed to fetch listings: ${res.status} ${res.statusText}`);
          }

          const data = await res.json();
          let listings = data.result || [];

          console.log(`✅ Fetched ${listings.length} listings for Check-In/Out`);

          if (listings.length === 0) {
            console.warn("⚠️ No listings found");
            setTodayCheckIn([]);
            setTodayCheckOut([]);
            setLoadingCheck(false);
            return;
          }

          // Exclude UAE listings
          listings = listings.filter(
            (listing) =>
              !(
                (listing.country &&
                  listing.country.toLowerCase().includes("united arab emirates")) ||
                (listing.countryCode && listing.countryCode.toUpperCase() === "AE") ||
                (listing.city && listing.city.toLowerCase().includes("dubai")) ||
                (listing.city && listing.city.toLowerCase().includes("abu dhabi"))
              )
          );

          const today = new Date();
          const todayStr = today.toISOString().split("T")[0];
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split("T")[0];

          const checkInList = [];
          const checkOutList = [];

          await Promise.all(
            listings.map(async (listing) => {
              const calUrl = `https://api.hostaway.com/v1/listings/${listing.id}/calendar?startDate=${yesterdayStr}&endDate=${todayStr}&includeResources=1`;
              const calRes = await fetch(calUrl, {
                headers: {
                  Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
                  "Content-Type": "application/json",
                },
              });
              if (!calRes.ok) return;
              const calData = await calRes.json();

              for (const dayData of calData.result || []) {
                for (const resv of dayData.reservations || []) {
                  try {
                    const resvUrl = `${HOSTAWAY_BASE_URL}/reservations/${resv.id}?includeResources=1`;
                    const resvRes = await fetch(resvUrl, {
                      headers: {
                        Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
                        "Content-Type": "application/json",
                      },
                    });
                    if (!resvRes.ok) continue;
                    const resvData = await resvRes.json();
                    const reservation = resvData.result;

                    const vehicleNumber =
                      reservation?.customFieldValues?.find(
                        (field) => field.customField?.name === "Vehicle Number"
                      )?.value || "-";

                    // ✅ Get Actual Check-in Time custom field
                    const actualCheckinTime =
                      reservation?.customFieldValues?.find(
                        (field) => field.customField?.name === "Actual Check-in Time"
                      )?.value || null;

                    const phone = reservation?.phone || "-";
                    let paymentStatus = reservation?.paymentStatus;
                    if (paymentStatus && paymentStatus.toLowerCase() !== 'paid') {
                      let totalPaid = 0;
                      if (Array.isArray(reservation.financeField)) {
                        const totalPaidField = reservation.financeField.find(f => f.name === 'totalPaid' && f.isDeleted === 0);
                        if (totalPaidField) totalPaid = totalPaidField.total ?? totalPaidField.value ?? 0;
                      }
                      const total = parseFloat(reservation.totalPrice || 0);
                      const curr = reservation.currency || "PKR";
                      let capStatus = paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
                      if (paymentStatus.toLowerCase() === 'unknown') {
                        capStatus = 'Due';
                      }
                      
                      if (total > 0) {
                        const remaining = Math.max(0, total - totalPaid).toFixed(2).replace(/\.00$/, '');
                        paymentStatus = `${capStatus} (${remaining} ${curr})`;
                      } else {
                        paymentStatus = capStatus;
                      }
                    }

                    const guestObj = {
                      guest: reservation.guestName,
                      phone,
                      paymentStatus,
                      vehicle: vehicleNumber,
                      apartment: listing.internalListingName || listing.name || "N/A",
                      arrival: reservation.arrivalDate,
                      departure: reservation.departureDate,
                      comment: reservation.comment || "-",
                      id: reservation.id,
                    };

                    if (reservation.arrivalDate === todayStr) checkInList.push(guestObj);
                    // ✅ Only show checkouts if they have actually checked in
                    if (reservation.departureDate === todayStr && actualCheckinTime)
                      checkOutList.push(guestObj);
                  } catch (innerErr) {
                    console.warn("⚠️ Failed fetching reservation details:", innerErr);
                  }
                }
              }
            })
          );

          setTodayCheckIn(checkInList);
          setTodayCheckOut(checkOutList);
          console.log(
            `✅ Check-In/Out data loaded: ${checkInList.length} check-ins, ${checkOutList.length} check-outs`
          );

          // Fetch existing comments from Teable
          const allReservationIds = [...checkInList, ...checkOutList].map((r) => r.id);
          fetchExistingComments(allReservationIds);
        } catch (err) {
          console.error("❌ Error fetching Check-In/Out:", err);
          console.error("Error details:", err.message);
          setErrorCheck(`Failed to load Check-In/Out data: ${err.message}`);
        } finally {
          setLoadingCheck(false);
        }
      };

      fetchTodayCheckInOut();
    }, 1000);

    return () => {
      clearTimeout(timer);
      window.__checkInOutCooldown = null;
    };
  }, [activeTab, user?.role]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
        <MDTypography variant="h6" ml={2}>
          Loading...
        </MDTypography>
      </MDBox>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated || !user) {
    window.location.href = "/authentication/sign-in";
    return null;
  }

  function mapRecordToReservation(row) {
    const fields = row.fields || {};

    const getValue = (dbKey, teableAlias) => {
      if (row[dbKey] !== undefined) return row[dbKey];
      if (fields[teableAlias] !== undefined) return fields[teableAlias];
      if (fields[`${teableAlias} `] !== undefined) return fields[`${teableAlias} `];
      return undefined;
    };

    let tags = [];
    try {
      const rawTags = getValue("tags", "Tags");
      if (Array.isArray(rawTags)) tags = rawTags;
      else if (typeof rawTags === "string" && rawTags) tags = JSON.parse(rawTags);
    } catch (e) {
      console.warn("Tag parse failed:", e);
    }

    const paymentStatusVal = getValue("payment_status", "Payment Status") || row.paymentStatus;
    
    if (paymentStatusVal) {
      const pStatus = paymentStatusVal.toLowerCase();
      let formattedStatus = pStatus === 'unknown' ? 'Due' : pStatus.charAt(0).toUpperCase() + pStatus.slice(1);
      
      const totalPriceVal = getValue("total_price", "Total Price");
      if (pStatus !== 'paid' && totalPriceVal !== undefined) {
        const total = parseFloat(totalPriceVal || 0);
        const paid = parseFloat(getValue("total_paid", "Total Paid") || 0);
        const curr = getValue("currency", "Currency") || "PKR";
        if (total > 0) {
          const remaining = Math.max(0, total - paid).toFixed(2).replace(/\.00$/, '');
          formattedStatus = `${formattedStatus} (${remaining} ${curr})`;
        }
      }

      if (!tags.includes(formattedStatus)) {
        tags.push(formattedStatus);
      }
    }

    const stacks = [
      "Upcoming Stay",
      "Checked In",
      "Early Checked In",
      "Staying Guest",
      "Upcoming Checkout",
      "Checked Out",
      "Same Day Checked Out",
      "Early Checked Out",
      "No Show",
      "Unknown",
    ];

    let rawStatus = String(getValue("status", "Status") || "").trim();
    let stackVal = rawStatus || "Unknown";

    // Normalize casing & naming variations
    const lowerStatus = rawStatus.toLowerCase().replace(/[-_]/g, " ");
    if (lowerStatus === "early checked in" || lowerStatus === "early check in" || lowerStatus === "early checkin") {
      stackVal = "Early Checked In";
    } else if (lowerStatus === "same day checked out" || lowerStatus === "same day check out" || lowerStatus === "same day checkout") {
      stackVal = "Same Day Checked Out";
    } else if (lowerStatus === "early checked out" || lowerStatus === "early check out" || lowerStatus === "early checkout") {
      stackVal = "Early Checked Out";
    } else if (lowerStatus === "checked in" || lowerStatus === "checkin") {
      stackVal = "Checked In";
    } else if (lowerStatus === "checked out" || lowerStatus === "checkout") {
      stackVal = "Checked Out";
    } else if (lowerStatus === "upcoming stay") {
      stackVal = "Upcoming Stay";
    } else if (lowerStatus === "upcoming checkout") {
      stackVal = "Upcoming Checkout";
    } else if (lowerStatus === "staying guest") {
      stackVal = "Staying Guest";
    } else if (lowerStatus === "no show") {
      stackVal = "No Show";
    }

    const actualIn = getValue("actual_checkin", "Actual Checkin") || getValue("actual_checkin", "Checked In");
    const actualOut = getValue("actual_checkout", "Actual Checkout") || getValue("actual_checkout", "Checked Out");
    const arrDate = getValue("arrival_date", "Arrival Date");
    const depDate = getValue("departure_date", "Departure Date");

    const getPktDateStr = (val) => {
      if (!val || val === "N/A") return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      const isRaw = typeof val === "string" && !(/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(val.trim()));
      if (isRaw) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      }
      const pkt = new Date(d.getTime() + (5 * 60 * 60 * 1000));
      return pkt.toISOString().split("T")[0];
    };

    const getPktHourNum = (val) => {
      if (!val || val === "N/A") return null;
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      const isRaw = typeof val === "string" && !(/Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(val.trim()));
      if (isRaw) return d.getHours();
      return new Date(d.getTime() + (5 * 60 * 60 * 1000)).getUTCHours();
    };

    const nowPktStr = new Date(Date.now() + (5 * 60 * 60 * 1000)).toISOString().split("T")[0];
    const arrDateClean = arrDate ? String(arrDate).split("T")[0].split(" ")[0] : "";
    const depDateClean = depDate ? String(depDate).split("T")[0].split(" ")[0] : "";

    // Dynamic refinement based on actual checkin / checkout
    if (actualOut && actualOut !== "N/A") {
      const inDatePkt = getPktDateStr(actualIn);
      const outDatePkt = getPktDateStr(actualOut);
      const isSameDayStay = (arrDateClean && depDateClean && arrDateClean === depDateClean) || (arrDateClean && outDatePkt === arrDateClean);
      const isTodayCheckout = outDatePkt === nowPktStr;

      if (isTodayCheckout && isSameDayStay) {
        stackVal = "Same Day Checked Out";
      } else if (isTodayCheckout && depDateClean && outDatePkt < depDateClean) {
        stackVal = "Early Checked Out";
      } else {
        stackVal = "Checked Out";
      }
    } else if (actualIn && actualIn !== "N/A") {
      const inHour = getPktHourNum(actualIn);
      const inDatePkt = getPktDateStr(actualIn);
      if (nowPktStr <= arrDateClean && inDatePkt && inDatePkt < arrDateClean) {
        stackVal = "Early Checked In";
      } else if (arrDateClean === nowPktStr && inDatePkt === arrDateClean && inHour !== null && inHour < 15) {
        stackVal = "Early Checked In";
      } else if (arrDateClean === nowPktStr) {
        stackVal = "Checked In";
      } else if (depDateClean === nowPktStr) {
        stackVal = "Upcoming Checkout";
      } else if (arrDateClean < nowPktStr && depDateClean > nowPktStr) {
        stackVal = "Staying Guest";
      }
    } else {
      const isStatusModified =
        lowerStatus === "modified" ||
        String(getValue("status", "Status") || "").toLowerCase() === "modified" ||
        String(getValue("reservation_status", "Reservation Status") || "").toLowerCase() === "modified";

      if (lowerStatus === "no show" || lowerStatus === "noshow" || stackVal === "No Show") {
        stackVal = "No Show";
      } else if (
        arrDateClean &&
        arrDateClean !== nowPktStr &&
        (isStatusModified || arrDateClean < nowPktStr)
      ) {
        stackVal = "No Show";
      } else if (arrDateClean < nowPktStr && depDateClean > nowPktStr) {
        stackVal = "Staying Guest";
      } else if (depDateClean && depDateClean < nowPktStr) {
        stackVal = "No Show";
      } else {
        stackVal = "Upcoming Stay";
      }
    }

    return {
      id: row.id || row.reservation_id,
      guestName: getValue("guest_name", "Guest Name") || "N/A",
      reservationId: getValue("reservation_id", "Reservation ID") || "N/A",
      listingName: getValue("listing_name", "Listing Name") || "N/A",
      arrivalDate: getValue("arrival_date", "Arrival Date") || "N/A",
      departureDate: getValue("departure_date", "Departure Date") || "N/A",
      aptStatus: getValue("apt_status", "Apt Status") || "N/A",
      stayDuration: getValue("stay_duration", "Stay Duration") || "N/A",
      actualCheckin: actualIn || "N/A",
      actualCheckout: actualOut || "N/A",
      booking_date: getValue("booking_date", "Booking Date") || null,
      printedCheckIn: getValue("printed_checkin", "Printed Check In") || false,
      printedCheckOut: getValue("printed_checkout", "Printed Check Out") || false,
      tags,
      stack: stacks.includes(stackVal) ? stackVal : "Unknown",
    };
  }

  // Loading state - only show for initial home tab data load
  if (loading && activeTab === 0) {
    const loadingContent = (
      <MDBox mt={6} mb={3} display="flex" justifyContent="center">
        <CircularProgress />
      </MDBox>
    );

    // Never block screen with full-screen "Loading home data..." when reservations are already available or loading in background
    if (reservations.length > 0) {
      // Allow instant rendering
    } else {
      return user?.role === "user" ? (
        loadingContent
      ) : (
        <DashboardLayout>
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <MDTypography variant="h6" sx={{ color: "#6b7280", fontWeight: 500 }}>
              Loading home data...
            </MDTypography>
          </MDBox>
          <Footer />
        </DashboardLayout>
      );
    }
  }

  // Error state
  if (error) {
    const errorContent = (
      <MDBox mt={6} mb={3}>
        <MDTypography color="error">Error: {error}</MDTypography>
      </MDBox>
    );

    if (user?.role === "user") {
      return errorContent;
    }

    return (
      <DashboardLayout>
        <MDBox mt={6} mb={3}>
          <MDTypography variant="h5" color="error">
            Error: {error}
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }



  const refresh = () => {
    // your actual card refresh logic (API call, reload, etc.)
    window.location.reload();
  };

  // 🔄 Sync Options Handlers
  const handleSyncOptionsOpen = (event) => {
    setSyncMenuAnchor(event.currentTarget);
  };

  const handleSyncOptionsClose = () => {
    setSyncMenuAnchor(null);
  };

  const handleOpenSyncDialog = () => {
    handleSyncOptionsClose();
    setSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false);
    setSyncReservationInput("");
  };

  const handleSubmitSyncRequest = async () => {
    if (!syncReservationInput.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a reservation ID",
        severity: "warning",
      });
      return;
    }

    setSyncRequestLoading(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(
        `/api/fdo/reservations/${syncReservationInput.trim()}/sync`,
        {
          method: "POST",
          headers: headers
        }
      );

      if (!response.ok) {
        throw new Error(`Webhook failed: ${response.status}`);
      }

      setSnackbar({
        open: true,
        message: "Sync request completed successfully!",
        severity: "success",
      });
      handleCloseSyncDialog();
      fetchReservations(false);
    } catch (error) {
      console.error("Sync request error:", error);
      setSnackbar({
        open: true,
        message: "Failed to send sync request. Please try again.",
        severity: "error",
      });
    } finally {
      setSyncRequestLoading(false);
    }
  };

  const handleSyncAll = () => {
    handleSyncOptionsClose();
    handleSync();
  };

  const handleSync = async () => {
    // Prevent view_only users and custom users without complete access from syncing
    if (isViewOnly() || (isCustom() && !hasPermission("fdoPanel", "complete"))) {
      console.log("⚠️ Sync action blocked: Insufficient permissions");
      setSnackbar({
        open: true,
        message: "You don't have permission to sync data",
        severity: "error",
      });
      return;
    }

    if (syncing || cooldown > 0) {
      console.log("⚠️ Sync skipped — syncing or cooldown active.");
      return;
    }

    console.log("🔄 Starting sync...");
    setSyncing(true);

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // ✅ Using local backend sync instead of n8n webhook
      const response = await fetch(
        "/api/fdo/reservations/sync",
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            triggeredBy: "FDO Panel",
          }),
        }
      );

      console.log("📡 Webhook response status:", response.status);

      if (!response.ok) {
        throw new Error(`Webhook failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.text();
      console.log("✅ Sync successful:", data);

      // ✅ Show success Snackbar
      console.log("📢 Triggering success Snackbar...");
      setSnackbar({
        open: true,
        message: "Sync completed successfully!",
        severity: "success",
      });

      // ✅ Refresh UI immediately
      fetchReservations(false);

    } catch (error) {
      console.error("❌ Sync failed:", error);

      // 👇 Log where we are before setting Snackbar
      console.log("📢 Triggering error Snackbar...");

      if (error.message.includes("NetworkError") || error.message.includes("Failed to fetch")) {
        setSnackbar({
          open: true,
          message: "Network error: Please check your internet connection.",
          severity: "error",
        });
      } else if (error.message.includes("Webhook failed")) {
        setSnackbar({
          open: true,
          message: "Server error: Webhook failed. Please try again later.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Unexpected error: ${error.message}`,
          severity: "error",
        });
      }
    } finally {
      console.log("✅ Sync finished — resetting syncing state.");
      setSyncing(false);
    }
  };

  const matchesSearch = (guest, term) => {
    if (!term.trim()) return true;
    const lowerTerm = term.toLowerCase();

    return (
      String(guest.reservationId ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.guestName ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.aptStatus ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.listingName ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.arrivalDate ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.departureDate ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.stayDuration ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.actualCheckin ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.actualCheckout ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(guest.tags ?? "")
        .toLowerCase()
        .includes(lowerTerm) ||
      String(hotelEyeEntriesMap?.[guest.reservationId]?.status ?? "pending")
        .toLowerCase()
        .includes(lowerTerm)
    );
  };

  async function downloadExcel() {
    if (loadingCheck) {
      alert("Please wait... Data is still loading.");
      return;
    }
    if (todayCheckIn.length === 0 && todayCheckOut.length === 0) {
      alert("No data available to download.");
      return;
    }

    const showPhone = !(user?.role === "user" || (user?.role === "custom" && hasPermission("fdoPanel", "hideContact")));
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-GB");
    const fileDate = dateStr.replace(/\//g, "-");

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Upcoming Reservations");

    // ===== ROW 1 =====
    if (showPhone) {
      ws.mergeCells("B1:O1");
    } else {
      ws.mergeCells("B1:N1");
    }
    ws.getCell("A1").value = dateStr;
    ws.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell("A1").font = { bold: true };

    ws.getCell("B1").value = "Upcoming Reservation";
    ws.getCell("B1").alignment = { horizontal: "center", vertical: "middle" };
    ws.getCell("B1").font = { bold: true, size: 13 };

    // ===== ROW 2 =====
    if (showPhone) {
      ws.mergeCells("A2:H2");
      ws.getCell("A2").value = "Today Upcoming Arrivals";
      ws.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("A2").font = { bold: true, size: 12 };

      ws.mergeCells("I2:O2");
      ws.getCell("I2").value = "Today Upcoming Departures";
      ws.getCell("I2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("I2").font = { bold: true, size: 12 };
    } else {
      ws.mergeCells("A2:G2");
      ws.getCell("A2").value = "Today Upcoming Arrivals";
      ws.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("A2").font = { bold: true, size: 12 };

      ws.mergeCells("H2:N2");
      ws.getCell("H2").value = "Today Upcoming Departures";
      ws.getCell("H2").alignment = { horizontal: "center", vertical: "middle" };
      ws.getCell("H2").font = { bold: true, size: 12 };
    }

    // ===== ROW 3 HEADERS =====
    const headers = showPhone
      ? [
          "Sr#",
          "Apt#",
          "Name",
          "Phone#",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
          "Sr#",
          "Apt#",
          "Name",
          "Phone#",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
        ]
      : [
          "Sr#",
          "Apt#",
          "Name",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
          "Sr#",
          "Apt#",
          "Name",
          "Vehicle#",
          "Reminder",
          "Status",
          "Payment",
        ];
    ws.addRow(headers);

    ws.getRow(3).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF17621B" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // ===== DATA ROWS =====
    const maxLength = Math.max(todayCheckIn.length, todayCheckOut.length);
    for (let i = 0; i < maxLength; i++) {
      const checkIn = todayCheckIn[i] || {};
      const checkOut = todayCheckOut[i] || {};

      const rowData = [
        checkIn.apartment ? i + 1 : "",
        checkIn.apartment || "",
        checkIn.guest || "",
        ...(showPhone ? [checkIn.phone || ""] : []),
        checkIn.vehicle || "",
        "", // Reminder
        commentInputs[checkIn.id] || "", // Status (from comments)
        checkIn.apartment
          ? checkIn.paymentStatus && checkIn.paymentStatus.toLowerCase() !== "unknown"
            ? checkIn.paymentStatus
            : "Due"
          : "",

        checkOut.apartment ? i + 1 : "",
        checkOut.apartment || "",
        checkOut.guest || "",
        ...(showPhone ? [checkOut.phone || ""] : []),
        checkOut.vehicle || "",
        "", // Reminder
        commentInputs[checkOut.id] || "", // Status (from comments)
        checkOut.apartment
          ? checkOut.paymentStatus && checkOut.paymentStatus.toLowerCase() !== "unknown"
            ? checkOut.paymentStatus
            : "Due"
          : "",
      ];

      ws.addRow(rowData);
    }

    // ===== STYLING =====
    for (let r = 4; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const fillColor = r % 2 === 0 ? "FFF5F7F5" : "FFFFFFFF";
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    }

    // ===== AUTO WIDTH FOR NORMAL COLUMNS =====
    ws.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (!cell.value) return;
        const len = cell.value.toString().trim().length;
        if (len > maxLength) maxLength = len;
      });
      column.width = Math.max(maxLength * 0.9, 4);
    });

    // ===== SHRINK SMALL COLUMNS (Sr#, Apt#, Vehicle#, Payment) =====
    const shrinkCols = showPhone
      ? ["A", "B", "E", "H", "I", "J", "M", "N"]
      : ["A", "B", "D", "G", "H", "I", "K", "N"];
    shrinkCols.forEach((col) => {
      let maxLen = 0;
      ws.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
        if (!cell.value) return;
        const len = cell.value.toString().trim().length;
        if (len > maxLen) maxLen = len;
      });
      ws.getColumn(col).width = Math.min(maxLen + 1, 9);
    });

    // ===== FINAL ALIGNMENT + SHRINK FIT =====
    ws.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell((cell) => {
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: false,
          shrinkToFit: true,
        };
      });
    }
    );

    ws.getRow(1).height = 25;
    ws.getRow(2).height = 20;
    ws.getRow(3).height = 23;

    // ===== SAVE FILE =====
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Upcoming_Reservation_${fileDate}.xlsx`);
  }

  // ✅ Download PDF for Today's Check-In/Out
  function downloadPDF() {
    if (loadingCheck) {
      alert("Please wait... Data is still loading.");
      return;
    }
    if (todayCheckIn.length === 0 && todayCheckOut.length === 0) {
      alert("No data available to download.");
      return;
    }

    const showPhone = !(user?.role === "user" || (user?.role === "custom" && hasPermission("fdoPanel", "hideContact")));
    const doc = new jsPDF({ orientation: "landscape", unit: "mm" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const cellHeight = 8;
    let y = 10;

    // === COLUMN WIDTHS ===
    const colWidths = showPhone
      ? [
          10,
          18,
          35,
          28,
          25,
          28,
          20,
          20, // Left table (8)
          10,
          18,
          35,
          28,
          25,
          20,
          20, // Right table (7)
        ]
      : [
          10,
          18,
          35,
          25,
          28,
          20,
          20, // Left table (7)
          10,
          18,
          35,
          25,
          20,
          20, // Right table (6)
        ];
    const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
    const availableWidth = pageWidth - 2 * margin;
    const gap = 2;
    const scale = (availableWidth - gap) / totalColWidth;
    const scaledWidths = colWidths.map((w) => w * scale);

    const leftCount = showPhone ? 8 : 7;
    const rightStartIndex = showPhone ? 8 : 7;

    // === Helper: Auto-fit text ===
    const fitText = (text, maxWidth, baseSize = 8.5) => {
      let size = baseSize;
      doc.setFontSize(size);
      while (doc.getTextWidth(text) > maxWidth && size > 5) {
        size -= 0.3;
        doc.setFontSize(size);
      }
      return size;
    };

    // === Helper: New Page Header (for pagination) ===
    const drawHeader = () => {
      const dateStr = new Date().toLocaleDateString("en-GB");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(dateStr, margin + 2, y + 5.5);

      const titleStartX = margin + scaledWidths[0];
      const titleWidth = scaledWidths.slice(1).reduce((a, b) => a + b, 0) + gap;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("Upcoming Reservation", titleStartX + titleWidth / 2, y + 5.5, { align: "center" });

      y += cellHeight + 2;

      // === SECTION TITLES ===
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);

      const leftStartX = margin;
      const leftWidth = scaledWidths.slice(0, leftCount).reduce((a, b) => a + b, 0);
      const rightStartX = leftStartX + leftWidth + gap;
      const rightWidth = scaledWidths.slice(leftCount).reduce((a, b) => a + b, 0);

      // LEFT
      doc.setFillColor(220, 220, 220);
      doc.rect(leftStartX, y, leftWidth, cellHeight, "F");
      doc.text("Today Upcoming Arrivals", leftStartX + leftWidth / 2, y + 5.5, { align: "center" });

      // RIGHT
      doc.setFillColor(220, 220, 220);
      doc.rect(rightStartX, y, rightWidth, cellHeight, "F");
      doc.text("Today Upcoming Departures", rightStartX + rightWidth / 2, y + 5.5, {
        align: "center",
      });

      // Divider lines
      doc.setDrawColor(200, 200, 200);
      doc.line(rightStartX, y, rightStartX, y + cellHeight);

      y += cellHeight + 1;

      // === HEADERS ===
      const headers = showPhone
        ? [
            "Sr#",
            "Apt#",
            "Name",
            "Phone#",
            "Vehicle#",
            "Reservation Status",
            "Remarks",
            "Payment",
            "Sr#",
            "Apt#",
            "Name",
            "Phone#",
            "Vehicle#",
            "Remarks",
            "Payment",
          ]
        : [
            "Sr#",
            "Apt#",
            "Name",
            "Vehicle#",
            "Reservation Status",
            "Remarks",
            "Payment",
            "Sr#",
            "Apt#",
            "Name",
            "Vehicle#",
            "Remarks",
            "Payment",
          ];

      doc.setFont("helvetica", "bold");
      doc.setTextColor(255);

      let x = margin;
      headers.forEach((header, i) => {
        if (i === rightStartIndex) x += gap;
        const width = scaledWidths[i];
        doc.setFillColor(23, 98, 27);
        doc.rect(x, y, width, cellHeight, "F");

        const maxWidth = width - 3;
        const fittedSize = fitText(header, maxWidth, 8.5);
        doc.setFontSize(fittedSize);
        doc.text(header, x + width / 2, y + 5.3, { align: "center" });

        x += width;
      });

      y += cellHeight;

      // ✅ Reset text color and font for data rows
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
    };

    // === Draw header for the first page ===
    drawHeader();

    // === DATA ROWS ===
    const checkInData = todayCheckIn;
    const checkOutData = todayCheckOut;
    const rowHeight = 7;
    const maxLength = Math.max(checkInData.length, checkOutData.length);

    doc.setTextColor(0);
    doc.setDrawColor(200, 200, 200);
    doc.setFont("helvetica", "normal");

    for (let i = 0; i < maxLength; i++) {
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");

      // Page break if needed
      if (y + rowHeight > pageHeight - 10) {
        doc.addPage();
        y = 10;

        // 🔧 Reset colors to prevent white text on new pages
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(200, 200, 200);

        drawHeader();
      }

      const isEven = i % 2 === 0;
      const bg = isEven ? [245, 247, 245] : [255, 255, 255];
      const checkIn = checkInData[i] || {};
      const checkOut = checkOutData[i] || {};

      // LEFT
      let x = margin;
      const leftData = [
        checkInData[i] ? (i + 1).toString() : "",
        checkIn.apartment || "",
        checkIn.guest || "",
        ...(showPhone ? [checkIn.phone || ""] : []),
        checkIn.vehicle || "",
        "",
        "",
        checkInData[i]
          ? checkIn.paymentStatus && checkIn.paymentStatus.toLowerCase() !== "unknown"
            ? checkIn.paymentStatus
            : "Due"
          : "",
      ];

      leftData.forEach((cell, j) => {
        const width = scaledWidths[j];
        doc.setFillColor(...bg);
        doc.rect(x, y, width, rowHeight, "F");
        doc.rect(x, y, width, rowHeight, "S");
        if (cell) {
          const maxWidth = width - 4;
          const fittedSize = fitText(cell, maxWidth, 8.2);
          doc.setFontSize(fittedSize);
          doc.text(cell, x + width / 2, y + 4.5, { align: "center" });
        }
        x += width;
      });

      // RIGHT
      x = margin + scaledWidths.slice(0, leftCount).reduce((a, b) => a + b, 0) + gap;
      const rightData = [
        checkOutData[i] ? (i + 1).toString() : "",
        checkOut.apartment || "",
        checkOut.guest || "",
        ...(showPhone ? [checkOut.phone || ""] : []),
        checkOut.vehicle || "",
        "",
        checkOutData[i]
          ? checkOut.paymentStatus && checkOut.paymentStatus.toLowerCase() !== "unknown"
            ? checkOut.paymentStatus
            : "Due"
          : "",
      ];

      rightData.forEach((cell, j) => {
        const width = scaledWidths[j + leftCount];
        doc.setFillColor(...bg);
        doc.rect(x, y, width, rowHeight, "F");
        doc.rect(x, y, width, rowHeight, "S");
        if (cell) {
          const maxWidth = width - 4;
          const fittedSize = fitText(cell, maxWidth, 8.2);
          doc.setFontSize(fittedSize);
          doc.text(cell, x + width / 2, y + 4.5, { align: "center" });
        }
        x += width;
      });

      y += rowHeight;
    }

    const dateStr = new Date().toLocaleDateString("en-GB");
    doc.save(`Upcoming_Reservation_${dateStr.replace(/\//g, "-")}.pdf`);
  }

  // ✅ Fetch Apartment Status data for admin users
  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🏢 Fetching apartment status...");

      const res = await fetch(`${HOSTAWAY_BASE_URL}/listings?country=Pakistan`, {
        headers: {
          Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        console.error("❌ Failed to fetch listings:", res.status, res.statusText);
        throw new Error(`Failed to fetch listings: ${res.status}`);
      }

      const data = await res.json();
      const listings = data.result || [];
      console.log(`✅ Fetched ${listings.length} listings`);

      const today = new Date().toISOString().split("T")[0];

      const getRoomCategory = (listing) => {
        let roomCategory = "Other";

        const details = listing;
        const nameLower = (details.name || details.internalListingName || "").toLowerCase();
        const isPremium = premiumApartmentIds.includes(listing.id);
        const isStudio = nameLower.includes("studio") || nameLower.includes("(s)");

        if (isStudio) {
          roomCategory = "Studio";
        } else if (isPremium) {
          roomCategory = "2BR Premium";
        } else {
          const bedroomsNum = details.bedroomsNumber !== undefined && details.bedroomsNumber !== null
            ? details.bedroomsNumber
            : (details.bedrooms !== undefined && details.bedrooms !== null ? details.bedrooms : null);

          if (bedroomsNum === 0) roomCategory = "Studio";
          else if (bedroomsNum === 1) roomCategory = "1BR";
          else if (bedroomsNum === 2) roomCategory = "2BR";
          else if (bedroomsNum >= 3) roomCategory = "3BR";
        }

        return roomCategory;
      };

      // Show UI immediately (statuses will hydrate in background)
      const initialRows = listings.map((listing) => ({
        id: listing.id,
        name: listing.internalListingName || `ID ${listing.id}`,
        status: "unknown",
        category: getRoomCategory(listing),
      }));

      {
        const categories = ["2BR Premium", "3BR", "1BR", "Studio", "2BR"];
        const initialGrouped = {};
        categories.forEach((cat) => {
          initialGrouped[cat] = initialRows.filter((v) => v.category === cat);
        });
        initialRows.forEach((v) => {
          if (!categories.includes(v.category)) {
            if (!initialGrouped[v.category]) initialGrouped[v.category] = [];
            initialGrouped[v.category].push(v);
          }
        });
        setListingSections(initialGrouped);
      }

      setLoading(false);

      // Concurrency-limited calendar hydration (faster + less rate-limit risk)
      const runWithConcurrencyLimit = async (items, limit, mapper) => {
        const results = new Array(items.length);
        let nextIndex = 0;

        const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
          while (nextIndex < items.length) {
            const current = nextIndex++;
            try {
              results[current] = await mapper(items[current], current);
            } catch (e) {
              results[current] = null;
            }
          }
        });

        await Promise.all(workers);
        return results;
      };

      // For each listing, fetch its calendar to get today's status
      const results = await runWithConcurrencyLimit(listings, 10, async (listing) => {
        const roomCategory = getRoomCategory(listing);

        const calRes = await fetch(
          `${HOSTAWAY_BASE_URL}/listings/${listing.id}/calendar?startDate=${today}&endDate=${today}`,
          {
            headers: {
              Authorization: `Bearer ${HOSTAWAY_TOKEN}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!calRes.ok) {
          return {
            id: listing.id,
            name: listing.internalListingName || `ID ${listing.id}`,
            status: "unknown",
            category: roomCategory,
          };
        }

        const calData = await calRes.json();
        const todayStatus = calData.result?.[0]?.status;

        return {
          id: listing.id,
          name: listing.internalListingName || `ID ${listing.id}`,
          status: todayStatus || "unknown",
          category: roomCategory,
        };
      });

      const valid = results.filter(Boolean);
      console.log(`✅ Got ${valid.length} valid apartment statuses`);

      // Dynamically group based on the fetched categories
      const categories = ["2BR Premium", "3BR", "1BR", "Studio", "2BR"];
      const grouped = {};

      categories.forEach((cat) => {
        grouped[cat] = valid.filter((v) => v.category === cat);
      });
      valid.forEach((v) => {
        if (!categories.includes(v.category)) {
          if (!grouped[v.category]) {
            grouped[v.category] = [];
          }
          grouped[v.category].push(v);
        }
      });

      setListingSections(grouped);
      console.log("✅ Apartment status loaded successfully");
    } catch (err) {
      console.error("❌ Error loading apartment status:", err);
      setError(`Failed to load apartment status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Main content for both user and admin
  const mainContent = (
    <MDBox mt={user?.role === "user" ? 1 : 0} mb={0}>
      {/* ✅ Conditional content based on active tab for admin users */}
      {user?.role !== "user" && activeTab === 1 ? (
        // Apartment Status Tab Content
        <Box sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "700", color: "#1f2937" }}>
              🏢 Apartment Status
            </Typography>

            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              {/* Status Filter Checkboxes - COMMENTED OUT */}
              {/* <Box display="flex" alignItems="center" gap={1.5} sx={{ 
                backgroundColor: "#f9fafb",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb"
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
                  Filter:
                </Typography>
                <Form.Check
                  type="checkbox"
                  label="Available"
                  checked={statusFilter.available}
                  onChange={(e) => setStatusFilter({ ...statusFilter, available: e.target.checked })}
                  style={{ margin: 0, cursor: "pointer" }}
                />
                <Form.Check
                  type="checkbox"
                  label="Reserved"
                  checked={statusFilter.reserved}
                  onChange={(e) => setStatusFilter({ ...statusFilter, reserved: e.target.checked })}
                  style={{ margin: 0, cursor: "pointer" }}
                />
                <Form.Check
                  type="checkbox"
                  label="Blocked"
                  checked={statusFilter.blocked}
                  onChange={(e) => setStatusFilter({ ...statusFilter, blocked: e.target.checked })}
                  style={{ margin: 0, cursor: "pointer" }}
                />
                <Button
                  size="small"
                  onClick={() => setStatusFilter({ available: true, reserved: true, blocked: true })}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    color: "#3b82f6",
                    padding: "2px 6px",
                    minWidth: "auto",
                    "&:hover": {
                      backgroundColor: "#eff6ff"
                    }
                  }}
                >
                  All
                </Button>
              </Box> */}

              {/* PDF Download Button - COMMENTED OUT */}
              {/* <Button
                variant="outlined"
                onClick={generateApartmentStatusPDF}
                disabled={pdfLoading}
                startIcon={pdfLoading ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#047857",
                  border: "2px solid #047857",
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: "600",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "#ecfdf5",
                    borderColor: "#065f46",
                    color: "#065f46",
                  },
                  "&:disabled": {
                    backgroundColor: "#f3f4f6",
                    borderColor: "#9ca3af",
                    color: "#9ca3af",
                  }
                }}
              >
                Download PDF
              </Button> */}

              <TextField
                label="Search by Apartment Name"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                sx={{
                  width: "280px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
          </Box>

          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "60vh",
              }}
            >
              <Typography variant="h6" sx={{ color: "#6b7280", fontWeight: 500 }}>
                Loading apartment data...
              </Typography>
            </Box>
          )}
          {error && <Typography color="error">{error}</Typography>}

          {!loading && !error && Object.keys(listingSections).length > 0 && (
            <Row>
              {/* Check if "Select All" is active */}
              {(() => {
                const isAllStatus =
                  statusFilter.available && statusFilter.reserved && statusFilter.blocked;
                let entries = Object.entries(listingSections);

                // Only sort if NOT "Select All" - keep original order for "Select All"
                if (!isAllStatus) {
                  entries = entries.sort(([catA], [catB]) => {
                    const categoryOrder = ["Studio", "1BR", "2BR", "2BR Premium", "3BR"];
                    return categoryOrder.indexOf(catA) - categoryOrder.indexOf(catB);
                  });
                }

                return entries.map(([category, entries], idx) => {
                  // ✅ Filter by search query AND status filter
                  const filteredEntries = entries.filter((row) => {
                    const matchesSearch = row.name.toLowerCase().includes(searchQuery);
                    const matchesStatus =
                      (statusFilter.available && row.status === "available") ||
                      (statusFilter.reserved && row.status === "reserved") ||
                      (statusFilter.blocked && row.status === "blocked");
                    return matchesSearch && matchesStatus;
                  });

                  if (filteredEntries.length === 0) return null;

                  const total = entries.length;
                  const available = entries.filter((e) => e.status === "available").length;
                  const reserved = entries.filter((e) => e.status === "reserved").length;
                  const blocked = entries.filter((e) => e.status === "blocked").length;
                  const occupancy = total > 0 ? ((reserved / total) * 100).toFixed(1) : 0;

                  // ✅ Determine filter type
                  const isSingleStatus =
                    (statusFilter.available && !statusFilter.reserved && !statusFilter.blocked) ||
                    (!statusFilter.available && statusFilter.reserved && !statusFilter.blocked) ||
                    (!statusFilter.available && !statusFilter.reserved && statusFilter.blocked);

                  const isAllStatus =
                    statusFilter.available && statusFilter.reserved && statusFilter.blocked;

                  // ✅ For single/dual status view, use full width (md={12}). For all statuses, use md={6}
                  const colWidth = isAllStatus ? 6 : 12;

                  return (
                    <Col key={idx} md={colWidth} className="mb-4">
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#f9fafb",
                            px: 3,
                            py: 2,
                            borderBottom: "1px solid #e5e7eb",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                            {category}
                          </Typography>
                          {/* Always show same format: Total | Available | Reserved | Blocked */}
                          <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500 }}>
                            Total: {total} |{" "}
                            <span style={{ color: "#10B981", fontWeight: 600 }}>
                              Available: {available}
                            </span>{" "}
                            |{" "}
                            <span style={{ color: "#EF4444", fontWeight: 600 }}>
                              Reserved: {reserved}
                            </span>{" "}
                            |{" "}
                            <span style={{ color: "#6B7280", fontWeight: 600 }}>
                              Blocked: {blocked}
                            </span>
                          </Typography>
                        </Box>

                        <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Apartment Name</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEntries.map((row, i) => (
                              <tr key={i}>
                                <td>{row.name}</td>
                                <td
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      row.status === "available"
                                        ? "#10B981"
                                        : row.status === "reserved"
                                          ? "#EF4444"
                                          : "#6B7280",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {row.status}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Box>
                    </Col>
                  );
                });
              })()}
            </Row>
          )}
        </Box>
      ) : user?.role !== "user" && activeTab === 2 ? (
        // Today's Check-In/Out Tab Content
        <Box sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: "700", color: "#1f2937" }}>
              📅 Today's Check-In / Check-Out
            </Typography>

            {!loadingCheck && !(user?.role === "custom" && hasPermission("fdoPanel", "hideDownload")) && (
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* PDF Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={downloadPDF}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: "bold",
                    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                    color: "#17621B",
                    border: "2px solid #17621B",
                    "&:hover": {
                      backgroundColor: "#1e7a20",
                      borderColor: "#145517",
                      color: "#fff",
                    },
                  }}
                >
                  Download PDF
                </Button>

                {/* CSV Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={downloadExcel}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: "bold",
                    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                    color: "#d97706",
                    border: "2px solid #d97706",
                    "&:hover": {
                      backgroundColor: "#f59e0b",
                      borderColor: "#d97706",
                      color: "#fff",
                    },
                  }}
                >
                  Download Sheet
                </Button>
              </Box>
            )}
          </Box>

          {loadingCheck && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "60vh",
              }}
            >
              <Typography variant="h6" sx={{ color: "#6b7280", fontWeight: 500 }}>
                Loading data...
              </Typography>
            </Box>
          )}
          {errorCheck && <Typography color="error">{errorCheck}</Typography>}

          {!loadingCheck && !errorCheck && (
            <Row>
              {/* Check-In Table */}
              <Col md={6} className="mb-4">
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#f9fafb",
                      px: 3,
                      py: 2,
                      border: "2px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                      Today Check-In &nbsp;{" "}
                      <Typography component="span" variant="body2" sx={{ color: "#6b7280" }}>
                        Total: {todayCheckIn.length}
                      </Typography>
                    </Typography>
                  </Box>

                  <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>Guest Name</th>
                        <th>Vehicle Number</th>
                        <th>Apartment</th>
                        <th>Arrival Date</th>
                        <th>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayCheckIn.map((row, i) => (
                        <tr key={i}>
                          <td>{row.guest}</td>
                          <td>{row.vehicle}</td>
                          <td>{row.apartment}</td>
                          <td>{row.arrival}</td>
                          <td style={{ minWidth: "200px" }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <TextField
                                size="small"
                                placeholder="Add comment..."
                                value={commentInputs[row.id] || ""}
                                onChange={(e) => handleCommentChange(row.id, e.target.value)}
                                fullWidth
                                sx={{ backgroundColor: "#fff" }}
                              />
                              <IconButton
                                onClick={() => handleSendComment(row)}
                                color="success"
                                size="small"
                              >
                                <SendIcon />
                              </IconButton>
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              </Col>

              {/* Check-Out Table */}
              <Col md={6} className="mb-4">
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#f9fafb",
                      px: 3,
                      py: 2,
                      border: "2px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
                      Today Check-Out &nbsp;{" "}
                      <Typography component="span" variant="body2" sx={{ color: "#6b7280" }}>
                        Total: {todayCheckOut.length}
                      </Typography>
                    </Typography>
                  </Box>

                  <Table striped bordered hover responsive style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>Guest Name</th>
                        <th>Vehicle Number</th>
                        <th>Apartment</th>
                        <th>Departure Date</th>
                        <th>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayCheckOut.map((row, i) => (
                        <tr key={i}>
                          <td>{row.guest}</td>
                          <td>{row.vehicle}</td>
                          <td>{row.apartment}</td>
                          <td>{row.departure}</td>
                          <td style={{ minWidth: "200px" }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <TextField
                                size="small"
                                placeholder="Add comment..."
                                value={commentInputs[row.id] || ""}
                                onChange={(e) => handleCommentChange(row.id, e.target.value)}
                                fullWidth
                                sx={{ backgroundColor: "#fff" }}
                              />
                              <IconButton
                                onClick={() => handleSendComment(row)}
                                color="success"
                                size="small"
                              >
                                <SendIcon />
                              </IconButton>
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              </Col>
            </Row>
          )}
        </Box>
      ) : (
        // Home Tab Content (Default - Reservations)
        <>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12}>
              <Card>
                {/* Summary Widget Inside Kanban Card */}
                {user?.role !== "user" && (
                  <Box sx={{ mb: 1, mt: 2, px: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, px: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
                        Daily Occupancy & Departures Overview
                      </Typography>
                      <Button 
                        variant="contained"
                        disableElevation
                        size="small" 
                        onClick={() => setShowSummary(!showSummary)} 
                        sx={{ 
                          textTransform: "none", 
                          bgcolor: "#5e6b47 !important",
                          color: "#fff !important",
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.75rem",
                          "&:hover": { bgcolor: "#4a5538 !important" }
                        }}
                      >
                        {showSummary ? "Hide Summary" : "Show Summary"}
                      </Button>
                    </Box>
                    
                    <Collapse in={showSummary}>
                      <Box sx={{ p: 2, backgroundColor: "#ffffff", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #f3f4f6" }}>
                        <SummaryWidget reservations={reservations} setSearchTerm={setSearchTerm} />
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <MDBox p={1} display="flex" justifyContent="space-between" alignItems="center">
                {/* Left side title */}
                <MDTypography
                  variant="h5"
                  sx={{
                    fontWeight: "700",
                    color: "#1f2937",
                    fontSize: { xs: "0.9rem", sm: "1.5rem" },
                  }}
                  noWrap
                >
                  Reservations
                </MDTypography>

                {/* Right side (Search + Button) */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={{ xs: 0.5, sm: 0.5, md: 1 }}
                  sx={{ flexShrink: 1 }}
                >
                  {/* Search Bar */}
                  <MDBox
                    sx={{
                      position: "relative",
                      width: { xs: 80, sm: 120, md: 160 },
                    }}
                  >
                    {/* Search icon inside input */}
                    <SearchIcon
                      sx={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 15,
                        color: "#666",
                        zIndex: 1,
                        pointerEvents: "none",
                      }}
                    />

                    {/* Input field */}
                    <InputBase
                      placeholder="Search reservations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        pl: 5.5,
                        pr: 1.5,
                        py: 0.7,
                        width: "100%",
                        borderRadius: "10px",
                        border: "1px solid #ccc",
                        backgroundColor: "#fff",
                        fontSize: "0.65rem",
                        transition: "border 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          border: "1.5px solid #555",
                        },
                        "&:focus-within": {
                          border: "2px solid #333",
                        },
                      }}
                    />
                  </MDBox>

                  {/* Date Range Filters */}
                  {canChangeDate && (
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: "10px",
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <MDTypography variant="caption" sx={{ color: "#666", mr: 1, fontWeight: "bold" }}>
                          From:
                        </MDTypography>
                        <input
                          type="date"
                          value={startDate}
                          onChange={handleStartDateChange}
                          style={{
                            border: "none",
                            outline: "none",
                            fontSize: "0.75rem",
                            fontFamily: "inherit",
                            color: "#333",
                            backgroundColor: "transparent",
                          }}
                        />
                      </MDBox>
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          borderRadius: "10px",
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <MDTypography variant="caption" sx={{ color: "#666", mr: 1, fontWeight: "bold" }}>
                          To:
                        </MDTypography>
                        <input
                          type="date"
                          value={endDate}
                          onChange={handleEndDateChange}
                          style={{
                            border: "none",
                            outline: "none",
                            fontSize: "0.75rem",
                            fontFamily: "inherit",
                            color: "#333",
                            backgroundColor: "transparent",
                          }}
                        />
                      </MDBox>

                      <IconButton
                        onClick={handleApplyDateFilter}
                        size="small"
                        sx={{
                          bgcolor: "#5e6b47",
                          color: "#fff",
                          borderRadius: "10px",
                          p: 0.6,
                          '&:hover': { bgcolor: "#4a5538" }
                        }}
                      >
                        <SearchIcon fontSize="small" sx={{ color: "#fff" }} />
                      </IconButton>
                    </MDBox>
                  )}

                  {/* Last Refresh Indicator */}
                  {lastRefreshTime && (
                    <MDBox sx={{ display: { xs: "none", md: "block" } }}>
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Updated: {new Date(lastRefreshTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </MDTypography>
                    </MDBox>
                  )}

                  {/* Sync Button */}
                  <MDButton
                    variant="outlined"
                    onClick={handleSyncOptionsOpen}
                    disabled={
                      syncing ||
                      cooldown > 0 ||
                      isViewOnly() ||
                      (isCustom() && !hasPermission("fdoPanel", "complete"))
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "0.8rem",
                      borderRadius: "10px",
                      px: 0,
                      py: 0,
                      border: "2px solid",
                      borderColor: "primary.main",
                      color: "primary.main",
                      backgroundColor: "transparent",
                      transition: "all 0.2s ease",
                      fontSize: { xs: "0.7rem", md: "0.8rem" },
                      "&:hover": {
                        borderColor: "primary.dark",
                        color: "primary.dark",
                      },
                      "&:disabled": {
                        opacity: 1, // keep text readable
                        color: "text.secondary", // visible text when disabled
                        borderColor: "text.secondary",
                      },
                    }}
                  >
                    <SyncIcon
                      sx={{
                        fontSize: 10,
                        animation: syncing ? "spin 1s linear infinite" : "none",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />

                    {syncing
                      ? "Syncing..."
                      : cooldown > 0
                        ? `${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
                        : "Sync"}
                  </MDButton>

                  {/* Sync Menu */}
                  <Menu
                    anchorEl={syncMenuAnchor}
                    open={Boolean(syncMenuAnchor)}
                    onClose={handleSyncOptionsClose}
                  >
                    <MenuItem onClick={handleOpenSyncDialog}>Sync</MenuItem>
                    <MenuItem onClick={handleSyncAll}>Sync All (Today)</MenuItem>
                  </Menu>

                  {/* Sync Request Dialog */}
                  <Dialog open={syncDialogOpen} onClose={handleCloseSyncDialog}>
                    <DialogTitle>Sync Request</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Enter the reservation ID to trigger a specific sync request.
                      </DialogContentText>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="reservation"
                        label="Reservation ID"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={syncReservationInput}
                        onChange={(e) => setSyncReservationInput(e.target.value)}
                        sx={{
                          '& .MuiInput-underline:after': {
                            borderBottomColor: '#5e6b47',
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#5e6b47',
                          }
                        }}
                      />
                    </DialogContent>
                    <DialogActions>
                      <MDButton onClick={handleCloseSyncDialog} color="light">
                        Cancel
                      </MDButton>
                      <MDButton
                        onClick={handleSubmitSyncRequest}
                        disabled={syncRequestLoading}
                        variant="contained"
                        sx={{
                          backgroundColor: "#5e6b47",
                          color: "#ffffff",
                          "&:hover": {
                            backgroundColor: "#4b5639",
                          },
                          "&:focus:not(:hover)": {
                            backgroundColor: "#5e6b47",
                          },
                        }}
                      >
                        {syncRequestLoading ? "Sending..." : "Send Request"}
                      </MDButton>
                    </DialogActions>
                  </Dialog>
                </MDBox>
              </MDBox>

              <MDBox
                display="flex"
                overflow="auto"
                px={2}
                pb={2}
                sx={{
                  "& > *:last-child": { mr: 0 },
                  // Desktop view (xl and up): Keep original layout
                  "@media (min-width: 1536px)": {
                    flexDirection: "row",
                    flexWrap: "nowrap",
                  },
                  // Laptop/Tablet view (lg and down): Single row layout
                  "@media (max-width: 1535px)": {
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    gap: 1,
                    "& > div": {
                      minWidth: "280px !important",
                      maxWidth: "280px",
                      flex: "0 0 280px",
                    },
                  },
                }}
              >
                {stacks.map((stack) => {
                  // Get all guests in this stack
                  const stackGuests = reservations.filter((guest) => guest.stack === stack);

                  // Skip completely empty stacks
                  if (stackGuests.length === 0) return null;

                  // Filter guests by search term
                  const filteredGuests = stackGuests.filter((guest) =>
                    matchesSearch(guest, searchTerm)
                  );

                  // Skip stack if no guests match the search
                  if (filteredGuests.length === 0) return null;

                  return (
                    <MDBox
                      key={stack}
                      minWidth={360}
                      mr={2}
                      sx={{
                        "@media (min-width: 1536px)": { minWidth: 335, marginRight: 2 },
                        "@media (max-width: 1535px)": {
                          minWidth: "280px !important",
                          maxWidth: "280px",
                          marginRight: 1,
                          flex: "0 0 280px",
                        },
                      }}
                    >
                      <Card sx={{ ...(stackStyles[stack] || {}) }}>
                        <MDBox p={2}>
                          <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="h6">{stack}</MDTypography>
                            <Chip
                              label={filteredGuests.length}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: "bold", backgroundColor: "#28282B" }}
                            />
                          </MDBox>
                        </MDBox>

                        <MDBox
                          px={2}
                          pb={2}
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            maxHeight: {
                              xs: "calc(100vh - 180px)", // Mobile portrait: reduce overhead to 180px
                              sm: "calc(98vh - 220px)", // Tablet portrait: reduce overhead to 220px
                              md: "calc(98vh - 250px)", // Medium: reduce overhead to 250px
                              lg: "calc(98vh - 280px)", // Desktop: keep original 280px
                            },
                            // Landscape mode adjustments - reduce overhead even more due to shorter viewport height
                            "@media (max-width: 600px) and (orientation: landscape)": {
                              maxHeight: "calc(100vh - 60px)", // Mobile landscape: minimal overhead (increased from 120px)
                            },
                            "@media (min-width: 600px) and (max-width: 900px) and (orientation: landscape)":
                            {
                              maxHeight: "calc(100vh - 60px)", // Tablet landscape: reduced overhead (increased from 150px)
                            },
                          }}
                        >
                          {filteredGuests.map((guest) => (
                            <ReservationCard
                              key={guest.id}
                              guest={guest}
                              allGuests={reservations}
                              setSnackbar={setSnackbar}
                              searchTerm={searchTerm}
                              stack={stack}
                              isViewOnly={isViewOnly}
                              isCustom={isCustom}
                              hasPermission={hasPermission}
                              openCommentReservationId={openCommentReservationId}
                              setOpenCommentReservationId={setOpenCommentReservationId}
                              openPreviewReservationId={openPreviewReservationId}
                              setOpenPreviewReservationId={setOpenPreviewReservationId}
                              hotelEyeEntryGlobal={hotelEyeEntriesMap[guest.reservationId]}
                              onUpdateHotelEyeEntry={(resId, details) => setHotelEyeEntriesMap(prev => ({ ...prev, [resId]: details }))}
                              onRefresh={() => fetchReservations(false)}
                              sseTrigger={sseTrigger}
                            />
                          ))}
                        </MDBox>
                      </Card>
                    </MDBox>
                  );
                })}
                {/* ✅ Show message if no stacks have any match */}
                {stacks.every((stack) => {
                  const stackGuests = reservations.filter((guest) => guest.stack === stack);
                  const filteredGuests = stackGuests.filter((guest) =>
                    matchesSearch(guest, searchTerm)
                  );
                  return filteredGuests.length === 0;
                }) && (
                    <MDBox sx={{ textAlign: "center", py: 4, width: "100%" }}>
                      <MDTypography
                        variant="body1"
                        sx={{ color: "#6b7280", fontWeight: 500 }}
                      >
                        {loading
                          ? "⏳ Loading reservations data..."
                          : searchTerm
                          ? `🔍 No matches found for "${searchTerm}"`
                          : "📋 No active reservations found for the selected date."}
                      </MDTypography>
                    </MDBox>
                  )}
              </MDBox>
            </Card>
          </Grid >
        </Grid >
        </>
      )}
    </MDBox>
  );

  // Return with appropriate layout based on user role
  return (
    <>
      {user?.role === "user" ? (
        mainContent
      ) : (
        <DashboardLayout>
          {/* ✅ Complete Navigation Bar for Admin Users */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              color: "#1f2937",
              borderBottom: "1px solid #e2e8f0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              mb: 2,
            }}
          >
            <Toolbar sx={{ justifyContent: "space-between", px: { xs: 0.5, sm: 2 }, py: 1 }}>
              {/* Left: Title */}
              <Box
                display="flex"
                alignItems="center"
                gap={{ xs: 1, sm: 1 }}
                sx={{ flexShrink: 0, mr: { xs: 0.5, sm: 2 } }}
              >
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  onClick={handleMiniSidenav}
                  sx={{ p: 0.5, display: { lg: "none" } }}
                >
                  <Icon fontSize="small">{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>
                <Box display="flex" flexDirection="column">
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "700",
                      color: "#1f2937",
                      fontSize: { xs: "0.9rem", sm: "1.5rem" },
                    }}
                    noWrap
                  >
                    FDO Panel
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: { xs: "0.55rem", sm: "0.75rem" },
                      fontWeight: "500",
                    }}
                    noWrap
                  >
                    Guest Management System
                  </Typography>
                </Box>
              </Box>

              {/* Center: Complete Tabs - All three tabs visible */}
              <Box
                sx={{ flexGrow: 1, display: "flex", justifyContent: "center", overflow: "hidden" }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(event, newValue) => setActiveTab(newValue)}
                  centered
                  sx={{
                    "& .MuiTabs-indicator": {
                      display: "none",
                    },
                    "& .MuiTab-root": {
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                      mx: { xs: 0.5, sm: 0.5 },
                      px: { xs: 1.5, sm: 2, md: 3 },
                      py: 1,
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      color: "#4b5563",
                      backgroundColor: "transparent",
                      minWidth: { xs: "55px", sm: "90px" },
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                      // Ensure content is centered
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "& .MuiTab-iconWrapper": {
                        marginBottom: 0,
                        marginRight: 0,
                      },
                    },
                    "& .Mui-selected": {
                      color: "#000 !important",
                      backgroundColor: "transparent !important",
                      border: "1.5px solid #000",
                      boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Tab
                    icon={
                      <HomeIcon sx={{ display: { xs: "block", sm: "none" }, fontSize: "30px" }} />
                    }
                    label={<Box sx={{ display: { xs: "none", sm: "block" } }}>Home</Box>}
                  />
                  <Tab
                    icon={
                      <ApartmentIcon
                        sx={{ display: { xs: "block", sm: "none" }, fontSize: "30px" }}
                      />
                    }
                    label={
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>Apartment Status</Box>
                    }
                  />
                  <Tab
                    icon={
                      <EventAvailableIcon
                        sx={{ display: { xs: "block", sm: "none" }, fontSize: "30px" }}
                      />
                    }
                    label={
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>Todays Check-In/Out</Box>
                    }
                  />
                </Tabs>
              </Box>

              {/* Right: User Info */}
              <Box
                display="flex"
                alignItems="center"
                gap={{ xs: 0, sm: 1 }}
                sx={{ flexShrink: 0, ml: { xs: 0.5, sm: 2 } }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={{ xs: 0, sm: 1 }}
                  sx={{ flexShrink: 0, ml: { xs: 0.5, sm: 2 } }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: "#f3f4f6",
                      color: "#374151",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                    }}
                  >
                    {(user?.name || user?.username)?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                  <Typography
                    sx={{ color: "#1f2937", fontWeight: 600, display: { xs: "none", sm: "block" } }}
                  >
                    {user?.name || user?.username}
                  </Typography>

                  {/* NOTIFICATION ICON */}
                  <IconButton
                    ref={anchorRef}
                    size="small"
                    sx={{
                      color: "#4b5563",
                      "&:hover": { backgroundColor: "#f3f4f6" },
                    }}
                    onClick={() => setNotificationsOpen(true)}
                  >
                    <Badge badgeContent={unreadCount > 20 ? "20+" : unreadCount} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Box>
              </Box>
            </Toolbar>
          </AppBar>

          {mainContent}
          <Footer />
        </DashboardLayout>
      )}

      <Notifications
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        anchorEl={anchorRef.current}
        onNotificationClick={handleNotificationClick}
      />

      {/* ✅ Global Notification Stack (Only for Admin - UserLayout handles it for users) */}
      {user?.role !== "user" && (
        <Stack
          spacing={1}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.drawer + 9999,
            maxWidth: 400,
          }}
        >
          {snackPack.map((snack) => (
            <Grow key={snack.id} in={true}>
              <Alert
                onClose={() => setSnackPack((prev) => prev.filter((item) => item.id !== snack.id))}
                severity={snack.severity || "info"}
                variant="filled"
                sx={{
                  width: "100%",
                  minWidth: 320,
                  borderRadius: "12px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  background: snack.richContent
                    ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
                    : (() => {
                      switch (snack.severity) {
                        case "error":
                          return "#F8D7DA";
                        case "warning":
                          return "#FFF3CD";
                        case "info":
                          return "#CCE5FF";
                        case "success":
                          return "#D4EDDA";
                        default:
                          return undefined;
                      }
                    })(),
                  color: snack.richContent
                    ? "#fff"
                    : (() => {
                      switch (snack.severity) {
                        case "error":
                          return "#842029";
                        case "warning":
                          return "#856404";
                        case "info":
                          return "#084298";
                        case "success":
                          return "#155724";
                        default:
                          return undefined;
                      }
                    })(),
                }}
              >
                {snack.richContent || snack.message}
              </Alert>
            </Grow>
          ))}
        </Stack>
      )}

      {/* Legacy Snackbar for other messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 9999, mb: 8, mr: 3 }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default KanbanView;
